import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {
    DisplayActionIcon,
    DisplayValue,
    ECONode,
    ECOTree,
    EcoOrientation,
    OrgUser,
    Organization,
    SecurityTask,
    isDeveloper,
    toTaskDescription,
    toTaskPresentation,
    TaskPresentation,
    OrgPart,
    OrgHat,
} from '@newgenus/common';
import { SidebarPositionDetailComponent } from './sidebar-components/sidebar-position-detail.component';
import { SidebarPositionComponent } from './sidebar-components/sidebar-position.component';
import { applyLinkedPosts, linkedDropRefusal, postsOfCard } from './linked-posts';
import { CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { SidebarEmptyUserComponent } from './sidebar-components/sidebar-empty-user.component';
import { ConfirmDialog } from '../dialogs/confirm.dialog';
import { UserPoolDrawerComponent, PoolUser } from './sidebar-components/user-pool-drawer.component';
import { Position, OrgBoardBuilderService } from './services/org-board-builder.service';
import { OrgBoardNodeComponent } from './node-components/org-board-node/org-board-node.component';
import { SidebarUserComponent } from './sidebar-components/sidebar-user.component';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { DragScrollComponent } from 'ngx-drag-scroll';
import { Observable, Subject, Subscription, interval, takeUntil } from 'rxjs';
import { RightSideBarService } from '../../services/right-sidebar.service';
import { animate, style, transition, trigger, AnimationEvent } from '@angular/animations';
import { OrgDialogService } from './services/org-dialog.service';
import { OrgEntityColorsService } from '../org-dashboard/services/org-entity-colors.service';
import { CommonModule, DatePipe } from '@angular/common';
import { OrgHattingService } from './services/org-hatting.service';
import { OrgBoardService } from './services/org-board-service';
import { ProfileColorService } from './services/profile-color.service';
import { OrgExcelService } from './shared/excel/org-excel.service';
import {
    PublishAsTemplateData,
    PublishAsTemplateDialogComponent,
    PublishAsTemplateResult,
} from '../org-creation/dialogs/publish-as-template-dialog.component';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuTrigger } from '@angular/material/menu';
import { MaterialModule } from '../material.module';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { OrgCreateNewPositionDialogComponent } from './dialogs/org-position-dialog/org-create-position.dialog';
import { OrgEditPositionDialogComponent } from './dialogs/org-position-dialog/org-edit-position-dialog/org-edit-position-dialog.component';
import { OrgBulkAssignStaffDialogComponent } from './dialogs/org-bulk-assign-staff-dialog/org-bulk-assign-staff-dialog.component';
import { DialogService } from '../../services/dialog.service';
import { ToastrService } from 'ngx-toastr';
import { touchDistance, zoomFromPinch } from './shared/org-board-touch.util';
import { MatBottomSheet, MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { OverlayBackStackService } from '../../services/overlay-back-stack.service';
import { OrgBoardToolsSheetComponent } from './org-board-tools-sheet.component';
import { NEW_ORG_PART_QUICK_ACTION } from './new-org-part-switch';
import { boardPlace } from './shared/board-place';
import { orgBoardHeightGroup } from './shared/height-group';
import { fitBoardZoom, stepBoardZoom } from './shared/board-presenting';

@Component({
    selector: 'shared-organization-board',
    imports: [
        CommonModule,
        MaterialModule,
        FormsModule,
        OrgBoardNodeComponent,
        DragScrollComponent,
        MatBadgeModule,
        DatePipe,
        MatSliderModule,
        SidebarEmptyUserComponent,
        SidebarUserComponent,
        SidebarPositionDetailComponent,
        SidebarPositionComponent,
        UserPoolDrawerComponent,
        MatBottomSheetModule,
    ],
    templateUrl: './organization-board.component.html',
    styleUrls: ['./organization-board.component.scss'],
    animations: [
        // trigger('fadeInOut', [
        //   transition(
        //     ':enter', [
        //     style({ opacity: 0 }),
        //     animate('0.2s ease-out',
        //       style({ opacity: 1 }))
        //   ]),
        //   transition(
        //     ':leave', [
        //     style({ opacity: 1 }),
        //     animate('0.2s ease-in',
        //       style({ opacity: 0 }))
        //   ])
        // ]),
        trigger('slideInOut', [
            transition(':enter', [
                style({ transform: 'translateX(100%)', position: 'absolute', right: '0px', top: '65px' }), // Top must align with the height of the toolbar in app.component.html.
                animate('300ms ease-in', style({ transform: 'translateX(0%)' })),
            ]),
            transition(':leave', [animate('300ms ease-in', style({ transform: 'translateX(-100%)' }))]),
        ]),
        trigger('smoothHeight', [
            transition(':leave', [style({ height: '*' }), animate('0.4s ease-out', style({ height: 0 }))]),
            transition(':enter', [style({ height: '0' }), animate('0.4s ease-out', style({ height: '*' }))]),
        ]),
        trigger('delayedOpacity', [
            transition(':leave', [style({ opacity: 1 }), animate('0.4s ease-out', style({ opacity: 0 }))]),
            transition(':enter', [style({ opacity: 0 }), animate('0.4s 0.4s ease-out', style({ opacity: 1 }))]),
        ]),
        trigger('shrinkWidthLeave', [
            transition(':leave', [style({ width: '*' }), animate('0.4s ease-out', style({ width: 0 }))]),
            transition(':enter', [style({ width: '0' }), animate('0.4s ease-out', style({ width: '*' }))]),
        ]),
    ],
})
export class OrganizationBoardComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    //#region Variables
    // Variables for this component that are within scope of "this" component.

    @Input()
    public organization: Organization | null = null;

    @Input() // TODO: Create a feature and PERMISSIONS for this.
    public set mayEdit(value: boolean) {
        this._mayEdit = value;
        this.dialogService.mayEdit = value;
    }
    public get mayEdit(): boolean {
        return this._mayEdit;
    }
    private _mayEdit = false;

    @Input()
    public set mayManageUsers(value: boolean) {
        this._mayManageUsers = value;
        this.dialogService.mayManageUsers = value;
    }
    public get mayManageUsers(): boolean {
        return this._mayManageUsers;
    }
    private _mayManageUsers = false;

    @Input()
    public set mayManageHats(value: boolean) {
        this._mayManageHats = value;
        this.dialogService.mayManageHats = value;
    }
    public get mayManageHats(): boolean {
        return this._mayManageHats;
    }
    private _mayManageHats = false;
    /** "New Org Part" quick action withdrawn for go-live (new-org-part-switch.ts). */
    public readonly newOrgPartOn = NEW_ORG_PART_QUICK_ACTION;

    @Input()
    public set mayManageSecurityGroups(value: boolean) {
        this._mayManageSecurityGroups = value;
        this.dialogService.mayManageSecurityGroups = value;
    }
    public get mayManageSecurityGroups(): boolean {
        return this._mayManageSecurityGroups;
    }
    private _mayManageSecurityGroups = false;

    @Input()
    public set mayManagePolicies(value: boolean) {
        this._mayManagePolicies = value;
        this.dialogService.mayManagePolicies = value;
    }
    public get mayManagePolicies(): boolean {
        return this._mayManagePolicies;
    }
    private _mayManagePolicies = false;

    /**
     * Dark-deploy switchboard (design §3b, non-route surfaces): whether the
     * `invitations`/`policies` module is on for the current user/org. A
     * `mayManageX` flag above says the user MAY do the thing; these say the
     * module itself is even switched on. Default `true` so a host that doesn't
     * bind them (e.g. admin-tool's org board, which never grants
     * `mayManageUsers`/`mayManagePolicies` anyway) keeps today's behaviour —
     * the main app binds these from `ModuleSwitchService`.
     */
    @Input() invitationsModuleOn = true;
    @Input() policiesModuleOn = true;

    @Input() // TODO: UID of whose editing the board.
    public editingBy: string | null = null;

    @Input()
    public set isDev(value: boolean) {
        this._isDev = value;
        this.dialogService.isDev = value;
    }
    public get isDev(): boolean {
        return this._isDev;
    }
    private _isDev = false;

    @Input() // TODO: this should come from the Org doc, and be linked to a UID. Only the matching UID should be allowed to edit?
    public isEditing = false;

    /**
     * The API base URL for backend function calls (invitation create, check email, etc.).
     * Should be set by the host application from environment configuration.
     * Example: 'https://us-central1-newgenus-dev-19da7.cloudfunctions.net/api-'
     */
    @Input()
    public set apiBaseUrl(value: string | undefined) {
        this.obService.apiBaseUrl = value;
        this.dialogService.apiBaseUrl = value;
    }

    /** Online user IDs for presence indicators in list dialogs */
    @Input()
    public set onlineUserIds(value: string[]) {
        this.dialogService.onlineUserIds = value || [];
    }

    @Output()
    public isEditingChange = new EventEmitter<boolean>();

    @Output()
    public templateEmitted = new EventEmitter<{ templateRef: TemplateRef<any>; data?: any }>();

    /**
     * Emits a boolean value when an item is being dragged.
     * Used for hiding the sidebar menu in newgenus-frontend to aid in drag and drop navigation.
     *
     * @see isDroppableItemBeingDragged
     */
    @Output()
    public dragItem = new EventEmitter<boolean>();

    private nodeSelected: ECONode<Position> | null = null;

    /**
     * Used to determined if a new org is selected. This will then trigger a scrollTo operation to the first node.
     */
    private previousOrgKey: string | undefined;

    public isNavigatingWithDrag = false;
    public isDroppableItemBeingDragged = false;

    /** Tracks the last processed drop to prevent duplicate handling from CDK and fallback */
    private lastProcessedDropKey: string | null = null;

    /** Currently highlighted droppable-part element during drag */
    private _dragHoverElement: Element | null = null;
    /** Bound pointermove handler for drag highlight tracking */
    private _dragPointerMoveHandler: ((e: PointerEvent) => void) | null = null;

    public isDragNavDisabled = false;
    public isDragging = false;
    public hideScrollbar = false;
    public orgOptions: DisplayValue[] = [];
    public showLoader = true;
    public tree = new ECOTree();
    // public tree2 = new ECOTree();

    public isLocked = false; // TODO should be a field on the orgDoc.
    public lockedBy: string | null = null; // TODO should be a field on the orgDoc.

    /**
     * Flag to prevent tree rebuilds while hat assignment is being processed.
     * This avoids the "Unknown User" flash that occurs when the backend updates
     * the organization document in stages.
     */
    private isHatAssignmentInProgress = false;
    /** Stores the latest org data that arrived while hat assignment was in progress */
    private deferredOrgUpdate: any = null;
    /**
     * Map of user IDs currently being assigned to hats - used to track task completion
     * Each entry contains userId and userName/hatName for the completion toaster
     */
    private pendingHatAssignments: Map<string, { userName: string; hatName: string }> = new Map();
    /**
     * Set of user IDs currently being removed from hats - used to track task completion
     * Each entry contains userId and userName for the completion toaster
     */
    private pendingHatRemovals: Map<string, { userName: string; hatName: string }> = new Map();

    public childrenLinkPaths: any[] = [];
    private dragScrollContainer: any;
    private interval: Observable<number> | undefined;
    private terminateInterval = new Subject<void>();

    // --- Touch pan/zoom (mobile). ngx-drag-scroll is mouse-only, so we drive the
    // scroll container + zoomLevel directly from touch gestures. Active only in
    // view mode (!isEditing) so edit-mode cdkDrag node-dragging is untouched.
    @ViewChild('dragContainer') private dragContainerRef?: ElementRef<HTMLElement>;
    public readonly isTouchDevice =
        typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    private isBoardPanning = false;
    private isBoardPinching = false;
    private panLastX = 0;
    private panLastY = 0;
    // Frame-to-frame pinch tracking (supports simultaneous pan + zoom).
    private lastPinchDist = 0;
    private lastFocalX = 0;
    private lastFocalY = 0;
    private readonly boardTouchStart = (e: TouchEvent): void => this.onBoardTouchStart(e);
    private readonly boardTouchMove = (e: TouchEvent): void => this.onBoardTouchMove(e);
    private readonly boardTouchEnd = (e: TouchEvent): void => this.onBoardTouchEnd(e);
    public securityTasks: SecurityTask[] = [];
    private securityTasksSub: undefined | Subscription;
    public template!: TemplateRef<any>;

    public userOptions: DisplayValue[] = [];
    public hatOptions: DisplayValue[] = [];
    public policyOptions: DisplayValue[] = [];
    public securityGroupOptions: DisplayValue[] = [];

    // Random ID for the board.
    private orbBoardId = 'OB-' + Math.random().toString(36).substring(7);

    /**
     * The organization to display and interact with.
     * Changes made to the "organization" input property may update this _organization, unless there are
     * changes made that are conflicting with the currently edited _organization.
     *
     * The _organization will be emitted into NGRX for local updates and changes, these will then
     * be pushed to the DB.
     * These kind of changes should always be equal to the "organization" input property.
     *
     * @protected
     * @type {Organization}
     * @memberof OrganizationBoardComponent
     */
    protected _organization!: Organization;

    public toolbarSelection: ToolbarOptionsEnum = ToolbarOptionsEnum.none;

    public lineOptions: DisplayValue[] = [
        { display: 'Cross', value: 'M' },
        { display: 'Line', value: 'L' },
        { display: 'Bezier', value: 'B' },
    ];
    public orientationOptions: DisplayValue[] = [
        { display: 'Top', value: EcoOrientation.RO_TOP },
        { display: 'Bottom', value: EcoOrientation.RO_BOTTOM },
        { display: 'Left', value: EcoOrientation.RO_LEFT },
        { display: 'Right', value: EcoOrientation.RO_RIGHT },
    ];

    /**
     * Tracks which node ID has what drop list.
     *
     * @type {Record<string, CdkDropList>} - The key is the node ID and the value is the CdkDropList.
     */
    public dropListTracker: Record<string, CdkDropList> = {};
    public dropLists: CdkDropList[] = [];
    /** Reference to the user pool drop list, added when the pool component is ready */
    public userPoolDropList: CdkDropList | null = null;
    /** Node a user-row drag started from — the unassign target when the row is
     *  dropped on the staff pool (cleared on drag end). */
    private dragSourceNode: ECONode<any> | null = null;
    public userDropDataMap: Record<string, any> = {};
    public zoomLevel = 0.55;

    private destroy$ = new Subject<void>();
    public orgBoardWidth = 'calc(100vw - 1.5rem)';

    public menuTopLeftPosition = { x: '0', y: '0' };
    public contextMenuItems: (DisplayActionIcon & { iconColor?: string })[] = [];
    @ViewChild(MatMenuTrigger, { static: true }) contextMenu!: MatMenuTrigger;
    @ViewChild('sideBarOneUserTemplate') sideBarOneUserTemplate!: any;
    @ViewChild('sideBarManyUserTemplate') sideBarManyUserTemplate!: any;
    @ViewChild('sideBarEmptyUserTemplate') sideBarEmptyUserTemplate!: any;
    @ViewChild('sideBarPositionTemplate') sideBarPositionTemplate!: any;
    @ViewChild('sideBarUserTemplate') sideBarUserTemplate!: any;
    @ViewChild('obSideToolbar') obSideToolbar!: MatSelectionList;

    private justEnteredEditMode = false; // Flag to track the specific transition

    /**
     * Guard flag for the two-phase height calculation system.
     * Set true while _correctDepartmentHeightsFromDOM() (Phase 2) is running
     * to prevent recursive loops — since Phase 2 calls updateTree() +
     * detectChanges(), which can re-trigger the height calculation.
     * See updateNodeDimensions() JSDoc for the full system overview.
     */
    private _heightCorrectionInProgress = false;

    public isPositionsSwitchOn = false; // <-- Added property
    public isVfpEditingOn = false;

    /**
     * Edit-mode "Hide staff" (user request, 2026-09-28): switch every person off the board so
     * the editor works on structure alone. A TASK mode like VFP Editing — never persisted,
     * reset on Done. Visuals are CSS (`.ob-hide-staff ::ng-deep .ob-staff`); the staff
     * INTERACTIONS are guarded in code, because the pool drawer, the menus (CDK overlays) and
     * the drop fallback sit outside what a class on the board root can reach
     * (docs/categorized/features/ORG-BOARD-HIDE-STAFF-IN-EDIT-MODE-DESIGN.md).
     */
    public hideStaffOnBoard = false;

    public onHideStaffChange(checked: boolean): void {
        this.hideStaffOnBoard = checked;
        // Detached (Board of Directors) boxes are measured from the DOM — re-measure so a box
        // shrinks/grows with its occupants and the connector meets the new bottom edge.
        setTimeout(() => this.measureDetachedBoxes(), 60);
    }

    /** ISS-006: board-level "always show VFPs" view option (persisted locally).
     * Complements the per-part pin (vfpAlwaysVisible): the pin is an org-wide
     * authored choice, this is the viewer's own preference. */
    public showAllVfps = typeof localStorage !== 'undefined' && localStorage.getItem('orgBoardShowAllVfps') === 'true';

    public onShowAllVfpsChange(checked: boolean): void {
        this.showAllVfps = checked;
        try {
            localStorage.setItem('orgBoardShowAllVfps', String(checked));
        } catch {
            /* private mode */
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // Full screen ("presenting"): the board alone on a wall TV, fitted to the
    // screen width. The root takes the whole viewport by CSS (.ob-presenting),
    // and the browser's own full screen hides its chrome where it is allowed.
    // ═══════════════════════════════════════════════════════════════
    @ViewChild('boardRoot') private boardRootRef?: ElementRef<HTMLElement>;
    public isPresenting = false;
    private zoomBeforePresenting: number | null = null;

    public async togglePresenting(): Promise<void> {
        if (this.isPresenting) {
            await this.stopPresenting();
            return;
        }
        if (this.isEditing) return; // presenting is a reading view
        this.zoomBeforePresenting = this.zoomLevel;
        this.isPresenting = true;
        const root = this.boardRootRef?.nativeElement;
        try {
            if (root?.requestFullscreen && !document.fullscreenElement) await root.requestFullscreen();
        } catch {
            /* refused (iframe, iOS Safari): the CSS layer alone still fills the window */
        }
        // Fit after the layout settles in its new size.
        setTimeout(() => this.fitBoardToScreen(), 150);
    }

    public fitBoardToScreen(): void {
        const container = document.getElementsByClassName('drag-scroll-content')[0] as HTMLElement | undefined;
        if (!container || !this.tree?.width) return;
        // The tree's padding is 10 * zoom rem on each side, so it scales with the zoom too.
        this.zoomLevel = fitBoardZoom(container.clientWidth, this.tree.width + 2 * 10 * 16);
        this.tree.padding = 10 * this.zoomLevel;
        container.scrollLeft = 0;
        container.scrollTop = 0;
        this.cdr.detectChanges();
    }

    private async stopPresenting(): Promise<void> {
        this.isPresenting = false;
        try {
            if (document.fullscreenElement) await document.exitFullscreen();
        } catch {
            /* already out */
        }
        if (this.zoomBeforePresenting !== null) {
            this.zoomLevel = this.zoomBeforePresenting;
            if (this.tree) this.tree.padding = 10 * this.zoomLevel;
            this.zoomBeforePresenting = null;
        }
        this.cdr.detectChanges();
    }

    /** Esc (or the browser's own exit) leaves full screen without our button. */
    @HostListener('document:fullscreenchange')
    public onFullscreenChange(): void {
        if (!document.fullscreenElement && this.isPresenting) void this.stopPresenting();
    }

    @HostListener('document:keydown.escape')
    public onEscapeWhilePresenting(): void {
        // Without the browser's full screen (refused), Esc still leaves the CSS layer.
        if (this.isPresenting && !document.fullscreenElement) void this.stopPresenting();
    }

    // ═══════════════════════════════════════════════════════════════
    // Delete Processing Panel State
    // ═══════════════════════════════════════════════════════════════
    /** Whether the delete processing side panel is visible */
    public deletePanel = {
        visible: false,
        phase: 'idle' as 'idle' | 'deleting' | 'processing' | 'done' | 'error',
        partName: '',
        partType: '',
        partKey: '',
        descendantKeys: [] as string[],
        taskStatuses: [] as {
            key: string;
            name: string;
            action: string;
            icon: string;
            status: string;
            statusIcon: string;
            dedupKey?: string;
        }[],
        tasksCompleted: 0,
        totalTasks: 0,
        startTime: 0,
    };
    private deleteTaskMonitorSub?: Subscription;
    private deleteFinishTimeout: ReturnType<typeof setTimeout> | null = null;
    private deleteAutoCloseTimeout: ReturnType<typeof setTimeout> | null = null;

    // ═══════════════════════════════════════════════════════════════
    // User Operation Processing Panel State
    // ═══════════════════════════════════════════════════════════════
    /** Side panel for user removal / approval operations */
    public userOpPanel = {
        visible: false,
        phase: 'idle' as 'idle' | 'processing' | 'done' | 'error',
        userName: '',
        userId: '',
        partKey: '',
        action: '' as '' | 'remove' | 'approve' | 'reject' | 'assign',
        taskStatuses: [] as {
            key: string;
            name: string;
            action: string;
            icon: string;
            status: string;
            statusIcon: string;
            dedupKey?: string;
        }[],
        tasksCompleted: 0,
        totalTasks: 0,
        startTime: 0,
    };
    private userOpTaskMonitorSub?: Subscription;
    private userOpFinishTimeout: ReturnType<typeof setTimeout> | null = null;
    private userOpAutoCloseTimeout: ReturnType<typeof setTimeout> | null = null;
    /** Set of userId:partKey composite keys being processed — scoped grey-out to specific post */
    public processingUserKeysSet: Set<string> = new Set();

    //#endregion

    constructor(
        public sidebarService: RightSideBarService,
        private orgHattingService: OrgHattingService,
        private treeBuilder: OrgBoardBuilderService,
        public snackbar: MatSnackBar,
        private dialogService: OrgDialogService,
        public obService: OrgBoardService,
        private dialog: MatDialog,
        private confirmDialogService: DialogService,
        private cdr: ChangeDetectorRef,
        private toastr: ToastrService,
        private ngZone: NgZone,
        private profileColorService: ProfileColorService,
        public entityColors: OrgEntityColorsService,
        private orgExcelService: OrgExcelService,
        private bottomSheet: MatBottomSheet,
        private overlayBack: OverlayBackStackService
    ) {}

    log(...args: any[]) {
        // console.log(...args);
    }

    /**
     * Export the current organization to an `.xlsx` workbook (Structure + Staff
     * sheets, see {@link OrgExcelService}). Triggered from the edit-mode toolbar.
     *
     * Pure client-side — no Firestore reads, no backend round-trip. The exported
     * file is the source-of-truth fixture used by the wizard's "Import from
     * Excel" path (Plan §16.6).
     */
    public async onExportToExcel(): Promise<void> {
        if (!this.organization) {
            this.toastr.warning('No organization loaded — nothing to export.', 'Export to Excel');
            return;
        }
        try {
            await this.orgExcelService.exportOrganization(this.organization);
            this.toastr.success(`Exported "${this.organization.name}" to Excel.`, 'Export to Excel');
        } catch (err) {
            console.error('[OrgBoard] Excel export failed:', err);
            this.toastr.error('Excel export failed. Check the console for details.', 'Export to Excel');
        }
    }

    /**
     * P4.c — open the publish-as-template dialog. Hidden from the toolbar unless
     * the current viewer is in edit-mode and listed in `organization.owners`
     * (the dialog re-checks; this is a UX gate so non-owners never see the
     * button at all).
     */
    public onPublishAsTemplate(): void {
        if (!this.organization) {
            this.toastr.warning('No organization loaded — nothing to publish.', 'Publish as template');
            return;
        }
        const data: PublishAsTemplateData = {
            orgKey: (this.organization as { key?: string }).key ?? '',
            defaultName: this.organization.name ?? '',
            owners: Array.isArray(this.organization.owners) ? this.organization.owners : [],
        };
        this.dialog.open<PublishAsTemplateDialogComponent, PublishAsTemplateData, PublishAsTemplateResult>(
            PublishAsTemplateDialogComponent,
            { data, panelClass: 'newgenus-publish-template-panel' }
        );
    }

    /**
     * Returns the avatar URL for a user, falling back to a generated coloured initial.
     * Mirrors the helper in `OrgBoardNodeComponent` so the header-strip chips rendered
     * directly inside the card title bar (`.above-card` / `.above-card2`) can show
     * proper avatars without re-implementing fallback logic.
     */
    public getUserAvatarUrl(user: any): string {
        if (user?.photoThumbnail) {
            return user.photoThumbnail;
        }
        const uid = user?.uid || user?.key;
        if (uid && (this.organization as any)?.orgUsers?.[uid]?.photoThumbnail) {
            return (this.organization as any).orgUsers[uid].photoThumbnail || '';
        }
        const name = user?.name || 'User';
        return this.profileColorService.getAvatarUrl(uid || '', name);
    }

    /**
     * Returns true if the user (by uid/key) is currently in the online users
     * presence set. Used by the header-strip chips to render a green dot on the
     * avatar for live users — same source of truth as `org-board-node`.
     */
    public isHeaderUserOnline(user: any): boolean {
        const uid = user?.uid || user?.key;
        return !!uid && (this.dialogService.onlineUserIds || []).includes(uid);
    }

    /** Returns the first non-pending user on a post, or first user, or null. */
    public getHeaderPostUser(post: any): any {
        const arr = post?.userDetailsArray;
        if (arr?.length) {
            return arr.find((u: any) => !u?.isPending) || arr[0] || null;
        }
        // Fall back to Held-From-Above user (post inherits an occupant from a
        // higher node). heldFromAboveUserName can be a comma-separated list, and
        // heldFromAboveUid mirrors that. Use the first uid/name pair so the chip
        // can show an avatar instead of "Unoccupied".
        const hfaName = post?.heldFromAboveUserName;
        const hfaUid = post?.heldFromAboveUid;
        if (hfaName) {
            const firstName = String(hfaName).split(',')[0].trim();
            const firstUid = hfaUid ? String(hfaUid).split(',')[0].trim() : '';
            // Try to find the full user record on orgUsers so the avatar resolves
            // (photoThumbnail / colour fallback) the same as direct users.
            const orgUser = firstUid && (this.organization as any)?.orgUsers?.[firstUid];
            if (orgUser) {
                return { ...orgUser, uid: firstUid, name: orgUser.name || firstName, isHFA: true };
            }
            return { uid: firstUid, key: firstUid, name: firstName, isHFA: true };
        }
        return null;
    }

    /**
     * ALL occupants of a header post, active (non-pending) first. The header
     * chip renders the first as the big avatar block and flags any extras —
     * a head post with two assigned users used to silently render only the
     * first, hiding erroneous double assignments entirely (ISS-008).
     */
    public getHeaderPostUsers(post: any): any[] {
        const arr = post?.userDetailsArray;
        if (arr?.length) {
            const active = arr.filter((u: any) => !u?.isPending);
            const pending = arr.filter((u: any) => u?.isPending);
            return [...active, ...pending];
        }
        const hfa = this.getHeaderPostUser(post);
        return hfa ? [hfa] : [];
    }

    //#region Instantiation
    // Setup\fetches\initialization.

    public ngOnInit(): void {
        this.obService.updateNodeLinking
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.updateNodeLinkingLines(this.tree));

        // A sidebar/dialog toggled a per-part setting that changes tree relationships
        // (e.g. excludeFromHfa). Apply it to our in-memory org and rebuild so HFA
        // recomputes immediately (persistence is handled by the sidebar via updatePart).
        this.obService.partSettingChanged
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ partKey, changes }) => this.applyPartSettingAndRebuild(partKey, changes));

        // A linked post's "Go to origin" (settings card / dialog): reuse the held-from-above
        // navigation, searching from the board root (the target can sit in any branch).
        this.obService.navigateToPart.pipe(takeUntil(this.destroy$)).subscribe((partKey) => {
            const root = this.tree?.nDatabaseNodes?.find((n: ECONode<Position>) => n && n.id !== -1);
            if (partKey && root) this.onNavigateToHfaOrigin(partKey, root);
        });

        // setTimeout(() => {
        //   this.onSelectSideToolbar({ options: [{ value: ToolbarOptionsEnum.new_hat }] } as any);
        // }, 200);
    }

    public export() {
        // // download the file using old school javascript method
        // this.exportAsService.save(this.exportAsConfig, 'My File Name').subscribe(() => {
        //   // save started
        // });
        // // get the data as base64 or json object for json type - this will be helpful in ionic or SSR
        // this.exportAsService.get(this.exportAsConfig).subscribe(content => {
        //   console.log(content);
        // });
    }

    /**
     * Pre-load profile colors so that avatars render with the correct color
     * immediately when dialogs/cards open, without a visible flash.
     *
     * 1. Seed the cache from orgUsers (instant, no network).
     * 2. For any users whose color isn't on orgUsers yet, fetch from Firestore in the background.
     */
    private preloadProfileColors(org: Organization): void {
        if (!org?.orgUsers) return;
        const missing = this.profileColorService.seedFromOrgUsers(org.orgUsers as Record<string, any>);
        if (missing.length > 0) {
            this.profileColorService.ensureLoadedMany(missing);
        }
    }

    public onSelectSideToolbar(event: MatSelectionListChange) {
        this.selectSideTool((event.options[0].value as ToolbarOptionsEnum) || ToolbarOptionsEnum.none);
        this.obSideToolbar?.deselectAll();
    }

    /**
     * Runs a side-toolbar selection. Shared by the desktop side panel
     * (onSelectSideToolbar) and the mobile bottom sheet (openToolsSheet), so both
     * surfaces drive identical behaviour.
     */
    public selectSideTool(value: ToolbarOptionsEnum) {
        this.toolbarSelection = value || ToolbarOptionsEnum.none;

        switch (this.toolbarSelection) {
            case ToolbarOptionsEnum.hats:
                this.dialogService.openHatsListDialog();
                break;
            case ToolbarOptionsEnum.my_policies:
                this.dialogService.openMyPoliciesDialog();
                break;
            case ToolbarOptionsEnum.policies:
                this.dialogService.openPoliciesListDialog();
                break;
            case ToolbarOptionsEnum.users:
                this.dialogService.openUsersListDialog();
                break;
            case ToolbarOptionsEnum.security_groups:
                this.dialogService.openSecurityGroupsListDialog();
                break;
            case ToolbarOptionsEnum.positions:
                this.dialogService.openPositionsListDialog();
                break;
            case ToolbarOptionsEnum.new_user:
                this.dialogService.openInviteUserDialog();
                break;
            case ToolbarOptionsEnum.new_policy:
                this.dialogService.openPolicyDialog(true);
                break;
            case ToolbarOptionsEnum.new_hat:
                this.dialogService.openNewHatDialog();
                break;
            case ToolbarOptionsEnum.new_part:
                this.dialogService.openHierarchyDialog();
                break;
            case ToolbarOptionsEnum.new_sg:
                this.dialogService.openSecurityGroupDialog(true).then().catch();
                break;

            default:
                this.snackbar.open('Unrecognized instruction.', 'Close', { duration: 3000 });
                break;
        }
    }

    /**
     * Mobile edit-mode tools: open the palette as a bottom sheet (the board stays
     * visible above it) and run the chosen tool through the shared handler.
     */
    public openToolsSheet(): void {
        const ref = this.bottomSheet.open(OrgBoardToolsSheetComponent, {
            data: {
                mayManageUsers: this.mayManageUsers,
                mayManageHats: this.mayManageHats,
                mayManagePolicies: this.mayManagePolicies,
                mayManageSecurityGroups: this.mayManageSecurityGroups,
                invitationsModuleOn: this.invitationsModuleOn,
                policiesModuleOn: this.policiesModuleOn,
                isEditing: this.isEditing,
                hideStaffOnBoard: this.hideStaffOnBoard,
            },
        });
        this.overlayBack.trackSheet(ref);
        ref.afterDismissed().subscribe((value?: string) => {
            if (value === 'toggle_hide_staff') {
                // The header row (and its slide toggle) is hidden on phones — the sheet is the
                // only way to flip "Hide staff" there.
                this.onHideStaffChange(!this.hideStaffOnBoard);
                return;
            }
            if (value) this.selectSideTool(value as ToolbarOptionsEnum);
        });
    }

    //#endregion
    //#region User Interactions
    // Callbacks from html, anything the user can "interact" with from the view.

    public onRightClick(event: MouseEvent) {
        // preventDefault avoids to show the visualization of the right-click menu of the browser
        event.preventDefault();

        // we record the mouse position in our object
        this.menuTopLeftPosition.x = event.clientX + 'px';
        this.menuTopLeftPosition.y = event.clientY + 'px';

        this.contextMenuItems = [
            {
                display: 'Zoom In',
                icon: 'zoom_in',
                action: this.zoomIn.bind(this),
            },
            {
                display: 'Zoom Out',
                icon: 'zoom_out',
                action: this.zoomOut.bind(this),
            },
            {
                display: 'Reset Zoom',
                icon: 'zoom_out_map',
                action: this.resetZoom.bind(this),
            },
            this.isEditing
                ? {
                      display: 'Done Editing',
                      icon: 'done',
                      action: this.onEditClick.bind(this, !this.isEditing),
                      disabled: !this.mayEdit,
                  }
                : {
                      display: 'Edit',
                      icon: 'edit',
                      action: this.onEditClick.bind(this, !this.isEditing),
                      disabled: !this.mayEdit,
                  },
            {
                display: 'Select All',
                icon: 'select_all',
                action: this.selectSiblingNodes.bind(this, this.tree.nDatabaseNodes[0]),
            },
            {
                display: 'Scroll Up',
                icon: 'arrow_upward',
                action: () => {
                    this.scrollVertical(50);

                    setTimeout(() => {
                        this.stopInterval();
                    }, 500);
                },
            },
            {
                display: 'Scroll Down',
                icon: 'arrow_downward',
                action: () => {
                    this.scrollVertical(-50);

                    setTimeout(() => {
                        this.stopInterval();
                    }, 500);
                },
            },
            {
                display: 'Jump Left',
                icon: 'arrow_back',
                action: () => {
                    this.scrollHorizontal(100);

                    setTimeout(() => {
                        this.stopInterval();
                    }, 500);
                },
            },
            {
                display: 'Jump Right',
                icon: 'arrow_forward',
                action: () => {
                    this.scrollHorizontal(-100);

                    setTimeout(() => {
                        this.stopInterval();
                    }, 500);
                },
            },
        ];

        // we open the menu
        this.contextMenu.openMenu();
    }

    /**
     * Opens the position sidebar when clicking on a department/division header.
     */
    public onPositionHeaderClick(node: any): void {
        this.sidebarService.openTemplate(this.sideBarPositionTemplate, node);
    }

    /**
     * Opens the position sidebar for sections/units clicked inside node components.
     * Wraps the position data into a node-like structure expected by the sidebar.
     */
    public onOpenPositionTemplate(event: { data: any; parentNode: ECONode<Position> | null }): void {
        const positionData = event.data;
        const parentNode = event.parentNode;

        // Build a lightweight node-like wrapper so the sidebar component can read .data
        const wrappedNode = {
            id: positionData.partKey || positionData.key || positionData.name,
            data: {
                ...positionData,
                partType: positionData.partType || positionData.type || 'section',
                parentPartKey: positionData.parentPartKey || parentNode?.data?.partKey,
                colour: positionData.colour ||
                    parentNode?.data?.colour || { color: '#000000', background: '#FFFFFF', linkColor: '#000000' },
                depth: positionData.depth ?? (parentNode?.data?.depth ?? 0) + 1,
            },
            nodeParent: parentNode,
            nodeChildren: [],
        };

        this.sidebarService.openTemplate(this.sideBarPositionTemplate, wrappedNode as any);
    }

    /**
     * Handles navigation to an HFA origin position from the position sidebar.
     * Walks up the ECONode tree from the current node to find the ancestor
     * with the matching partKey, then re-opens the position sidebar for it.
     */
    /**
     * Navigates to the origin position of an HFA (Held From Above) tag.
     *
     * ARCHITECTURE NOTE (2026-03-27):
     * heldFromAbovePartKey points to a POST, but posts are NOT top-level ECO tree nodes.
     * They are sub-parts nested inside ECO nodes:
     *   - postsInParent (direct children of division/executive/department)
     *   - sectionsInDepartment[].postsInSection
     *   - sectionsInDepartment[].unitsInSection[].postsInUnit
     *
     * SEARCH ORDER:
     *   1. Ancestor chain of currentNode (handles same-branch navigation)
     *   2. All top-level ECO nodes by partKey/key (handles cross-branch navigation)
     *   3. Sub-parts inside all ECO nodes via _findPostInSubParts()
     *
     * When the target is found as a sub-part, we scroll to the CONTAINING ECO node
     * (the card) but open the sidebar for the specific POST (wrapped like
     * onOpenPositionTemplate does).
     *
     * @param partKey - The heldFromAbovePartKey from the post showing HFA
     * @param currentNode - The ECO node where the HFA icon was clicked
     */
    public onNavigateToHfaOrigin(partKey: string, currentNode: ECONode<Position>): void {
        if (!partKey || !currentNode) {
            console.warn('[org-board] onNavigateToHfaOrigin: missing partKey or currentNode', {
                partKey,
                hasNode: !!currentNode,
            });
            return;
        }

        const allNodes = this.tree?.nDatabaseNodes || [];

        // 1. Check if the target partKey is a top-level ECO node (ancestor chain first, then all nodes)
        let ancestor: ECONode<Position> | null | undefined = currentNode;
        while (ancestor) {
            const pos = ancestor.data as Position;
            if ((pos?.partKey || pos?.key) === partKey) {
                this._openOriginSidebar(ancestor, ancestor);
                return;
            }
            ancestor = ancestor.nodeParent;
        }

        const topLevelMatch = allNodes.find((n: ECONode<Position>) => {
            const p = n.data as Position;
            return (p?.partKey || p?.key) === partKey;
        });
        if (topLevelMatch) {
            this._openOriginSidebar(topLevelMatch, topLevelMatch);
            return;
        }

        // 2. Target is a sub-part (post inside postsInParent / sections / units).
        //    Find which ECO node contains the target post, then open sidebar for the POST's user(s).
        for (const n of allNodes) {
            const pos = n.data as any;
            const matchedPost = this._findPostInSubParts(pos, partKey);
            if (matchedPost) {
                // Wrap the post data like onOpenPositionTemplate does so any
                // position-sidebar fallback still has correct context.
                const wrappedNode = {
                    id: matchedPost.partKey || matchedPost.key || matchedPost.name,
                    data: {
                        ...matchedPost,
                        partType: matchedPost.partType || 'post',
                        parentPartKey: matchedPost.parentPartKey || (n.data as any)?.partKey,
                        colour: matchedPost.colour ||
                            (n.data as any)?.colour || {
                                color: '#000000',
                                background: '#FFFFFF',
                                linkColor: '#000000',
                            },
                        depth: matchedPost.depth ?? ((n.data as any)?.depth ?? 0) + 1,
                    },
                    nodeParent: n,
                    nodeChildren: [],
                };
                this._openOriginSidebar(wrappedNode as any, n);
                return;
            }
        }

        console.warn('[org-board] onNavigateToHfaOrigin: no node or sub-part found for partKey', {
            partKey,
            currentNodeKey: (currentNode.data as any)?.partKey || (currentNode.data as any)?.key,
            topLevelNodeCount: allNodes.length,
        });
    }

    /**
     * Helper for onNavigateToHfaOrigin — picks the right sidebar template for
     * the origin post and scrolls the board to the containing ECO node.
     *
     * If the origin post has active (non-pending) staff, open the user-detail
     * sidebar — which shows the full user view (all hats, org parts, SGs,
     * policies) rather than just the single-position view. This matches the
     * UX of clicking the user directly on their home post.
     *
     * If the origin post is vacant (no active users), fall back to the
     * position-detail sidebar so the user can still see the post itself.
     */
    private _openOriginSidebar(targetNode: ECONode<Position>, scrollNode: ECONode<Position>): void {
        const data: any = targetNode?.data;
        const activeUsers = (data?.userDetailsArray || []).filter((u: any) => !u?.isPending);

        if (activeUsers.length > 0) {
            const isMany = activeUsers.length > 1;
            // The user templates resolve via `data.data.data.userDetailsArray[0]`.
            // The sidebar service wraps the payload once (`templateData = { data: payload }`),
            // so passing the ECONode directly yields:
            //   data (templateData) → { data: ECONode }
            //   data.data           → ECONode
            //   data.data.data      → ECONode.data (position) ✔ has userDetailsArray
            // This matches the working path used by `getSidebarTemplateForNode($event)`.
            this.sidebarService.openTemplate(
                isMany ? this.sideBarManyUserTemplate : this.sideBarOneUserTemplate,
                targetNode
            );
        } else {
            this.sidebarService.openTemplate(this.sideBarPositionTemplate, targetNode);
        }
        this.scrollToNode(scrollNode);
    }

    /**
     * Searches an ECO node's nested sub-parts for a post matching the given partKey.
     *
     * ECO tree nodes (departments/divisions/executives) contain nested sub-parts:
     *   - postsInParent: Posts directly under the node
     *   - sectionsInDepartment[]: Each section contains:
     *     - postsInSection: Posts directly in the section
     *     - unitsInSection[]: Each unit contains:
     *       - postsInUnit: Posts in the unit
     *
     * This is used by onNavigateToHfaOrigin() to find which ECO node card
     * contains the target HFA origin post.
     *
     * @returns The matched post object, or null if not found.
     * @see org-board-builder.service.ts → appendSectionsAndUnitsDetailsToParts()
     *      for how these nested arrays are built.
     */
    private _findPostInSubParts(pos: any, partKey: string): any | null {
        // Match on partKey OR key — the builder stores HFA's origin as
        // `childPost.partKey || childPost.key`, so the lookup must mirror that
        // fallback or HFA navigation silently fails when only `key` is set.
        const matches = (p: any) => p && ((p.partKey && p.partKey === partKey) || (p.key && p.key === partKey));

        // Direct posts under the node (postsInParent / headerPostsInParent)
        const directPost = pos?.postsInParent?.find(matches) || pos?.headerPostsInParent?.find(matches);
        if (directPost) return directPost;

        // Detached groups (Board of Directors box etc.). These posts are pulled out
        // of the header/body buckets and rendered in a floating box, but they still
        // belong to this container — so a pool-user drop on a detached chip must
        // resolve here, or the fallback drop path finds the chip attrs and then
        // silently locates no part (the drop does nothing).
        for (const group of pos?.detachedGroups || []) {
            const groupPost = group?.posts?.find(matches);
            if (groupPost) return groupPost;
        }

        // Sections in department
        for (const section of pos?.sectionsInDepartment || []) {
            const sectionPost = section.postsInSection?.find(matches) || section.headerPostsInSection?.find(matches);
            if (sectionPost) return sectionPost;
            for (const unit of section.unitsInSection || []) {
                const unitPost = unit.postsInUnit?.find(matches) || unit.headerPostsInUnit?.find(matches);
                if (unitPost) return unitPost;
            }
        }

        // Units directly under the node (when department has units without sections)
        for (const unit of pos?.unitsInDepartment || []) {
            const unitPost = unit.postsInUnit?.find(matches) || unit.headerPostsInUnit?.find(matches);
            if (unitPost) return unitPost;
        }

        return null;
    }

    /**
     * Handles VFP (Valuable Final Product) changes from node components.
     * Saves the VFP text to Firestore via OrgBoardService.updatePart().
     */
    public async onVfpChanged(event: { partKey: string; vfp: string }): Promise<void> {
        const { partKey, vfp } = event;
        console.log('[org-board] onVfpChanged:', { partKey, vfp });

        if (!this.organization?.key || !partKey) {
            console.warn('[org-board] Cannot save VFP - missing org key or partKey');
            return;
        }

        // Find the part in the org's parts array and update it locally
        const part = this.organization.orgParts?.find((p) => p.partKey === partKey || p.key === partKey);
        if (part) {
            part.vfp = vfp;
        }

        // Also update the tree node data so views reflect the change immediately
        if (this.tree?.nDatabaseNodes) {
            const treeNode = this.tree.nDatabaseNodes.find(
                (n: any) => n.data?.partKey === partKey || n.data?.key === partKey
            );
            if (treeNode?.data) {
                treeNode.data.vfp = vfp;
            }
        }

        // Use the actual partKey from the found part for Firestore
        const firestorePartKey = part?.partKey || partKey;

        // Save to Firestore
        try {
            await this.obService.updatePart(
                { partKey: firestorePartKey, vfp } as any,
                this.organization,
                false // don't show toast for inline edits
            );
            console.log(
                '[org-board] VFP saved successfully for part:',
                firestorePartKey,
                '(emitted key:',
                partKey + ')'
            );
        } catch (error) {
            console.error('[org-board] Failed to save VFP:', error);
            this.ngZone.run(() => this.toastr.error('Failed to save VFP', 'Error'));
        }
    }

    /**
     * Handles per-VFP "always visible" pin toggle from node components.
     *
     * Pinned VFPs render permanently on the read-only org board (overriding the
     * default hover-to-reveal behaviour). Persisted on the part as
     * `vfpAlwaysVisible` via the same `updatePart` security task used by VFP
     * text edits.
     */
    public async onVfpAlwaysVisibleChanged(event: { partKey: string; vfpAlwaysVisible: boolean }): Promise<void> {
        const { partKey, vfpAlwaysVisible } = event;
        console.log('[org-board] onVfpAlwaysVisibleChanged:', { partKey, vfpAlwaysVisible });

        if (!this.organization?.key || !partKey) {
            console.warn('[org-board] Cannot save VFP pin state - missing org key or partKey');
            return;
        }

        // Mirror the optimistic local update pattern used by onVfpChanged so the
        // toggle feels instant even before the backend round-trip completes.
        const part = this.organization.orgParts?.find((p) => p.partKey === partKey || p.key === partKey);
        if (part) {
            (part as any).vfpAlwaysVisible = vfpAlwaysVisible;
        }

        if (this.tree?.nDatabaseNodes) {
            const treeNode = this.tree.nDatabaseNodes.find(
                (n: any) => n.data?.partKey === partKey || n.data?.key === partKey
            );
            if (treeNode?.data) {
                treeNode.data.vfpAlwaysVisible = vfpAlwaysVisible;
            }
        }

        // Pinned VFPs contribute to card height. Re-run the two-phase
        // height calculation so all sibling cards of the same partType
        // resize together (so the dept/division row stays aligned).
        this.updateNodeDimensions();

        const firestorePartKey = part?.partKey || partKey;

        try {
            await this.obService.updatePart(
                { partKey: firestorePartKey, vfpAlwaysVisible } as any,
                this.organization,
                false // don't show toast for inline edits
            );
            console.log('[org-board] VFP pin state saved for part:', firestorePartKey);
        } catch (error) {
            console.error('[org-board] Failed to save VFP pin state:', error);
            this.ngZone.run(() => this.toastr.error('Failed to save VFP pin state', 'Error'));
        }
    }

    // ---- Division VFP spanning boxes ----
    /** Track which division VFPs are being edited (by partKey) */
    public editingDivisionVfp: Record<string, boolean> = {};

    /**
     * Returns an array of division VFP box descriptors with calculated positions
     * that span across all child department nodes below each division.
     */
    public getDivisionVfpBoxes(): Array<{
        node: any;
        left: number;
        top: number;
        width: number;
        vfp: string;
        partKey: string;
        partType: string;
        bgColor: string;
    }> {
        if (!this.tree?.nDatabaseNodes) return [];
        const nodes = this.tree.nDatabaseNodes;
        const boxes: any[] = [];

        for (const node of nodes) {
            if (node.data?.partType !== 'division') continue;
            if (!node.data?.vfp && !this.isEditing) continue;

            // Find child department nodes
            const childNodes = nodes.filter(
                (n: any) => n.data?.parentPartKey === node.data?.partKey && !n._isAncestorCollapsed?.()
            );

            if (childNodes.length === 0) {
                // No visible children — render VFP below the division card itself
                boxes.push({
                    node,
                    left: node.XPosition,
                    top: node.YPosition + node.h + 8,
                    width: node.w,
                    vfp: node.data?.vfp || '',
                    partKey: node.data?.partKey || node.data?.key,
                    partType: 'division',
                    bgColor: node.bc || '#1565c0',
                });
            } else {
                // Span across children
                let minX = Infinity,
                    maxX = -Infinity,
                    maxBottom = -Infinity;
                for (const child of childNodes) {
                    if (child.XPosition < minX) minX = child.XPosition;
                    if (child.XPosition + child.w > maxX) maxX = child.XPosition + child.w;
                    if (child.YPosition + child.h > maxBottom) maxBottom = child.YPosition + child.h;
                }
                boxes.push({
                    node,
                    left: minX,
                    top: maxBottom + 8,
                    width: maxX - minX,
                    vfp: node.data?.vfp || '',
                    partKey: node.data?.partKey || node.data?.key,
                    partType: 'division',
                    bgColor: node.bc || '#1565c0',
                });
            }
        }
        return boxes;
    }

    public startEditingDivisionVfp(partKey: string): void {
        if (this.isEditing && !this.editingDivisionVfp[partKey]) {
            this.editingDivisionVfp[partKey] = true;
            setTimeout(() => {
                const box = document.querySelector(`.division-vfp-box textarea`) as HTMLTextAreaElement;
                if (box) {
                    box.focus();
                    box.setSelectionRange(box.value.length, box.value.length);
                }
            });
        }
    }

    private _divisionVfpCancelled: Record<string, boolean> = {};

    public cancelEditingDivisionVfp(partKey: string): void {
        this._divisionVfpCancelled[partKey] = true;
        this.editingDivisionVfp[partKey] = false;
    }

    public async saveDivisionVfp(partKey: string, value: string): Promise<void> {
        this.editingDivisionVfp[partKey] = false;
        await this.onVfpChanged({ partKey, vfp: value });
    }

    public onDivisionVfpBlur(partKey: string, value: string): void {
        if (this._divisionVfpCancelled[partKey]) {
            this._divisionVfpCancelled[partKey] = false;
            return;
        }
        this.saveDivisionVfp(partKey, value);
    }

    public onDivisionVfpKeydown(event: KeyboardEvent, partKey: string, textarea: HTMLTextAreaElement): void {
        if (event.key === 'Escape') {
            event.stopPropagation();
            event.preventDefault();
            this.cancelEditingDivisionVfp(partKey);
        } else if (event.key === 'Enter' && event.ctrlKey) {
            event.stopPropagation();
            event.preventDefault();
            this.saveDivisionVfp(partKey, textarea.value);
        }
    }

    public trackDivisionVfpBox(index: number, box: any): string {
        return box.partKey;
    }

    /**
     * Smoothly scrolls the org board to center a specific ECONode in view.
     */
    private scrollToNode(node: ECONode<Position>): void {
        setTimeout(() => {
            // Find the matching node in the tree's database (has XPosition/YPosition)
            const treeNode = this.tree?.nDatabaseNodes?.find(
                (n: ECONode<Position>) => n.id === node.id || n.data?.key === node.data?.key
            );
            if (!treeNode) return;

            const dragScrollItem = document.getElementsByClassName('drag-scroll-content')[0];
            if (!dragScrollItem) return;

            const clientWidth = document.documentElement.clientWidth;
            const clientHeight = document.documentElement.clientHeight;
            const treePaddingPx = this.tree.padding * 16;

            const halfNodeWidth = (treeNode.w * this.zoomLevel) / 2;
            const halfNodeHeight = (treeNode.h * this.zoomLevel) / 2;

            const nodeVisualX = treeNode.XPosition * this.zoomLevel + treePaddingPx;
            const nodeVisualY = treeNode.YPosition * this.zoomLevel + treePaddingPx;

            const newScrollX = nodeVisualX - clientWidth / 2 + halfNodeWidth;
            const newScrollY = nodeVisualY - clientHeight / 2 + halfNodeHeight;

            dragScrollItem.scrollTo({
                left: Math.max(0, newScrollX),
                top: Math.max(0, newScrollY),
                behavior: 'smooth',
            });
        });
    }

    public onRightClickNode(event: MouseEvent, node: any) {
        // console.log('onRightClickNode > event:', event);
        // preventDefault avoids to show the visualization of the right-click menu of the browser
        event.preventDefault();

        // we record the mouse position in our object
        this.menuTopLeftPosition.x = event.clientX + 'px';
        this.menuTopLeftPosition.y = event.clientY + 'px';

        // sidebarService.openTemplate(sideBarOneUserTemplate, $event)
        this.contextMenuItems = [];

        // Permission-gated context menu items
        if (this.mayManageUsers || this.mayManageSecurityGroups) {
            this.contextMenuItems.push({
                display: 'Hats',
                icon: 'school',
                iconColor: this.entityColors.getColor('hats'),
                action: () => {
                    this.dialogService.openHatsListDialog(node?.data?.name);
                },
            });
        }

        if (this.mayManageUsers && !this.hideStaffOnBoard) {
            this.contextMenuItems.push({
                display: 'Staff',
                icon: 'group',
                iconColor: this.entityColors.getColor('users'),
                action: () => {
                    this.dialogService.openUsersListDialog(node?.data?.name);
                },
            });
        }

        if (this.mayManageSecurityGroups) {
            this.contextMenuItems.push({
                display: 'Security Groups',
                icon: 'security',
                iconColor: this.entityColors.getColor('securityGroups'),
                action: () => {
                    this.dialogService.openSecurityGroupsListDialog(node?.data?.name);
                },
            });

            this.contextMenuItems.push({
                display: 'Policies',
                icon: 'policy',
                iconColor: this.entityColors.getColor('policies'),
                action: () => {
                    this.dialogService.openPoliciesListDialog(node?.data?.name);
                },
            });
        }

        // Add "Reorder Children" option if the node has 2+ children
        // This allows atomic reordering via the backend reorderChildren operation
        // Requires edit-organization-board permission
        if (this.mayEdit) {
            const partKey = node?.data?.partKey;
            if (partKey && this.organization?.orgParts) {
                const childCount = this.organization.orgParts.filter((p: any) => p.parentPartKey === partKey).length;

                if (childCount >= 2) {
                    this.contextMenuItems.push({
                        display: 'Reorder Children',
                        icon: 'swap_vert',
                        colour: 'primary',
                        action: () => {
                            this.dialogService.openReorderChildrenDialog(partKey);
                        },
                    });
                }
            }

            // Add "Assign Staff to Posts" for any node that has child posts
            if (node?.data?.partType !== 'post' && !this.hideStaffOnBoard) {
                this.contextMenuItems.push({
                    display: 'Assign Staff to Posts',
                    icon: 'group_add',
                    colour: 'primary',
                    action: () => {
                        this.onBulkAssignStaff(node);
                    },
                });
            }
        }

        this.contextMenuItems.push({
            display: 'Open',
            icon: 'open_in_new',
            action: () => {
                this.sidebarService.openTemplate(this.sideBarPositionTemplate, node);
            },
        });

        // we open the menu
        this.contextMenu.openMenu();
    }

    public onSelectColor($event: Event) {
        // Color selection handler
    }

    /** Armed while editing, so Back leaves edit mode before leaving the board. */
    private releaseEditBack: (() => void) | null = null;

    public onEditClick(isEditing: boolean) {
        this.isEditing = isEditing;
        this.isEditingChange.emit(this.isEditing);

        // Edit mode is a layer like any dialog (DESIGN.md §Sheets): with the
        // board in edit mode, Back drops the editor first and only the next
        // press leaves the board — the same unwinding the dialogs above it do
        // (Paul, 2026-08-04: "back to the editor, then back to the org board
        // without editor").
        if (isEditing && !this.releaseEditBack) {
            this.releaseEditBack = this.overlayBack.push(() => {
                this.releaseEditBack = null;
                this.onEditClick(false);
            });
        } else if (!isEditing && this.releaseEditBack) {
            const release = this.releaseEditBack;
            this.releaseEditBack = null;
            release();
        }

        // Reset the edit-only task toggles when leaving edit mode
        if (!isEditing) {
            this.isVfpEditingOn = false;
            this.hideStaffOnBoard = false;
        }

        this.updateTree();
        this.updateOrgBoardWidthCalculations(isEditing);

        // Re-measure DOM after edit mode toggle since new buttons/chrome appear/disappear
        // Use a short delay so Angular renders the edit-mode elements first
        setTimeout(() => this._correctDepartmentHeightsFromDOM(), 100);
    }

    private updateOrgBoardWidthCalculations(isEditing: boolean) {
        if (isEditing) {
            this.orgBoardWidth = 'calc(100vw - 1.5rem - 300px)';
        } else {
            this.orgBoardWidth = 'calc(100vw - 1.5rem)';
        }
    }

    public onDrop(event: CdkDragDrop<any>, targetNode?: ECONode<Position>) {
        console.log('=== onDrop CALLED ===', {
            hasData: !!event.item.data,
            fromPool: event.item.data?.fromPool,
            previousContainer: event.previousContainer?.id,
            currentContainer: event.container?.id,
        });

        const droppedUser = event.item.data;

        // Check if this is a drop from the user pool (ADD operation)
        if (droppedUser?.fromPool) {
            if (this.hideStaffOnBoard) {
                console.log('onDrop: staff hidden on the board — pool drop ignored');
                return;
            }
            // Generate a unique key for this drop to prevent duplicate processing
            const dropKey = `${droppedUser.uid}-${Date.now()}`;
            if (this.lastProcessedDropKey && this.lastProcessedDropKey.startsWith(droppedUser.uid)) {
                console.log('onDrop: Drop already processed for this user, skipping');
                return;
            }
            this.lastProcessedDropKey = dropKey;

            console.log('=== USER POOL DROP DEBUG ===');
            console.log('User dropped from pool:', droppedUser);

            // Get the drop position from the event (screen coordinates)
            const dropPoint = event.dropPoint;
            console.log('Drop point (screen coords):', dropPoint);

            // Use document.elementFromPoint to find the actual element under the cursor
            // This will help us identify if user dropped on a section/unit/post within a department
            let nestedTargetInfo: {
                partKey: string;
                partType: string;
                partName: string;
                parentDeptKey: string;
            } | null = null;

            if (dropPoint) {
                // First, temporarily hide the dragged element so elementFromPoint can find what's underneath
                const draggedElement = event.item.element.nativeElement;
                const originalDisplay = draggedElement.style.display;
                draggedElement.style.display = 'none';

                // Find the element at the drop point
                const elementAtPoint = document.elementFromPoint(dropPoint.x, dropPoint.y);
                console.log('Element at drop point:', elementAtPoint);

                // Restore the dragged element
                draggedElement.style.display = originalDisplay;

                // Walk up the DOM tree to find a droppable-part element (section/unit/post)
                if (elementAtPoint) {
                    let currentElement: Element | null = elementAtPoint;
                    let depth = 0;
                    console.log('Starting DOM traversal from:', elementAtPoint.tagName, elementAtPoint.className);
                    while (currentElement && currentElement !== document.body && depth < 20) {
                        console.log(
                            `  [${depth}] Checking:`,
                            currentElement.tagName,
                            'classes:',
                            currentElement.className,
                            'has droppable-part:',
                            currentElement.classList?.contains('droppable-part'),
                            'data-part-key:',
                            currentElement.getAttribute?.('data-part-key')
                        );

                        if (currentElement.classList?.contains('droppable-part')) {
                            const partKey = currentElement.getAttribute('data-part-key');
                            const partType = currentElement.getAttribute('data-part-type');
                            const partName = currentElement.getAttribute('data-part-name');
                            const parentDeptKey = currentElement.getAttribute('data-parent-dept-key');

                            console.log('Found droppable-part with attributes:', {
                                partKey,
                                partType,
                                partName,
                                parentDeptKey,
                            });

                            if (partKey && partType && partName && parentDeptKey) {
                                nestedTargetInfo = { partKey, partType, partName, parentDeptKey };
                                console.log('Found nested drop target:', nestedTargetInfo);
                                break;
                            }
                        }
                        currentElement = currentElement.parentElement;
                        depth++;
                    }
                    console.log('DOM traversal complete. Found nested target:', nestedTargetInfo);
                }
            }

            // If we found a nested target (section/unit/post), use that
            // Otherwise, fall back to the department node
            let actualTargetNode: ECONode<Position> | undefined;
            let targetPartData: any = null;

            if (nestedTargetInfo) {
                // Find the parent department node first
                console.log('Looking for dept with key:', nestedTargetInfo.parentDeptKey);
                console.log(
                    'Available dept keys:',
                    this.tree.nDatabaseNodes.map((n: ECONode<Position>) => ({
                        id: n.id,
                        dataKey: n.data?.key,
                        name: n.data?.name,
                    }))
                );

                actualTargetNode = this.tree.nDatabaseNodes.find(
                    (n: ECONode<Position>) => n.data?.key === nestedTargetInfo!.parentDeptKey
                );

                console.log(
                    'Found actualTargetNode:',
                    actualTargetNode
                        ? {
                              id: actualTargetNode.id,
                              key: actualTargetNode.data?.key,
                              name: actualTargetNode.data?.name,
                          }
                        : 'NOT FOUND'
                );

                // Now find the specific nested part within that department
                if (actualTargetNode && actualTargetNode.data) {
                    const dept = actualTargetNode.data as any;

                    if (nestedTargetInfo.partType === 'section' && dept.sectionsInDepartment) {
                        targetPartData = dept.sectionsInDepartment.find(
                            (s: any) => s.key === nestedTargetInfo!.partKey
                        );
                    } else if (nestedTargetInfo.partType === 'unit' && dept.sectionsInDepartment) {
                        // Units are nested within sections
                        for (const section of dept.sectionsInDepartment) {
                            if (section.unitsInSection) {
                                targetPartData = section.unitsInSection.find(
                                    (u: any) => u.key === nestedTargetInfo!.partKey
                                );
                                if (targetPartData) break;
                            }
                        }
                    } else if (nestedTargetInfo.partType === 'post') {
                        // Posts live in MANY collections: postsInParent, postsInSection,
                        // postsInUnit AND their header variants (headerPostsInParent /
                        // headerPostsInSection / headerPostsInUnit — head/deputy/assistant
                        // posts promoted into the card title strip). The old inline search
                        // only covered section/unit body posts, so drops on HEAD posts
                        // resolved to the department and got blocked by the container
                        // guard (ISS-001). _findPostInSubParts covers every collection.
                        targetPartData = this._findPostInSubParts(dept, nestedTargetInfo.partKey);
                    }
                }

                console.log('Target nested part data:', targetPartData);
            }

            // Fall back to dropListTracker if no nested target was found
            if (!actualTargetNode) {
                for (const [nodeId, dropList] of Object.entries(this.dropListTracker)) {
                    if (dropList === event.container) {
                        actualTargetNode = this.tree.nDatabaseNodes.find(
                            (n: ECONode<Position>) => n.id.toString() === nodeId
                        );
                        break;
                    }
                }
            }

            // Final fallback to passed targetNode
            const nodeToUpdate = actualTargetNode || targetNode;

            // Determine display name based on whether we're targeting nested part or department
            const displayName = targetPartData?.name || nodeToUpdate?.data?.name;
            const targetType = targetPartData
                ? nestedTargetInfo?.partType
                : (nodeToUpdate?.data as any)?.partType || 'department';

            console.log('Final target:', {
                displayName,
                targetType,
                nodeKey: nodeToUpdate?.data?.key,
                nestedPartKey: targetPartData?.key,
            });

            // =========================================================================
            // CONTAINER DROP GUARD: Only Posts can receive user drops.
            // Containers (executive/division/department/section/unit) are structural only.
            // Drop directly on a Post within the container card instead.
            // =========================================================================
            if (targetType !== 'post') {
                console.log('[onDrop] BLOCKED: Cannot drop user onto container type:', targetType);
                this.ngZone.run(() =>
                    this.toastr.warning(
                        `"${displayName}" is a ${targetType || 'container'}. Drop the user directly onto a Post within this position.`,
                        'Drop Blocked'
                    )
                );
                return;
            }

            // LINKED POST GUARD: a linked post represents another post and is staffed THERE.
            if (
                this.refuseLinkedPostAssignment(
                    targetPartData?.key || targetPartData?.partKey || nodeToUpdate?.data?.partKey
                )
            ) {
                return;
            }

            const finalDisplayName = targetPartData?.name || nodeToUpdate?.data?.name;

            // For pool drops, we ADD the user without removing from source
            // The pool is infinite - users are never "removed" from it
            if (nodeToUpdate && (targetPartData || nodeToUpdate.data)) {
                // Create a minimal UserDetails object for display purposes
                // Full data will be populated when the backend processes the hat assignment
                const requireApproval = !!this.organization?.settings?.requireHatAssignmentApproval;
                const userToAdd: any = {
                    key: droppedUser.uid,
                    uid: droppedUser.uid,
                    name: droppedUser.name,
                    email: droppedUser.email,
                    isPending: requireApproval, // Show as pending immediately only when approval workflow is on
                    // These will be properly populated after backend hat assignment
                    positions: [],
                    primaryPositionSecurityGroupsForUser: [],
                    secondaryPositionsSecurityGroups: [],
                    positionsObjects: {},
                };

                // Determine which userDetailsArray to update
                // If we have a nested target, add to that part's array
                // Otherwise, add to the department node's array
                const targetData = targetPartData || nodeToUpdate.data;

                // Ensure the target has a userDetailsArray
                if (!targetData.userDetailsArray) {
                    targetData.userDetailsArray = [];
                }

                // Check if user is already in the array (prevent duplicates from CDK + fallback both firing)
                const alreadyExists = targetData.userDetailsArray.some((u: any) => u.uid === userToAdd.uid);
                if (alreadyExists) {
                    console.log('User already exists in target, skipping duplicate add:', userToAdd.name);
                    return;
                }

                // Add the user to the array
                targetData.userDetailsArray.push(userToAdd);

                // Create a new array reference to trigger Angular change detection
                targetData.userDetailsArray = [...targetData.userDetailsArray];

                // If targeting a department node (not nested part), update userDropDataMap
                if (!targetPartData && nodeToUpdate.data) {
                    this.userDropDataMap[nodeToUpdate.id] = nodeToUpdate.data.userDetailsArray;
                }

                console.log('Updated userDetailsArray for:', finalDisplayName);
                console.log('Array length:', targetData.userDetailsArray.length);
                console.log('Target data after update:', JSON.stringify(targetData, null, 2));

                // Force the parent department node to update by creating new data reference
                // This ensures Angular's change detection picks up nested changes
                if (nodeToUpdate && nodeToUpdate.data) {
                    nodeToUpdate.data = { ...nodeToUpdate.data };
                }

                this.ngZone.run(() =>
                    this.toastr.info(
                        `Adding ${droppedUser.name} to ${finalDisplayName} (${targetType})...`,
                        'Hat Assignment'
                    )
                );

                // =========================================================================
                // PHASE 2: Show hat selection dialog to persist the assignment to database
                // =========================================================================
                // Get the partKey for the target position
                // Note: nested parts (section/unit/post) use 'key' property, departments use 'partKey'
                const partKey = targetPartData?.key || targetPartData?.partKey || nodeToUpdate?.data?.partKey;

                console.log('[onDrop] Determining partKey:', {
                    'targetPartData?.key': targetPartData?.key,
                    'targetPartData?.partKey': targetPartData?.partKey,
                    'nodeToUpdate?.data?.partKey': nodeToUpdate?.data?.partKey,
                    'final partKey': partKey,
                });

                if (partKey && this.organization) {
                    // Find hats linked to this position
                    const hatsForPosition = this.orgHattingService.getHatsForPosition(this.organization, partKey);

                    // Open the hat selection dialog, passing the actual target partKey and name (for nested parts)
                    this.dialogService
                        .openSelectHatDialog(
                            nodeToUpdate,
                            { uid: droppedUser.uid, name: droppedUser.name, email: droppedUser.email },
                            hatsForPosition,
                            partKey, // Pass the actual target partKey (may differ from node.data.partKey for nested parts)
                            finalDisplayName // Pass the actual target name (may differ from node.data.name for nested parts)
                        )
                        .then((result) => {
                            if (!result || !result.confirmed) {
                                // User cancelled - remove the visual update we made
                                this.ngZone.run(() => {
                                    this.toastr.warning('Assignment cancelled', 'Cancelled');

                                    // Remove the user from the array since they cancelled
                                    // Need to get fresh reference to ensure we're updating the right data
                                    const freshTargetData = targetPartData || nodeToUpdate?.data;
                                    if (freshTargetData?.userDetailsArray) {
                                        freshTargetData.userDetailsArray = freshTargetData.userDetailsArray.filter(
                                            (u: { uid: string }) => u.uid !== droppedUser.uid
                                        );
                                        // Create new array reference to trigger change detection
                                        freshTargetData.userDetailsArray = [...freshTargetData.userDetailsArray];
                                    }

                                    // Also update the node data reference to trigger change detection
                                    if (nodeToUpdate?.data) {
                                        nodeToUpdate.data = { ...nodeToUpdate.data };
                                    }

                                    this.updateTree();
                                    this.updateHeldFromAboveMapping();
                                    this.cdr.markForCheck();
                                    this.cdr.detectChanges();
                                });
                                return;
                            }

                            if (result.action === 'assign' && result.selectedHat) {
                                const userId = droppedUser.uid;
                                const hatName = result.selectedHat.name;
                                this.pendingHatAssignments.set(userId, { userName: droppedUser.name, hatName });
                                this.isHatAssignmentInProgress = true;

                                // Set local pending state based on whether approval workflow is active
                                const freshTargetData = targetPartData || nodeToUpdate?.data;
                                const localUser = freshTargetData?.userDetailsArray?.find(
                                    (u: { uid: string }) => u.uid === droppedUser.uid
                                );
                                if (localUser) {
                                    localUser.isPending = !!result.pending;
                                }

                                // Open processing panel with grey-out and monitor backend tasks
                                const assignPartKey =
                                    targetPartData?.key || targetPartData?.partKey || nodeToUpdate?.data?.partKey || '';
                                this.openUserOpPanel(userId, droppedUser.name, assignPartKey, 'assign');

                                if (result.pending) {
                                    // Approval workflow ON — no security tasks are created until approved.
                                    // The Firestore write is already done, so finish immediately.
                                    this.finishUserOp();
                                } else {
                                    // Direct assignment — backend creates security tasks, monitor them.
                                    this.monitorUserOpTasks();
                                }
                            } else if (result.action === 'create-new') {
                                // Open the new hat dialog with the position and user pre-selected
                                this.ngZone.run(() => {
                                    this.dialogService.openNewHatDialog(
                                        nodeToUpdate,
                                        result.user,
                                        result.targetPartKey || partKey
                                    );
                                    this.toastr.info('Please complete the new hat creation form.', 'Create Hat');
                                });
                            }
                        })
                        .catch((err) => {
                            console.error('[onDrop] Error in hat selection:', err);
                            this.ngZone.run(() => this.toastr.error('Error assigning staff member to hat', 'Error'));
                        });
                } else {
                    // No partKey — can't show hat dialog
                }
            }

            // Update tree layout and trigger full change detection cycle
            this.updateTree();
            this.updateHeldFromAboveMapping();

            // Use setTimeout to ensure change detection runs after current cycle
            // Also force a refresh of the dropLists array to re-establish connections
            setTimeout(() => {
                // Force dropLists to be a new array reference to trigger change detection on user pool
                const positionDropLists = Object.values(this.dropListTracker);
                this.dropLists = this.userPoolDropList
                    ? [...positionDropLists, this.userPoolDropList]
                    : [...positionDropLists];
                console.log('Post-drop refresh > Drop lists refreshed:', this.dropLists.length);

                this.cdr.markForCheck();
                this.cdr.detectChanges();
            }, 100); // Slightly longer delay to ensure tree has re-rendered

            return;
        }

        // Standard position-to-position drop (MOVE operation)
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }

        this.snackbar.open('Updating tree...', 'Close', { duration: 1500 });
        this.updateTree();
        // this.updateNodeDimensions();
        this.updateHeldFromAboveMapping();

        // if (!targetNode) {
        //   return;
        // }

        // this.showLoader = true;
        // this.orgHattingService.onHatTodo()
        //   .then(() => {
        //     this.showLoader = false;
        //     console.log('onDrop > orgHattingService.onHatTodo > success');
        //     // Update the UI to show that the user is now in the new container.
        //     if (event.previousContainer === event.container) {
        //       moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        //     } else {
        //       transferArrayItem(
        //         event.previousContainer.data,
        //         event.container.data,
        //         event.previousIndex,
        //         event.currentIndex,
        //       );
        //     }
        //     this.snackBar.open('Item moved successfully.', 'Close', { duration: 3000 });
        //   })
        //   .catch((error) => {
        //     this.showLoader = false;
        //     console.error('onDrop > orgHattingService.onHatTodo > error:', error);
        //     this.snackBar.open('Failed to move item.', 'Close', { duration: 3000 });
        //   });

        // TODO:
        // - update tree node heights after drop.
        // - Fireoff a save to the org/subcollection for the change.
    }

    // ============================================
    // User Pool Drawer Methods
    // ============================================

    /** Track if user pool drawer is open */
    public isUserPoolDrawerOpen = false;

    /**
     * Called when a user drag starts from the user pool.
     * Used to prepare the org board for receiving a drop.
     */
    public onUserPoolDragStart(event: { user: PoolUser; event: any }): void {
        console.log('User pool drag started:', event.user);
        this.isDroppableItemBeingDragged = true;
        // Reset the last processed drop key for new drag session
        this.lastProcessedDropKey = null;
        this._startDragHighlightTracking();
    }

    /**
     * Called when a user drag ends from the user pool.
     * Handles the drop logic if user was dropped on a valid position.
     * If CDK detection failed, uses fallback position to manually detect drop target.
     */
    public onUserPoolDragEnd(event: { user: PoolUser; event: any; fallbackPosition?: { x: number; y: number } }): void {
        console.log('User pool drag ended:', event.user);
        this.isDroppableItemBeingDragged = false;
        this._stopDragHighlightTracking();
        // Staff hidden: the drawer is not rendered, but a drag that started before the toggle
        // flipped (or a synthetic one) must not assign anyone onto a post nobody can see.
        if (this.hideStaffOnBoard) return;

        // Check if there's a fallback position (CDK detection failed)
        if (event.fallbackPosition) {
            // Check if this drop was already processed by onDrop (CDK handler)
            if (this.lastProcessedDropKey && this.lastProcessedDropKey.startsWith(event.user.uid)) {
                console.log('Fallback: Drop already processed by CDK onDrop, skipping');
                return;
            }

            console.log('=== FALLBACK DROP DETECTION ===');
            console.log('Fallback position:', event.fallbackPosition);

            // Use the same detection logic as onDrop
            const dropPoint = event.fallbackPosition;
            const elementAtPoint = document.elementFromPoint(dropPoint.x, dropPoint.y);
            console.log('Element at fallback position:', elementAtPoint);

            if (elementAtPoint) {
                // Find the droppable-part element
                let currentElement: Element | null = elementAtPoint;
                let nestedTargetInfo: {
                    partKey: string;
                    partType: string;
                    partName: string;
                    parentDeptKey: string;
                } | null = null;
                let depth = 0;

                while (currentElement && currentElement !== document.body && depth < 20) {
                    if (currentElement.classList?.contains('droppable-part')) {
                        const partKey = currentElement.getAttribute('data-part-key');
                        const partType = currentElement.getAttribute('data-part-type');
                        const partName = currentElement.getAttribute('data-part-name');
                        const parentDeptKey = currentElement.getAttribute('data-parent-dept-key');

                        if (partKey && partType && partName && parentDeptKey) {
                            nestedTargetInfo = { partKey, partType, partName, parentDeptKey };
                            console.log('Found nested drop target via fallback:', nestedTargetInfo);
                            break;
                        } else {
                            console.warn('[Fallback] .droppable-part missing attrs:', {
                                partKey,
                                partType,
                                partName,
                                parentDeptKey,
                            });
                        }
                    }
                    currentElement = currentElement.parentElement;
                    depth++;
                }

                if (nestedTargetInfo) {
                    // =========================================================================
                    // CONTAINER DROP GUARD (Fallback): Only Posts can receive user drops.
                    // Same guard as in onDrop() — containers are structural only.
                    // =========================================================================
                    if (
                        nestedTargetInfo.partType !== 'post' &&
                        OrganizationBoardComponent.CONTAINER_PART_TYPES.has(nestedTargetInfo.partType)
                    ) {
                        console.log(
                            '[Fallback] BLOCKED: Cannot drop user onto container type:',
                            nestedTargetInfo.partType
                        );
                        this.ngZone.run(() =>
                            this.toastr.warning(
                                `"${nestedTargetInfo.partName}" is a ${nestedTargetInfo.partType}. Create a Post in this position first before assigning users.`,
                                'Drop Blocked'
                            )
                        );
                        return;
                    }

                    // Find the department node and the nested part
                    const deptNode = this.tree.nDatabaseNodes.find(
                        (n: ECONode<Position>) => n.data?.key === nestedTargetInfo!.parentDeptKey
                    );

                    if (deptNode) {
                        const targetPartData = this.findNestedPart(deptNode.data, nestedTargetInfo.partKey);

                        if (targetPartData) {
                            console.log('Found target via fallback:', targetPartData.name);

                            // Linked post: staffed at the post it represents — refuse, write nothing.
                            if (
                                this.refuseLinkedPostAssignment(
                                    targetPartData?.key || targetPartData?.partKey || nestedTargetInfo.partKey
                                )
                            ) {
                                return;
                            }

                            // Create user object
                            const requireApprovalFb = !!this.organization?.settings?.requireHatAssignmentApproval;
                            const userToAdd = {
                                key: event.user.uid,
                                uid: event.user.uid,
                                name: event.user.name,
                                email: event.user.email || '',
                                isPending: requireApprovalFb, // Show as pending immediately only when approval workflow is on
                                positions: [],
                                primaryPositionSecurityGroupsForUser: [],
                                secondaryPositionsSecurityGroups: [],
                                positionsObjects: {},
                            };

                            // Add user to target
                            if (!targetPartData.userDetailsArray) {
                                targetPartData.userDetailsArray = [];
                            }

                            // Check for duplicates
                            const existingIndex = targetPartData.userDetailsArray.findIndex(
                                (u: any) => u.uid === userToAdd.uid
                            );

                            if (existingIndex === -1) {
                                targetPartData.userDetailsArray.push(userToAdd);
                                console.log('Fallback: Added user to', nestedTargetInfo.partName);

                                // Force update
                                if (deptNode.data) {
                                    deptNode.data = { ...deptNode.data };
                                }

                                this.snackbar.open(
                                    `Adding ${event.user.name} to ${nestedTargetInfo.partName} (${nestedTargetInfo.partType})...`,
                                    'Close',
                                    { duration: 2000 }
                                );

                                this.updateTree();
                                this.updateHeldFromAboveMapping();
                                this.cdr.markForCheck();
                                this.cdr.detectChanges();

                                // =========================================================================
                                // PHASE 2: Show hat selection dialog to persist the assignment to database
                                // =========================================================================
                                // Note: nested parts (section/unit/post) use 'key' property, use nestedTargetInfo.partKey as fallback
                                const partKey =
                                    targetPartData?.key || targetPartData?.partKey || nestedTargetInfo.partKey;

                                console.log('[Fallback] Determining partKey:', {
                                    'targetPartData?.key': targetPartData?.key,
                                    'targetPartData?.partKey': targetPartData?.partKey,
                                    'nestedTargetInfo.partKey': nestedTargetInfo.partKey,
                                    'final partKey': partKey,
                                });

                                if (partKey && this.organization) {
                                    // Find hats linked to this position
                                    const hatsForPosition = this.orgHattingService.getHatsForPosition(
                                        this.organization,
                                        partKey
                                    );

                                    // Open the hat selection dialog, passing the actual target partKey and name
                                    this.dialogService
                                        .openSelectHatDialog(
                                            deptNode,
                                            { uid: event.user.uid, name: event.user.name, email: event.user.email },
                                            hatsForPosition,
                                            partKey, // Pass the actual target partKey (for nested parts)
                                            nestedTargetInfo.partName // Pass the actual target name (for nested parts)
                                        )
                                        .then((result) => {
                                            if (!result || !result.confirmed) {
                                                // User cancelled - remove the visual update we made
                                                this.ngZone.run(() =>
                                                    this.toastr.warning('Assignment cancelled', 'Cancelled')
                                                );

                                                // Remove the user from the array since they cancelled
                                                if (targetPartData.userDetailsArray) {
                                                    targetPartData.userDetailsArray =
                                                        targetPartData.userDetailsArray.filter(
                                                            (u: { uid: string }) => u.uid !== event.user.uid
                                                        );
                                                }
                                                this.updateTree();
                                                this.updateHeldFromAboveMapping();
                                                this.cdr.markForCheck();
                                                this.cdr.detectChanges();
                                                return;
                                            }

                                            if (result.action === 'assign' && result.selectedHat) {
                                                const userId = event.user.uid;
                                                const hatName = result.selectedHat.name;
                                                this.pendingHatAssignments.set(userId, {
                                                    userName: event.user.name,
                                                    hatName,
                                                });
                                                this.isHatAssignmentInProgress = true;

                                                // Set local pending state based on whether approval workflow is active
                                                const localUser = targetPartData?.userDetailsArray?.find(
                                                    (u: { uid: string }) => u.uid === event.user.uid
                                                );
                                                if (localUser) {
                                                    localUser.isPending = !!result.pending;
                                                }

                                                // Open processing panel with grey-out and monitor backend tasks
                                                const assignPartKey =
                                                    targetPartData?.key ||
                                                    targetPartData?.partKey ||
                                                    nestedTargetInfo.partKey ||
                                                    '';
                                                this.openUserOpPanel(userId, event.user.name, assignPartKey, 'assign');

                                                if (result.pending) {
                                                    // Approval workflow ON — no security tasks are created until approved.
                                                    // The Firestore write is already done, so finish immediately.
                                                    this.finishUserOp();
                                                } else {
                                                    // Direct assignment — backend creates security tasks, monitor them.
                                                    this.monitorUserOpTasks();
                                                }
                                            } else if (result.action === 'create-new') {
                                                // Open the new hat dialog with the position and user pre-selected
                                                this.ngZone.run(() => {
                                                    this.dialogService.openNewHatDialog(
                                                        deptNode,
                                                        result.user,
                                                        result.targetPartKey || partKey
                                                    );
                                                    this.toastr.info(
                                                        'Please complete the new hat creation form.',
                                                        'Create Hat'
                                                    );
                                                });
                                            }
                                        })
                                        .catch((err) => {
                                            console.error('[Fallback] Error in hat selection:', err);
                                            this.ngZone.run(() =>
                                                this.toastr.error('Error assigning staff member to hat', 'Error')
                                            );
                                        });
                                } else {
                                    // No partKey — can't show hat dialog
                                }
                            } else {
                                console.log('Fallback: User already exists in', nestedTargetInfo.partName);
                                this.ngZone.run(() =>
                                    this.toastr.warning(
                                        `${event.user.name} is already assigned to ${nestedTargetInfo.partName}`,
                                        'Already Assigned'
                                    )
                                );
                            }
                        }
                    }
                } else {
                    console.log('Fallback: No droppable-part found at position');
                }
            }
        }
    }

    /**
     * Helper method to find a nested part (section/unit/post) within a department by key.
     */
    private findNestedPart(deptData: any, partKey: string): any {
        if (!deptData) return null;

        // Posts in ANY collection — including the header-post variants
        // (head/deputy/assistant) the old inline search missed, and matching
        // partKey OR key (the chip emits `partKey || key`). Without this, the
        // fallback drop path resolved a head post's chip attributes and then
        // silently found no part (ISS-001's last broken link).
        const post = this._findPostInSubParts(deptData, partKey);
        if (post) return post;

        // Check sections
        if (deptData.sectionsInDepartment) {
            for (const section of deptData.sectionsInDepartment) {
                if (section.key === partKey) return section;

                // Check units in section
                if (section.unitsInSection) {
                    for (const unit of section.unitsInSection) {
                        if (unit.key === partKey) return unit;

                        // Check posts in unit
                        if (unit.postsInUnit) {
                            for (const post of unit.postsInUnit) {
                                if (post.key === partKey) return post;
                            }
                        }
                    }
                }

                // Check posts directly in section
                if (section.postsInSection) {
                    for (const post of section.postsInSection) {
                        if (post.key === partKey) return post;
                    }
                }
            }
        }

        return null;
    }

    /**
     * Called when the user pool drawer is toggled open/closed.
     */
    public onUserPoolDrawerToggled(isOpen: boolean): void {
        this.isUserPoolDrawerOpen = isOpen;
        console.log('User pool drawer toggled:', isOpen);
    }

    /**
     * Called when a user is dropped from the pool onto a position on the org board.
     * This is the ADD operation - the user is being added to a position for the first time.
     */
    public onUserDroppedOnPosition(event: { user: PoolUser; event: any }): void {
        console.log('User dropped on position from pool:', event.user);
        // TODO Phase 2:
        // 1. Identify which position the user was dropped on
        // 2. Show dialog to select existing hat or create new hat for this position
        // 3. Call OrgHattingService to assign user to the selected/created hat
        // 4. Update the org board display to show the user on the position
    }

    /**
     * Called when a user is dragged from a position back to the pool.
     * This is the REMOVE operation - the user is being unassigned from a position.
     */
    public onUserRemovedFromPosition(event: { user: PoolUser; event: any }): void {
        // CDK fires `dropped` on the last-entered container even when the pointer
        // is released over a gap — only act on a real over-the-pool release.
        if (event?.event && event.event.isPointerOverContainer === false) return;
        if (this.hideStaffOnBoard) return; // staff hidden — no unassign-by-drag either

        const sourceNode = this.dragSourceNode;
        this.dragSourceNode = null;
        const user = event.user as PoolUser & { key?: string; name?: string };
        const userId = user?.uid || user?.key;
        const partKey: string | undefined = sourceNode?.data?.key || (sourceNode?.data as any)?.partKey;
        const partName: string = sourceNode?.data?.name || 'this post';
        if (!userId || !partKey) {
            // No tracked source (e.g. the drag never started from a post row).
            return;
        }

        // Drag/drop runs outside Angular's zone — dialogs must open inside it.
        this.ngZone.run(() => {
            this.dialog
                .open(ConfirmDialog, {
                    data: {
                        title: 'Remove from post?',
                        description:
                            `Remove ${user?.name || 'this user'} from ${partName}?

` + 'They will be unassigned from the post’s hat(s) and returned to the staff pool.',
                        confirmResultData: true,
                    },
                    maxWidth: '420px',
                })
                .afterClosed()
                .subscribe((confirmed) => {
                    if (!confirmed) return;
                    // Same route as the row's red remove button — resolves the part's
                    // hats (including nested section/unit parts) and unassigns.
                    void this.onRemoveUserFromPosition({ userId, partKey });
                });
        });
    }

    /**
     * Red remove button on a HEADER-STRIP chip (head / deputy / assistant post
     * rendered inside the container's title bar). Same guard + same route as
     * `org-board-node.onRemoveUser` for a body post row — header posts carry the
     * part key as `partKey` (the droppable attr uses `partKey || key`).
     */
    public onRemoveHeaderPostUser(user: any, post: any): void {
        const userId = user?.key || user?.uid;
        const partKey = post?.partKey || post?.key;
        if (!userId || !partKey) {
            console.warn('[org-board] Cannot remove header-post user - missing user key or partKey', { user, post });
            return;
        }
        void this.onRemoveUserFromPosition({ userId, partKey });
    }

    /**
     * Called when user clicks the remove button next to a user on a position.
     * Finds the hat for the position and removes the user from it.
     */
    public async onRemoveUserFromPosition(event: { userId: string; partKey: string }): Promise<void> {
        const { userId, partKey } = event;

        if (!this.organization || !this.organization.key) {
            this.ngZone.run(() => this.toastr.error('Organization not loaded', 'Error'));
            return;
        }

        // Find hats for this specific position (the hat must have this partKey in its positionArray)
        let hatsForPosition = this.orgHattingService.getHatsForPosition(this.organization, partKey);

        // If no hats found, this might be a nested part (section/unit/post).
        // Try to find the parent part and look for hats there.
        if (hatsForPosition.length === 0) {
            const parentPartKey = this.findParentPartKey(partKey);
            if (parentPartKey) {
                hatsForPosition = this.orgHattingService.getHatsForPosition(this.organization, parentPartKey);
            }
        }

        if (hatsForPosition.length === 0) {
            // No hat found for this position or its parent
            this.ngZone.run(() =>
                this.toastr.warning(
                    'No hat found for this org part. The staff member may need to be removed via the Hats panel.',
                    'Warning'
                )
            );
            return;
        }

        // Find which hat(s) the user is actually on for this position (active or pending)
        const hatsWithUser = hatsForPosition.filter(
            (hat) =>
                this.orgHattingService.isUserInHat(hat, userId) ||
                this.orgHattingService.isUserPendingInHat(hat, userId)
        );

        if (hatsWithUser.length === 0) {
            // Check if user is pending on special rights hats (not tied to this position)
            let hasPendingSpecialRights = false;
            if (this.organization?.orgHats?.hatsKeys) {
                for (const hatKey of this.organization.orgHats.hatsKeys) {
                    const hat = this.organization.orgHats[hatKey] as any;
                    if (hat && !hat.isSoftDeleted && hat.pendingAssignedToArray?.includes(userId)) {
                        hasPendingSpecialRights = true;
                        break;
                    }
                }
            }
            if (!hasPendingSpecialRights) {
                this.ngZone.run(() =>
                    this.toastr.warning('Staff member is not assigned to any hat on this org part', 'Warning')
                );
                return;
            }
        }

        // Get user name for confirmation
        const userName = this.organization.orgUsers?.[userId]?.name || 'this user';
        const hatNames = hatsWithUser.map((h) => h.name).join(', ');

        // Confirm removal
        const confirmed = await this.confirmDialogService.asyncConfirm(
            'Remove Staff Member from Org Part',
            `Are you sure you want to remove "${userName}" from "${hatNames}"?`
        );

        if (!confirmed) {
            return;
        }

        // Check if user is pending on these hats
        const isPending = hatsWithUser.some((hat) => this.orgHattingService.isUserPendingInHat(hat, userId));

        if (isPending) {
            // Remove pending user — use rejectPositionAssignment flow
            // Open the panel immediately
            this.openUserOpPanel(userId, userName, partKey, 'reject');
            try {
                const orgId = this.organization.key;
                let approvals = await this.orgHattingService.loadPendingApprovalsForUser(orgId, userId, partKey);
                // Fall back: hat-edit approvals may store a different or empty positionKey
                if (approvals.length === 0 && partKey) {
                    approvals = await this.orgHattingService.loadPendingApprovalsForUser(orgId, userId);
                }
                if (approvals.length > 0) {
                    await this.orgHattingService.rejectPositionAssignment(orgId, approvals[0].key);
                } else {
                    // No approval record — find ALL hats where user is pending (including special rights)
                    const allPendingHats: any[] = [];
                    if (this.organization?.orgHats?.hatsKeys) {
                        for (const hatKey of this.organization.orgHats.hatsKeys) {
                            const hat = this.organization.orgHats[hatKey] as any;
                            if (hat && !hat.isSoftDeleted && hat.pendingAssignedToArray?.includes(userId)) {
                                allPendingHats.push({ ...hat, key: hatKey });
                            }
                        }
                    }
                    if (allPendingHats.length > 0) {
                        await this.orgHattingService.removePendingUserFromHats(orgId, allPendingHats, userId);
                    }
                }
                // Update local orgHats to remove user from pendingAssignedToArray so the
                // tree rebuilds without the pending user (Firestore is already updated above).
                // IMPORTANT: Only clear from hats tied to THIS position (hatsWithUser), not all hats,
                // otherwise the user disappears from other posts where they're also pending.
                const affectedHatKeys = new Set(hatsWithUser.map((h) => h.key));
                for (const orgRef of [this.organization, this._organization]) {
                    if (orgRef?.orgHats?.hatsKeys) {
                        for (const hatKey of orgRef.orgHats.hatsKeys) {
                            if (!affectedHatKeys.has(hatKey)) continue;
                            const hat = orgRef.orgHats[hatKey] as any;
                            if (hat?.pendingAssignedToArray) {
                                hat.pendingAssignedToArray = hat.pendingAssignedToArray.filter(
                                    (uid: string) => uid !== userId
                                );
                            }
                        }
                    }
                }

                // No backend tasks for pending removal — go straight to done
                this.ngZone.run(() => {
                    this.finishUserOp();
                    // Full tree rebuild needed — updateTree() only re-renders, but pending user
                    // data is baked into tree nodes at build time via extractUserDetailsForPositions
                    if (this._organization?.key !== undefined) {
                        this.ensureOrgPartsSorted(this._organization);
                        const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);
                        const ecoTree = new ECOTree();
                        this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, {
                            isPositionsOn: this.isPositionsSwitchOn,
                        });
                        this.loadTreeTheNewWay(ecoTree);
                    }
                    this.cdr.detectChanges();
                });
            } catch (err) {
                console.error('[org-board] Failed to remove pending user:', err);
                this.userOpPanel.phase = 'error';
                this.processingUserKeysSet = new Set();
                this.isHatAssignmentInProgress = false;
                this.ngZone.run(() => this.toastr.error(`Failed to remove pending user: ${err}`, 'Error'));
            }
            return;
        }

        // Open the panel for active user removal
        this.openUserOpPanel(userId, userName, partKey, 'remove');

        // Remove active user from all hats they're on for this position
        for (const hat of hatsWithUser) {
            const hatKey = hat.key;

            const result = await this.orgHattingService.removeUserFromHat(this.organization.key, hatKey, userId);

            if (!result.succeeded) {
                console.error('[org-board] Failed to remove user from hat:', result.error);
                this.userOpPanel.phase = 'error';
                this.processingUserKeysSet = new Set();
                this.isHatAssignmentInProgress = false;
                this.ngZone.run(() => this.toastr.error(`Failed to remove user: ${result.error}`, 'Error'));
                return;
            }
        }

        // Also remove from any special rights hats where the user is assigned
        // Special rights hats are org-wide (no positionArray) and wouldn't be found by getHatsForPosition
        if (this.organization?.orgHats?.hatsKeys) {
            for (const hatKey of this.organization.orgHats.hatsKeys) {
                const hat = this.organization.orgHats[hatKey] as any;
                if (!hat || hat.isSoftDeleted) continue;
                // Special rights hats have no positionArray or an empty one
                const isSpecialRights = !hat.positionArray || hat.positionArray.length === 0;
                if (isSpecialRights && hat.assignedToArray?.includes(userId)) {
                    const srResult = await this.orgHattingService.removeUserFromHat(
                        this.organization.key,
                        hatKey,
                        userId
                    );
                    if (!srResult.succeeded) {
                        console.error(
                            `[org-board] Failed to remove user from special rights hat ${hatKey}:`,
                            srResult.error
                        );
                    }
                }
            }
        }

        // Monitor backend security tasks for the removal
        this.monitorUserOpTasks();

        console.log('[org-board] User removal initiated, tracking task completion via panel');
    }

    /**
     * Called when an admin clicks the green-tick approve button on a pending user
     * on the org board. Instead of silently approving every requested hat, we open
     * the Approve-on-Post review dialog (same one used from the user details
     * dialog) so the admin can untick any hats they don't want to assign right
     * now. Approving still moves the user from pending → assigned on the post.
     */
    public async onApproveUser(event: { user: any; node: any; post?: any }): Promise<void> {
        const { user, node, post } = event;
        const userId = user?.uid || user?.key;
        if (!userId || !this.organization?.key) return;

        const orgId = this.organization.key;
        // For sub-part posts, use post.key (position key); for ECO-level posts, use node.data.key
        // Note: post.partKey is the hat/part key, NOT the position key used in approval records
        const positionKey = post?.key || node?.data?.key;
        const userName = user?.name || 'this user';

        try {
            // Load pending approvals for this user from the hatting service
            let approvals = await this.orgHattingService.loadPendingApprovalsForUser(orgId, userId, positionKey);

            // Fall back: hat-edit approvals may store a different or empty positionKey
            if ((!approvals || approvals.length === 0) && positionKey) {
                approvals = await this.orgHattingService.loadPendingApprovalsForUser(orgId, userId);
            }

            if (!approvals || approvals.length === 0) {
                this.ngZone.run(() =>
                    this.toastr.warning('No pending approval found for this staff member on this org part', 'Warning')
                );
                return;
            }

            // Hand off to the consolidated review dialog. It will call
            // approvePositionAssignment / rejectPositionAssignment for each underlying
            // record itself, so we only need to monitor the resulting security tasks
            // if the admin actually approved.
            const result = await this.dialogService.openApproveAssignmentDialog(approvals);

            if (result?.action === 'approved') {
                // Open the progress panel now that the admin has confirmed, then watch
                // the backend security tasks.
                this.openUserOpPanel(userId, userName, positionKey, 'approve');
                this.monitorUserOpTasks();
            }
            // If the admin cancelled or rejected, no progress panel is needed —
            // the dialog already issued any reject calls and surfaced its own toasts.
        } catch (error: any) {
            console.error('[org-board] Error approving user:', error);
            this.ngZone.run(() => this.toastr.error(`Failed to approve: ${error.message}`, 'Error'));
        }
    }

    /**
     * Find the parent part key for a nested part (section/unit/post).
     * Searches through the org parts to find which part contains this nested part.
     */
    private findParentPartKey(partKey: string): string | null {
        if (!this.organization?.orgParts) {
            return null;
        }

        // Find the target part by its partKey
        const targetPart = this.organization.orgParts.find((p) => p.partKey === partKey);

        // If we found the target part and it has a parent, return the parent's partKey
        if (targetPart?.parentPartKey) {
            return targetPart.parentPartKey;
        }

        return null;
    }

    /**
     * Called when the user pool drop list is ready.
     * Adds it to the dropLists array so positions can drop users back to the pool.
     */
    public onPoolDropListReady(poolDropList: CdkDropList): void {
        // console.log('User pool drop list registered, rebuilding dropLists');
        this.userPoolDropList = poolDropList;
        // Rebuild dropLists to include the pool
        const positionDropLists = Object.values(this.dropListTracker);
        this.dropLists = [...positionDropLists, poolDropList];
        // console.log(`onPoolDropListReady > Total drop lists: ${this.dropLists.length}`);
    }

    public toggleNodeCollapse(node: ECONode<Position>) {
        this.tree.collapseNode(node.id, true); // True = redraw to close up space
        this.updateNodeLinkingLines(this.tree);

        // setTimeout(() => {
        //   console.log('toggleNodeCollapse > node:', node)
        // });
    }

    public zoomIn() {
        this.setZoom(stepBoardZoom(this.zoomLevel, 1));
    }

    public zoomOut() {
        this.setZoom(stepBoardZoom(this.zoomLevel, -1));
    }

    private setZoom(zoom: number) {
        this.zoomLevel = zoom;
        if (this.tree) this.tree.padding = 10 * this.zoomLevel;
    }

    /**
     * Ctrl/Cmd + mouse wheel (and a trackpad pinch, which browsers send as a
     * ctrl-wheel) zooms in fine steps; a plain wheel still scrolls the board.
     */
    public onBoardWheel(e: WheelEvent) {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        // One notch (~100px) = an eighth of a button step (~0.6%); trackpads send
        // many small deltas, so the zoom glides.
        const steps = Math.max(-1, Math.min(1, -e.deltaY / 800));
        if (steps !== 0) this.setZoom(stepBoardZoom(this.zoomLevel, steps));
    }

    public treeWidthScaled() {
        // if (this.zoomLevel < 1) {
        //   return this.tree.width * this.zoomLevel + 'px';
        // }

        // else if (this.zoomLevel > 1) {
        //   return this.tree.width * this.zoomLevel + 'px';
        // }

        return this.tree.width + 'px';
    }

    public treePaddingScaled() {
        // if (this.zoomLevel < 1) {
        //   return this.tree.padding * this.zoomLevel + 'rem';
        // }

        // else if (this.zoomLevel > 1) {
        //   return this.tree.padding * this.zoomLevel + 'rem';
        // }

        return this.tree.padding + 'rem';
    }

    public treeHeightScaled() {
        return this.tree.height + 'px';
    }

    public resetZoom() {
        this.zoomLevel = 1;
    }

    public onOpenUser(orgUser: OrgUser) {
        // Find the node with the user Key on it.
        let isManyUser = false;
        const node = this.tree.nDatabaseNodes.find((node: ECONode<Position>) => {
            const userDetailsArray = node.data?.userDetailsArray;

            if (userDetailsArray && userDetailsArray.length > 0) {
                const match = userDetailsArray.find((user) => user.key === orgUser.uid);
                if (match) {
                    isManyUser = userDetailsArray.length > 1;
                    return true;
                }
            }

            return null;
        });

        if (node) {
            this.sidebarService.openTemplate(isManyUser ? this.sideBarManyUserTemplate : this.sideBarOneUserTemplate, {
                data: node,
            });
        } else {
            // console.log('onSmoothHeightDone: Ignoring event (not the one we were waiting for).');
        }
    }

    public onOpenUserTemplate(event: { user: any; node: ECONode<Position> }): void {
        const user = event.user;
        const node = event.node;
        // HFA users are OrgUser objects (have uid), direct users are UserDetails (have key)
        const isHFA = !!user.uid && !user.key;

        this.sidebarService.openTemplate(this.sideBarUserTemplate, {
            user: user,
            node: node,
            isHFA: isHFA,
        });
    }

    public onSelectOrgSecurityTask(task: SecurityTask) {
        // alert('Feature under development.');
        this.dialogService.openOrgNotification(task);
    }

    public onOpenOrgNotifications() {
        this.dialogService.openOrgNotificationList();
    }

    //#endregion
    //#region Events and Domain Functions
    // $watches/$on events, functions which don't fall into any of the other regions,
    // including any function that has to do domain functionality.

    // ITEM
    public onDragItemStart(sourceNode?: ECONode<any>) {
        this.dragSourceNode = sourceNode ?? null;
        this.isDroppableItemBeingDragged = true;
        this.dragItem.emit(true);
        this._startDragHighlightTracking();
    }

    // ITEM
    public onDragItemEnd() {
        this.dragSourceNode = null;
        this.isDroppableItemBeingDragged = false;
        this.dragItem.emit(false);
        this._stopDragHighlightTracking();
    }

    // NAVIGATION
    public onDragNavStart(): void {
        setTimeout(() => {
            if (!this.isDragNavDisabled) this.isDragging = true;
            else this.isDragging = false;
            this.updateOverFlowBehaviour(false);
        }, 100);
    }

    // NAVIGATION
    public onDragNavEnd(): void {
        this.isDragging = false;
        this.updateOverFlowBehaviour(true);
    }

    // TODO: [FOCUS-FEATURE] Sibling focus/grey-out feature — commented out for now.
    // When re-enabling, restore the body of this method which:
    // - Toggles nodeSelected state
    // - Sets isSelected=true on clicked node + ancestors + descendants
    // - Sets isSelected=false on all other nodes
    // - Calls updateNodeLinkingLines() to update link stroke colors
    // Also restore the opacity bindings in templates:
    //   - org-board-node.component.html (line ~62): [ngStyle]="{'opacity': node?.isSelected ? '1' : '0.7'}"
    //   - one-user.component.html (line ~1): same
    //   - organization-board.component.html (lines ~469, ~586): same + background-color/color conditionals
    // And restore isSelected-based stroke in updateNodeLinkingLines().
    public selectSiblingNodes(node: ECONode<Position>) {
        // Feature disabled — no-op
        return;
        /*
    if (node == this.nodeSelected) {
      this.nodeSelected = null;
      this.tree.nDatabaseNodes.forEach(x => {
        x.isSelected = false;
      });
    } else {
      this.nodeSelected = node;
      const nodes = this.getSiblingNodes(node).map(x => x.id);
      this.tree.nDatabaseNodes.forEach(x => {
        x.isSelected = x.id == node.id || nodes.indexOf(x.id) >= 0;
      });
    }
    this.updateNodeLinkingLines(this.tree);
    */
    }

    public stateChange(state: boolean) {
        this.isDragNavDisabled = state;
        this.updateOverFlowBehaviour(state);
    }

    private toggleDragNavOverFlowClass(allowOverFlow = false) {
        // console.log('toggleDragNavOverFlowClass > allowOverFlow:', allowOverFlow)
        const dragScrollItem = document.getElementsByClassName('drag-scroll-content')[0];
        // console.log('toggleDragNavOverFlowClass > dragScrollItem:', dragScrollItem)
        if (dragScrollItem) {
            if (allowOverFlow) {
                dragScrollItem.classList.replace('overflow-hidden', 'overflow-scroll');
            } else {
                dragScrollItem.classList.replace('overflow-scroll', 'overflow-hidden'); // TODO this should be permanent.
            }
        } else {
            console.error('Could not find drag-scroll-content element.');
        }
    }

    // ---------------------------------------------------------------------------
    // Touch pan / pinch-zoom (mobile). Listeners are attached outside Angular so
    // panning (direct scrollLeft/scrollTop) doesn't churn change-detection; pinch
    // re-enters the zone to re-render the scaled tree. Gated to view mode; the
    // canvas sets `touch-action: none` (.ob-touch-nav) only when !isEditing, so
    // the browser hands us the gesture instead of page-scrolling/zooming.
    // ---------------------------------------------------------------------------
    /**
     * ISS-019: once a column's own header has scrolled off the top of the board, a label in its
     * division's colour is pinned over it ("Dept. 8 Disbursements" / "Div. 3 Treasury"). Drawn
     * straight into `#placeLayer` from a scroll listener outside the zone — it moves every frame
     * of a pan, which change detection has no business running for.
     */
    @ViewChild('placeLayer') private placeLayerRef?: ElementRef<HTMLElement>;
    private boardPlaceFrame = 0;
    private boardPlaceScrollEl?: HTMLElement;
    private readonly boardPlaceLabels = new Map<string, HTMLElement>();
    private readonly onBoardPlaceScroll = () => {
        if (this.boardPlaceFrame) return;
        this.boardPlaceFrame = requestAnimationFrame(() => {
            this.boardPlaceFrame = 0;
            this.updateBoardPlace();
        });
    };

    private attachBoardPlaceTracking(): void {
        const el = this.resolveScrollContainer();
        if (!el || el === this.boardPlaceScrollEl) return;
        this.boardPlaceScrollEl?.removeEventListener('scroll', this.onBoardPlaceScroll);
        this.boardPlaceScrollEl = el;
        this.ngZone.runOutsideAngular(() => el.addEventListener('scroll', this.onBoardPlaceScroll, { passive: true }));
    }

    private updateBoardPlace(): void {
        const el = this.boardPlaceScrollEl;
        const layer = this.placeLayerRef?.nativeElement;
        if (!layer) return;
        const wanted = new Set<string>();
        if (el && el.scrollTop > 40 && this.organization?.orgParts?.length) {
            const view = el.getBoundingClientRect();
            const origin = layer.getBoundingClientRect();
            el.querySelectorAll<HTMLElement>('[data-card-key]').forEach((card) => {
                const r = card.getBoundingClientRect();
                // The card spans the top edge and its header is out of sight.
                if (r.top >= view.top - 8 || r.bottom < view.top + 48) return;
                if (r.right <= view.left || r.left >= view.right) return;
                const key = card.getAttribute('data-card-key') || '';
                const place = boardPlace(this.organization?.orgParts as never, key);
                if (!place) return;
                wanted.add(key);
                let label = this.boardPlaceLabels.get(key);
                if (!label) {
                    label = document.createElement('div');
                    label.className = 'ob-place-label';
                    label.setAttribute('data-testid', 'board-place-label');
                    layer.appendChild(label);
                    this.boardPlaceLabels.set(key, label);
                }
                const text = [place.title, place.subtitle].join('|');
                if (label.dataset['place'] !== text) {
                    // Rebuilt only when the wording changes (a rename, another org).
                    label.dataset['place'] = text;
                    label.replaceChildren();
                    for (const [cls, value] of [
                        ['ob-place-title', place.title],
                        ['ob-place-sub', place.subtitle],
                    ]) {
                        if (!value) continue;
                        const span = document.createElement('span');
                        span.className = cls;
                        span.textContent = value;
                        label.appendChild(span);
                    }
                    label.title = [place.subtitle, place.title].filter(Boolean).join(' › ');
                }
                label.style.background = place.background || '';
                label.style.color = place.text;
                const left = Math.max(r.left, view.left) - origin.left;
                const right = Math.min(r.right, view.right) - origin.left;
                label.style.left = `${left + 4}px`;
                label.style.width = `${Math.max(right - left - 8, 0)}px`;
            });
        }
        for (const [key, label] of this.boardPlaceLabels) {
            if (!wanted.has(key)) {
                label.remove();
                this.boardPlaceLabels.delete(key);
            }
        }
    }

    public ngAfterViewInit(): void {
        // The drag-scroll content appears with the organization, so retry until it exists.
        const tryAttach = (attempt = 0) => {
            this.attachBoardPlaceTracking();
            if (!this.boardPlaceScrollEl && attempt < 20) setTimeout(() => tryAttach(attempt + 1), 500);
        };
        tryAttach();
        const el = this.dragContainerRef?.nativeElement;
        if (!el) return;
        this.ngZone.runOutsideAngular(() => {
            el.addEventListener('touchstart', this.boardTouchStart, { passive: true });
            el.addEventListener('touchmove', this.boardTouchMove, { passive: true });
            el.addEventListener('touchend', this.boardTouchEnd, { passive: true });
            el.addEventListener('touchcancel', this.boardTouchEnd, { passive: true });
        });
    }

    private resolveScrollContainer(): HTMLElement | undefined {
        if (!this.dragScrollContainer)
            this.dragScrollContainer = document.getElementsByClassName('drag-scroll-content')[0];
        return this.dragScrollContainer as HTMLElement | undefined;
    }

    private onBoardTouchStart(e: TouchEvent): void {
        const container = this.resolveScrollContainer();
        if (!container) return;

        if (e.touches.length >= 2) {
            // Two-finger pinch+pan works in BOTH view and edit mode — cdkDrag is
            // single-pointer, so it never competes with a two-finger gesture.
            const rect = container.getBoundingClientRect();
            this.isBoardPinching = true;
            this.isBoardPanning = false;
            this.lastPinchDist = touchDistance(e.touches[0], e.touches[1]);
            this.lastFocalX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
            this.lastFocalY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
        } else if (e.touches.length === 1) {
            // One-finger: in edit mode, if the touch lands on a draggable node, let
            // cdkDrag move it (don't pan). Empty canvas (or view mode) → pan.
            if (this.isEditing && this.isOnDraggable(e.target)) {
                this.isBoardPanning = false;
                this.isBoardPinching = false;
                return;
            }
            this.isBoardPanning = true;
            this.isBoardPinching = false;
            this.panLastX = e.touches[0].clientX;
            this.panLastY = e.touches[0].clientY;
        }
    }

    /** True when a touch starts on a CDK-draggable element (so we leave it to cdkDrag). */
    private isOnDraggable(target: EventTarget | null): boolean {
        return target instanceof HTMLElement && !!target.closest('.cdk-drag');
    }

    private onBoardTouchMove(e: TouchEvent): void {
        const container = this.dragScrollContainer as HTMLElement | undefined;
        if (!container) return;

        if (this.isBoardPinching && e.touches.length >= 2) {
            // Map-style gesture: pinch (zoom) AND drag (pan) at once. Tracked frame to
            // frame. With transform-origin 0 0 (CSS, touch view mode), one formula does
            // both — keep the content under the PREVIOUS focal under the CURRENT focal,
            // scaled by s: newScroll = (scroll + lastFocal) * s - currentFocal. s===1
            // (no distance change) → pure pan; focal unchanged → pure zoom-about-focal.
            const rect = container.getBoundingClientRect();
            const dist = touchDistance(e.touches[0], e.touches[1]);
            const focalX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
            const focalY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
            const oldZoom = this.zoomLevel;
            const newZoom = zoomFromPinch(oldZoom, this.lastPinchDist, dist);
            const s = newZoom / oldZoom;

            const newLeft = (container.scrollLeft + this.lastFocalX) * s - focalX;
            const newTop = (container.scrollTop + this.lastFocalY) * s - focalY;

            if (newZoom !== oldZoom) {
                // Re-enter Angular so the scaled tree (transform: scale) re-renders.
                this.ngZone.run(() => {
                    this.zoomLevel = newZoom;
                    if (this.tree) this.tree.padding = 10 * this.zoomLevel;
                });
            }
            container.scrollLeft = newLeft;
            container.scrollTop = newTop;

            this.lastPinchDist = dist;
            this.lastFocalX = focalX;
            this.lastFocalY = focalY;
        } else if (this.isBoardPanning && e.touches.length === 1) {
            const t = e.touches[0];
            container.scrollLeft -= t.clientX - this.panLastX;
            container.scrollTop -= t.clientY - this.panLastY;
            this.panLastX = t.clientX;
            this.panLastY = t.clientY;
        }
    }

    private onBoardTouchEnd(e: TouchEvent): void {
        if (e.touches.length === 0) {
            this.isBoardPanning = false;
            this.isBoardPinching = false;
        } else if (e.touches.length === 1) {
            // Dropped from two fingers to one → resume panning from the remaining finger.
            this.isBoardPinching = false;
            this.isBoardPanning = true;
            this.panLastX = e.touches[0].clientX;
            this.panLastY = e.touches[0].clientY;
        }
    }

    public scrollVertical(depl: number) {
        if (!this.dragScrollContainer)
            this.dragScrollContainer = document.getElementsByClassName('drag-scroll-content')[0];

        if (!this.interval) this.interval = interval(5);

        this.interval.pipe(takeUntil(this.terminateInterval)).subscribe(() => {
            if (this.dragScrollContainer) {
                this.dragScrollContainer.scrollTop -= depl;
            }
        });
    }

    public scrollHorizontal(depl: number) {
        if (!this.dragScrollContainer)
            this.dragScrollContainer = document.getElementsByClassName('drag-scroll-content')[0];

        if (!this.interval) this.interval = interval(5);

        this.interval.pipe(takeUntil(this.terminateInterval)).subscribe(() => {
            if (this.dragScrollContainer) {
                this.dragScrollContainer.scrollLeft -= depl;
            }
        });
    }

    public stopInterval() {
        this.terminateInterval.next();
    }

    private scrollToFirstNode(): void {
        // Settimeout to allow the treeView to render.
        setTimeout(() => {
            const node = this.tree.nDatabaseNodes[0];
            if (node) {
                const dragScrollItem = document.getElementsByClassName('drag-scroll-content')[0];

                // Set dragScrollItem overflow to hidden.

                if (dragScrollItem) {
                    // Set overflow to hidden. This is to help prevent a bug where if the cursor is dragging outside the container, the drag-scroll goes in the opposite direction.
                    dragScrollItem.classList.add('overflow-hidden');

                    // Collect the client width. This is used to determine where the center of the screen is for the current user's device.
                    const clientWidth = document.documentElement.clientWidth;

                    // Half of the client view, this is center for the user.
                    const halfClientView = clientWidth / 2;

                    // Half of the node width (scaled), this is because the XPosition is based on the first left pixel of the node and we need to calculate where its center is.
                    const halfNodeWidth = (node.w * this.zoomLevel) / 2;

                    // Calculate tree padding in pixels (rem to px conversion, assuming 16px per rem)
                    const treePaddingPx = this.tree.padding * 16;

                    // Calculate the actual visual position of the node including padding and zoom
                    const nodeVisualX = node.XPosition * this.zoomLevel + treePaddingPx;

                    // Calculate the new scroll position to center horizontally
                    const newScrollXPosition = nodeVisualX - (halfClientView - halfNodeWidth);

                    // Scroll to top (Y=0) to show root node at top of tree
                    const newScrollYPosition = 0;

                    // Scroll to the new position.
                    dragScrollItem.scrollTo(newScrollXPosition, newScrollYPosition);
                }
            }
        });
    }

    //#endregion
    //#region Data Access & Subscriptions
    // API interactions, NGRX selects, etc.

    //#endregion
    //#region Setters, Updaters and Preloaders
    // Setter functions, and methods called from the Instantiation region.

    public updateTree() {
        this.tree.UpdateTree();
        this.updateNodeLinkingLines(this.tree);
    }

    /**
     * Check if any pending hat assignment tasks have completed.
     * When all pending assignments are done, clear the flag and trigger a tree rebuild.
     */
    private checkPendingHatAssignments(tasks: SecurityTask[]): void {
        if (this.pendingHatAssignments.size === 0) {
            return; // No pending assignments to check
        }

        // Find updateUser tasks that are completed for our pending users
        const completedAssignments: Array<{ userId: string; userName: string; hatName: string }> = [];

        for (const [userId, info] of this.pendingHatAssignments) {
            // Look for a completed task with this user's uid
            const completedTask = tasks.find(
                (t) =>
                    t.uid === userId &&
                    t.typeOfTask === 'updateUser' &&
                    (t.status === 'completed' || t.status === 'fulfilled')
            );

            if (completedTask) {
                completedAssignments.push({ userId, userName: info.userName, hatName: info.hatName });
            }
        }

        // Remove completed users from pending map and show success toaster
        for (const assignment of completedAssignments) {
            this.pendingHatAssignments.delete(assignment.userId);

            // Show success toaster
            this.ngZone.run(() =>
                this.toastr.success(
                    `${assignment.userName} successfully assigned to ${assignment.hatName}`,
                    'Completed'
                )
            );
        }

        // If all pending assignments are complete, clear the flag and trigger rebuild
        if (this.pendingHatAssignments.size === 0 && completedAssignments.length > 0) {
            this.isHatAssignmentInProgress = false;

            // Apply deferred org update if one arrived while assignment was in progress
            if (this.deferredOrgUpdate) {
                this._organization = this.deferredOrgUpdate;
                this.deferredOrgUpdate = null;
            }

            // Trigger a tree rebuild with the latest organization data
            if (this._organization?.key !== undefined) {
                this.ensureOrgPartsSorted(this._organization);
                const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);
                const ecoTree = new ECOTree();
                this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, {
                    isPositionsOn: this.isPositionsSwitchOn,
                });
                this.loadTreeTheNewWay(ecoTree);

                if (this.tree?.nDatabaseNodes?.length > 0) {
                    this.selectSiblingNodes(this.tree.nDatabaseNodes[0]);
                }

                this.updateOrganizationOptions();
                this.dialogService.organization = this._organization;
                this.obService.organization = this._organization;
                this.obService.organization$.next(this._organization);
            }
        }
    }

    /**
     * Check if any pending hat removal tasks have completed.
     * Shows a success toaster when a removal completes.
     */
    private checkPendingHatRemovals(tasks: SecurityTask[]): void {
        if (this.pendingHatRemovals.size === 0) {
            return; // No pending removals to check
        }

        // Find updateUser tasks that are completed for our pending removals
        const completedRemovals: Array<{ userId: string; userName: string; hatName: string }> = [];

        for (const [userId, info] of this.pendingHatRemovals) {
            // Look for a completed task with this user's uid
            const completedTask = tasks.find(
                (t) =>
                    t.uid === userId &&
                    t.typeOfTask === 'updateUser' &&
                    (t.status === 'completed' || t.status === 'fulfilled')
            );

            if (completedTask) {
                completedRemovals.push({ userId, userName: info.userName, hatName: info.hatName });
            }
        }

        // Remove completed removals from pending map and show success toaster
        for (const removal of completedRemovals) {
            this.pendingHatRemovals.delete(removal.userId);

            // Show success toaster
            this.ngZone.run(() =>
                this.toastr.success(`${removal.userName} successfully removed from ${removal.hatName}`, 'Completed')
            );
        }
    }

    public updateDropList(node: ECONode<Position>, dropList: CdkDropList) {
        setTimeout(() => {
            this.dropListTracker[node.id] = dropList;
            this.rebuildDropLists();
        });
    }

    /** Rebuilds the connection set the user-pool drags against. */
    private rebuildDropLists(): void {
        const positionDropLists = Object.values(this.dropListTracker);
        this.dropLists = this.userPoolDropList ? [...positionDropLists, this.userPoolDropList] : positionDropLists;
    }

    /**
     * ═══════════════════════════════════════════════════════════════════════
     *  NODE HEIGHT CALCULATION ENGINE
     * ═══════════════════════════════════════════════════════════════════════
     *
     *  PURPOSE:
     *  Calculates pixel heights for every ECO tree node so that all nodes
     *  of the same partType share the SAME height (the tallest sibling wins).
     *  This creates the uniform row appearance on the org board.
     *
     *  TWO-PHASE APPROACH:
     *
     *    Phase 1 — Pre-render estimation (this method)
     *      Iterates all tree nodes, sums PX_* pixel constants based on the
     *      node's data (sections, units, posts, users, HFA, title wrapping).
     *      Tracks maxDeptHeight / maxDivHeight / maxExecHeight across all
     *      nodes of each type, then applies the max to every same-type node.
     *
     *    Phase 2 — Post-render DOM correction (_correctDepartmentHeightsFromDOM)
     *      Scheduled via setTimeout(0) AFTER Angular renders the nodes.
     *      Queries actual DOM elements using [data-part-type] attributes,
     *      measures scrollHeight, and bumps node.h if the real content
     *      exceeds the pre-calculated estimate.
     *
     *  CALL CHAIN:
     *    loadTreeTheNewWay() → updateNodeDimensions() → updateTree()
     *                                                 → setTimeout → _correctDepartmentHeightsFromDOM()
     *
     *  CSS HEIGHT CHAIN (how node.h becomes visible pixels):
     *    .node-wrapper     [height: node.h + 'px']   — absolute positioned in the SVG canvas
     *    .draggable-item   [height: node.h + 'px']   — flex column container
     *      > .above-card   (fixed size header bar)    — first flex child
     *      > section       [flex: 1, overflow:hidden] — second flex child, fills remaining space
     *        > ng-container [cdkDropListGroup]         — ⚠️ MUST be ng-container (no DOM element)
     *          > shared-org-board-node [height: 100%]  — host element, resolves to section's height
     *            > mat-card [height: 100%]             — the visible card
     *
     *  ⚠️ CRITICAL: The cdkDropListGroup wrapper MUST be <ng-container>, NOT <div>.
     *  A real <div> breaks the height chain because shared-org-board-node's
     *  height:100% would resolve against the div (which has no explicit height)
     *  instead of the <section> flex child. This was the cause of a height bug
     *  during the 3-component → 1-component consolidation (March 2026).
     *
     *  PIXEL CONSTANTS:
     *  The PX_* constants below are derived from the org-board-node template
     *  and organization-board.component.scss rules. If you add, remove, or
     *  resize template elements, update these constants AND the 15% safety
     *  buffer may need adjustment.
     *
     *  See: docs/ORG-BOARD-HEIGHT-CALCULATION-SPEC.md for full pixel breakdowns.
     */
    private updateNodeDimensions() {
        // Cards are equalised per ROW (same partType at the same tree level), not
        // per partType board-wide: the CEO's executive card carries a head AND a
        // deputy, and equalising every executive to it left a tall empty band on
        // the executives a row below (Nov 2026 wall-TV pass). Same for the band
        // min-height: the tallest estimated band content (title + subtitle wrap +
        // chip strip) in the row.
        const rowMaxHeight = new Map<string, number>();
        const rowMaxBand = new Map<string, number>();
        const rowMaxTotal = new Map<string, number>();
        const heightGroupKey = (node: ECONode<Position>) => orgBoardHeightGroup(node);
        const nodeCalculations: { height: number; width?: number; headerStripPx?: number; bandPx?: number }[] = []; // Store calculated dimensions

        // Header chip strip pixel constants (must mirror the inline styles in
        // organization-board.component.html → #headerStripTpl). Verified against
        // devtools 2026-04-28: wrapper rendered 46.79px tall for one chip.
        //   wrapper: padding-top 6px + margin-top 6px = 12px
        //   gap between chips: 6px (chips stacked vertically, full card width)
        //   each chip rendered ~41px (avatar 32 + chip vertical padding ~10).
        //     Use 44 with small headroom for sub-pixel rounding.
        const PX_HEADER_STRIP_WRAPPER = 12;
        const PX_HEADER_STRIP_ROW = 76; // chip: 52px avatar; two lines (role tag + 24px post, 24px person)
        const PX_HEADER_STRIP_GAP = 6;
        const calcHeaderStripPx = (nodeData: any): number => {
            const headerPosts = nodeData?.headerPostsInParent || [];
            const n = headerPosts.length;
            if (n === 0) return 0;
            // 1 chip per row at 300px card width
            return PX_HEADER_STRIP_WRAPPER + n * PX_HEADER_STRIP_ROW + (n - 1) * PX_HEADER_STRIP_GAP;
        };

        // Estimate the FULL .above-card / .above-card2 content height for one
        // node — title + subtitle text (with wrapping) + chip strip + padding.
        // Used to equalise the band min-height across siblings so cards line up
        // even when one has a long wrapping subtitle (e.g. "Office of the
        // Chief Executive Officer") plus a header chip.
        const calcBandContentPx = (nodeData: any, partType: string, stripPx: number, cardWidth = 300): number => {
            // Card inner width assumptions (panel width ~300 dept/division, 500 exec)
            // .above-card padding: 0.5rem each side = 16px chrome.
            const PADDING = 16;
            const BORDER = 2;
            if (partType === 'division') {
                // .above-card2: padding-top 1rem + padding-bottom 1.5rem = 40px
                const DIV_PADDING = 40;
                const mainTitle = nodeData?.mainTitle || '';
                const name = nodeData?.name || '';
                // mainTitle 2.4rem bold (~44px line, ~21px per char) and name 1.8rem
                // (~35px line, ~16px per char). A division card is often far wider than
                // 300px (it spans its departments), so wrap against its real width.
                const inner = Math.max(120, cardWidth - 40);
                const mainLines = Math.max(1, Math.ceil(mainTitle.length / Math.max(6, Math.floor(inner / 21))));
                const nameLines = Math.max(1, Math.ceil(name.length / Math.max(8, Math.floor(inner / 16))));
                const textPx = mainLines * 44 + nameLines * 35 + 8; // 8 = padding-bottom on mainTitle div
                return DIV_PADDING + BORDER + textPx + stripPx;
            }
            // department / executive
            const name = nodeData?.name || '';
            const subTitle = nodeData?.subTitle || '';
            // name 1.75rem bold (~34px line, ~15px per char); subtitle 1.3rem semibold
            // (~26px line, ~11px per char) — wall-TV pass, Nov 2026. Wrap against the
            // card's real width; Phase 2 measures and corrects any shortfall.
            const inner = Math.max(120, cardWidth - PADDING);
            const nameLines = Math.max(1, Math.ceil(name.length / Math.max(8, Math.floor(inner / 15))));
            const subLines = subTitle
                ? Math.max(1, Math.ceil(subTitle.length / Math.max(10, Math.floor(inner / 11))))
                : 0;
            const textPx = nameLines * 34 + subLines * 26;
            return PADDING + BORDER + textPx + stripPx;
        };

        // ─── Pinned VFP pixel constants ───
        // Mirrors the inline styles on the .vfp-section / .sub-part-vfp blocks
        // in org-board-node.component.html. A VFP block is rendered as a flex row
        // (icon column + text column). The text column controls the height
        // because it can wrap.
        //
        // Node-level VFP (.vfp-section, dept/exec card footer):
        //   padding: 10px top + 10px bottom = 20px chrome
        //   text font-size 16px * line-height 1.45 ≈ 23.2px per line
        //   icon column min-height ≈ 28(icon) + 10(label) = 38px
        //   → single-line block ≈ max(20 + 23, 20 + 38) ≈ 60px
        // Sub-part VFP (.sub-part-vfp on section/unit):
        //   padding: 8px top + 8px bottom = 16px chrome + 8px margin band
        //   text font-size 15px * 1.4 ≈ 21px per line
        //   icon column ≈ 22 + 9 = 31px
        //   → single-line ≈ 16 + 22 + 8 ≈ 46px
        // Sub-part VFP on a post:
        //   padding: 6px top + 6px bottom = 12px chrome + 8px margin band
        //   text font-size 14px * 1.4 ≈ 20px per line
        //   → single-line ≈ 12 + 20 + 8 ≈ 40px
        //
        // Wrapping estimates assume the text column is ~240px wide on a 300px
        // card (300 minus icon column 36-40 minus paddings/gaps).
        const PX_VFP_NODE_BASE = 60;
        const PX_VFP_NODE_LINE = 23;
        const PX_VFP_SUBPART_BASE = 46;
        const PX_VFP_SUBPART_LINE = 21;
        const PX_VFP_POST_BASE = 40;
        const PX_VFP_POST_LINE = 20;
        const VFP_CHARS_PER_LINE_NODE = 26; // 16px font in ~240px column
        const VFP_CHARS_PER_LINE_SUBPART = 28; // 15px font
        const VFP_CHARS_PER_LINE_POST = 30; // 14px font

        /** Per-VFP pixel cost for a part. Returns 0 if not pinned (and not in edit mode). */
        const calcVfpBlockPx = (
            vfpText: string | undefined,
            isPinned: boolean,
            kind: 'node' | 'subpart' | 'post'
        ): number => {
            // Only pinned VFPs are factored into height. Hover-reveal and
            // edit-mode placeholders are layered over the card via CSS overflow
            // (.vfp-hover-only floats below) and don't need to be reserved.
            if (!isPinned) return 0;
            const base =
                kind === 'node' ? PX_VFP_NODE_BASE : kind === 'subpart' ? PX_VFP_SUBPART_BASE : PX_VFP_POST_BASE;
            const linePx =
                kind === 'node' ? PX_VFP_NODE_LINE : kind === 'subpart' ? PX_VFP_SUBPART_LINE : PX_VFP_POST_LINE;
            const charsPerLine =
                kind === 'node'
                    ? VFP_CHARS_PER_LINE_NODE
                    : kind === 'subpart'
                      ? VFP_CHARS_PER_LINE_SUBPART
                      : VFP_CHARS_PER_LINE_POST;
            const text = vfpText || '';
            const lines = Math.max(1, Math.ceil(text.length / charsPerLine));
            // base already covers one line; add for additional wrapped lines.
            return base + Math.max(0, lines - 1) * linePx;
        };

        /**
         * Sum the pinned-VFP pixel cost contributed by all sub-parts of a
         * department: section VFPs, unit VFPs, and post VFPs (both
         * postsInSection and postsInUnit). Each pinned sub-part VFP renders
         * inline inside the card, so its height pushes the card taller.
         */
        const calcSubPartVfpHeight = (nodeData: any): number => {
            let h = 0;
            const sections = nodeData?.sectionsInDepartment || [];
            sections.forEach((section: any) => {
                h += calcVfpBlockPx(section?.vfp, !!section?.vfpAlwaysVisible, 'subpart');
                const postsInSection = section?.postsInSection || [];
                postsInSection.forEach((post: any) => {
                    h += calcVfpBlockPx(post?.vfp, !!post?.vfpAlwaysVisible, 'post');
                });
                const unitsInSection = section?.unitsInSection || [];
                unitsInSection.forEach((unit: any) => {
                    h += calcVfpBlockPx(unit?.vfp, !!unit?.vfpAlwaysVisible, 'subpart');
                    const postsInUnit = unit?.postsInUnit || [];
                    postsInUnit.forEach((post: any) => {
                        h += calcVfpBlockPx(post?.vfp, !!post?.vfpAlwaysVisible, 'post');
                    });
                });
            });
            return h;
        };

        // --- First Pass: Calculate individual heights and find max ---
        this.tree.nDatabaseNodes.forEach((node: ECONode<Position>) => {
            let calculatedHeight = 100; // Default height
            let calculatedWidth: number | undefined = undefined; // Default width

            if (node.data && node.id !== -1) {
                const userDetailsArr = node.data.userDetailsArray;
                const firstEntry = userDetailsArr?.[0];

                // Calculate lines (ensure these are calculated correctly for all cases)
                const firstLine = (node?.id?.toString() || '') + (firstEntry ? firstEntry.name : '');
                const secondLine = ''; // Adjust if needed
                const thirdLine = ''; // Adjust if needed
                let fourthLine = '';
                let fifthLineHeight = 0;

                if (userDetailsArr && userDetailsArr.length > 0) {
                    userDetailsArr.forEach((user) => {
                        fourthLine += user?.name ? user.name + ' ' : '';
                    });
                }

                const hfaUidData = (node.data as any)?.heldFromAboveUid;
                if (hfaUidData) {
                    fifthLineHeight = 20; // Base height for "HFA:" label
                    const hfaCount = (hfaUidData.split(',') || []).length;
                    fifthLineHeight += hfaCount * 40; // Add height for each HFA user
                }

                let characterLength = 0;
                const partType = (node.data as any)?.partType;
                const name = (node.data as any)?.name || '';

                // ─── Shared pixel constants for postsInParent (used by executive, division, department) ───
                // Per-post: .user-list-container margin 8px + padding 12px + title row ~20px = 40px chrome
                // Per-user in post: avatar 3rem (48px) + padding 4px + margin-top 4px = ~56px
                const PX_PIP_WRAPPER = 8; // margin-top: 0.25rem on outer wrapper
                const PX_PIP_CHROME = 44; // per post: margin(8) + padding(12) + title(20) + gap(4)
                const PX_PIP_USER = 48; // per user in a post: avatar(40) + padding(4) + spacing(4)
                const PX_PIP_HFA_USER = 30; // ONE HFA row: holders' 22px avatars side by side + HFA link chip

                // Helper: calculate postsInParent height for any container node
                const calcPostsInParentHeight = (nodeData: any): number => {
                    const postsInParent = nodeData?.postsInParent || [];
                    if (postsInParent.length === 0) return 0;
                    let h = PX_PIP_WRAPPER;
                    postsInParent.forEach((post: any) => {
                        const postUsers = post?.userDetailsArray || [];
                        h += PX_PIP_CHROME;
                        if (postUsers.length > 0) {
                            h += postUsers.length * PX_PIP_USER;
                        } else if (post?.heldFromAboveUserName) {
                            h += PX_PIP_HFA_USER; // one row however many holders
                        }
                    });
                    return h;
                };

                // Calculate height based on partType
                if (partType === 'division') {
                    // Division base: .above-card2 header (~100px for mainTitle+name+padding+border)
                    //   + mat-card border 2px + mat-card-content padding ~32px + general spacing ~16px
                    //   + buffer for browser rounding ~20px = ~170px
                    const PX_DIV_BASE = 120; // was 170: no empty grey block under a division band (Nov 2026)

                    // Division headers have mainTitle (large font ~1.8rem) and name (smaller font ~1.4rem)
                    const mainTitle = (node.data as any)?.mainTitle || '';
                    const mainTitleLength = mainTitle.length;
                    // mainTitle uses font-size 1.8rem (~16px per char); wrap against the card's real width
                    const mainTitleLines =
                        Math.ceil(mainTitleLength / Math.max(12, Math.floor(((node.w || 300) - 40) / 16))) || 1;
                    // Extra height for each additional line of mainTitle (approx 30px per line)
                    const headerExtraHeight = (mainTitleLines - 1) * 30;

                    const postsInParentHeight = calcPostsInParentHeight(node.data);

                    calculatedHeight = PX_DIV_BASE + fifthLineHeight + headerExtraHeight + postsInParentHeight;
                } else if (partType === 'department') {
                    characterLength = firstLine.length + secondLine.length + thirdLine.length + fourthLine.length;

                    // Per-element pixel constants derived from the inline template in one-user.component.ts
                    // and SCSS rules (.user-list-container margins, .grouped img margins, etc.).
                    // AUDITED 2026-03-17 against actual DOM. Key: .user-list-container is display:flex
                    // flex-direction:column so internal margins do NOT collapse.
                    //
                    // PX_SECTION_HEADER: the full height consumed by one section title block
                    //   .user-list-container margins: 16px (margin-top 8 + margin-bottom 8)
                    //   .section-header-container margin-bottom: 8px
                    //   .section-title margin-top: 8px + min-height 48px (border-box)
                    //   Total: 16 + 8 + 8 + 48 = 80px + 20px buffer for flex spacing = 100px
                    // Nov 2026: wrapper margins 8+8 -> 4+4 and the title lost its 8px top margin.
                    const PX_SECTION_HEADER = 84;
                    // ── Per-font-size wrapping constants ──
                    // Section titles: font-size 22px bold → ~13px/char, line-height 1.4 → ~31px per extra line
                    //   Available width ~202px → ~15 chars/line
                    const PX_SECTION_TITLE_EXTRA_LINE = 31;
                    const CHARS_PER_SECTION_LINE = 15;
                    // Unit titles: font-size 16px bold → ~10px/char, line-height 1.4 → ~22px per extra line
                    //   Available width ~224px → ~22 chars/line, use 18 conservative
                    const PX_UNIT_TITLE_EXTRA_LINE = 22;
                    const CHARS_PER_UNIT_LINE = 18;
                    // Post titles: font-size 14px bold → ~8.5px/char, line-height ~1.4 → ~20px per extra line
                    //   Available width ~212px → ~25 chars/line, use 20 conservative
                    const PX_POST_TITLE_EXTRA_LINE = 30; // 24px titles wrap at ~30px a line
                    const CHARS_PER_POST_LINE = 13; // 24px medium titles, same as names (Nov 2026)
                    // ── Part-type badge character equivalents ──
                    // Inline .part-type-badge: font-size 9px, letter-spacing 0.5px, padding 1px 6px,
                    // border 1px, margin-left 6px. Total px → divided by title font px/char.
                    // "Section" badge (~66px) at 13px/char = 5 equivalent chars
                    // "Unit" badge (~46px) at 10px/char = 5 equivalent chars
                    // "Post" badge (~46px) at 8.5px/char = 6 equivalent chars
                    const BADGE_CHARS_SECTION = 5;
                    const BADGE_CHARS_UNIT = 5;
                    const BADGE_CHARS_POST = 6;
                    // HFA user row: each HFA user in own .user-list-container (16px) + .grouped img
                    //   (32px) + SCSS .grouped img margin-top/bottom (16px) + padding-bottom 8px = 72px
                    //   Plus the wrapper div margin-top 0.5rem (8px) applied once per HFA group
                    // Department-level HFA keeps its old per-person rows (not the post HFA row).
                    const PX_DEPT_HFA_USER = 72;
                    const PX_HFA_USER = 30; // ONE HFA row: holders' avatars side by side + HFA link chip (Nov 2026)
                    // Extra margin for HFA wrapper div (margin-top: 0.5rem = 8px)
                    const PX_HFA_WRAPPER = 8;
                    // Direct user row: img 32px + SCSS margins 16px + padding-bottom 8px = 56px + 2px buffer
                    const PX_DIRECT_USER = 52;
                    // Extra margin for direct-user wrapper div (margin-top: 0.5rem = 8px)
                    const PX_USER_WRAPPER = 8;
                    // PX_UNIT_CHROME: full height of a unit container (without users/posts)
                    //   .user-list-container margins: 16px
                    //   .ob-unit-container padding: 12px top + 4px bottom = 16px
                    //   .ob-unit-title min-height: 48px (border-box, includes padding 12+12 + 2px border)
                    //   line-height: 2 on container adds extra spacing
                    //   Total: 16 + 16 + 48 = 80px + 30px for line-height 2 + flex spacing = 110px
                    const PX_UNIT_CHROME = 110;
                    // PX_POST_CHROME: post name (.ob-post-name)
                    //   font-size 14px * ~1.4 line-height = ~20px + padding 4+4 = 28px + spacing 12px
                    const PX_POST_CHROME = 44;
                    // A post directly in a section lost its wrapper's 8px margins (Nov 2026).
                    const PX_SECTION_POST_GAP_SAVED = 8;
                    // Wrapper for all posts in a unit: nested .user-list-container margin-top/bottom (16px)
                    const PX_POSTS_WRAPPER = 16;
                    // PX_BASE: everything outside sections content
                    //   .above-card: height 35.2px + padding 16px + border 2px = 53px
                    //   .org-position-card border: 2px
                    //   Dept user header: margin-top 8 + img 42 + margin-bottom 8 + border-bottom 2 = 60px
                    //   mat-card-content padding (MDC): ~16px top + 16px bottom = 32px
                    //   General spacing between header/content: ~16px
                    //   Buffer for browser rounding and flex gaps: ~21px
                    const PX_BASE = 190; // card-content padding-top 16 -> 6 (Nov 2026)

                    // Helper: extra height for title text wrapping beyond one line.
                    // badgeChars accounts for inline part-type badges ("Section", "Unit", "Post")
                    // that reduce available horizontal space and cause earlier line breaks.
                    const titleWrapExtra = (
                        name: string,
                        charsPerLine: number,
                        pxPerLine: number,
                        badgeChars = 0
                    ): number => {
                        const len = (name || '').length + badgeChars;
                        const lines = Math.ceil(len / charsPerLine) || 1;
                        return (lines - 1) * pxPerLine;
                    };

                    let sectionsHeight = 0;
                    const sectionsInDept = (node.data as any).sectionsInDepartment || [];

                    // Also account for department-level HFA users (rendered above sections)
                    const deptHfa = (node.data as any)?.heldFromAboveUserName;
                    if (deptHfa) {
                        const deptHfaUids = ((node.data as any)?.heldFromAboveUid || '')
                            .split(',')
                            .filter((s: string) => s.trim());
                        const deptHfaCount = Math.max(deptHfaUids.length, 1);
                        // "HFA:" label (~20px) + wrapper div margin-top + per user in own .user-list-container
                        sectionsHeight += 20 + PX_HFA_WRAPPER + deptHfaCount * PX_DEPT_HFA_USER;
                    }

                    sectionsInDept.forEach((section: any) => {
                        // Section header (title bar + .user-list-container margins) + extra for title wrapping
                        sectionsHeight +=
                            PX_SECTION_HEADER +
                            titleWrapExtra(
                                section?.name,
                                CHARS_PER_SECTION_LINE,
                                PX_SECTION_TITLE_EXTRA_LINE,
                                BADGE_CHARS_SECTION
                            );

                        // Section users: template shows EITHER HFA users OR own userDetailsArray
                        const sectionUsers = section?.userDetailsArray || [];
                        if (sectionUsers.length > 0) {
                            // Direct users wrapper div margin-top: 0.5rem (8px) + per user row
                            sectionsHeight += PX_USER_WRAPPER + sectionUsers.length * PX_DIRECT_USER;
                        } else if (section?.heldFromAboveUserName) {
                            // HFA wrapper div: margin-top 0.5rem (8px)
                            // Each HFA user in own .user-list-container
                            sectionsHeight += PX_HFA_WRAPPER + PX_HFA_USER; // one row for all holders
                        }

                        // Posts directly in this section (not inside a unit) — Section D3 in template
                        const postsInSection = section?.postsInSection || [];
                        if (postsInSection.length > 0) {
                            sectionsHeight += PX_POSTS_WRAPPER;
                        }
                        postsInSection.forEach((post: any) => {
                            const postUsers = post?.userDetailsArray || [];
                            sectionsHeight +=
                                PX_POST_CHROME -
                                PX_SECTION_POST_GAP_SAVED +
                                titleWrapExtra(
                                    post?.name,
                                    CHARS_PER_POST_LINE,
                                    PX_POST_TITLE_EXTRA_LINE,
                                    BADGE_CHARS_POST
                                ) +
                                postUsers.length * PX_DIRECT_USER;
                            // HFA users on postsInSection
                            if (postUsers.length === 0 && post?.heldFromAboveUserName) {
                                sectionsHeight += PX_HFA_WRAPPER + PX_HFA_USER; // one row for all holders
                            }
                        });

                        // Units in this section
                        const unitsInSection = section?.unitsInSection || [];
                        unitsInSection.forEach((unit: any) => {
                            // Unit container chrome (margins + padding + title) + extra for title wrapping
                            sectionsHeight +=
                                PX_UNIT_CHROME +
                                titleWrapExtra(
                                    unit?.name,
                                    CHARS_PER_UNIT_LINE,
                                    PX_UNIT_TITLE_EXTRA_LINE,
                                    BADGE_CHARS_UNIT
                                );

                            // Unit users: EITHER own userDetailsArray OR HFA
                            const unitUsers = unit?.userDetailsArray || [];
                            if (unitUsers.length > 0) {
                                // Direct users wrapper div margin-top: 0.5rem (8px) + per user row
                                sectionsHeight += PX_USER_WRAPPER + unitUsers.length * PX_DIRECT_USER;
                            } else if (
                                unit?.heldFromAboveUserName &&
                                (!unit?.postsInUnit || unit.postsInUnit.length === 0)
                            ) {
                                // HFA wrapper div: margin-top 0.5rem (8px)
                                sectionsHeight += PX_HFA_WRAPPER + PX_HFA_USER; // one row for all holders
                            }

                            // Posts in this unit
                            const postsInUnit = unit?.postsInUnit || [];
                            if (postsInUnit.length > 0) {
                                // Nested .user-list-container wrapping all posts adds margin-top/bottom
                                sectionsHeight += PX_POSTS_WRAPPER;
                            }
                            postsInUnit.forEach((post: any) => {
                                const postUsers = post?.userDetailsArray || [];
                                sectionsHeight +=
                                    PX_POST_CHROME +
                                    titleWrapExtra(
                                        post?.name,
                                        CHARS_PER_POST_LINE,
                                        PX_POST_TITLE_EXTRA_LINE,
                                        BADGE_CHARS_POST
                                    ) +
                                    postUsers.length * PX_DIRECT_USER;
                                // HFA users on postsInUnit
                                if (postUsers.length === 0 && post?.heldFromAboveUserName) {
                                    sectionsHeight += PX_HFA_WRAPPER + PX_HFA_USER; // one row for all holders
                                }
                            });
                        });
                    });

                    // Apply 5% safety buffer for browser rounding and sub-pixel flex gaps.
                    // (Reduced from 15% — badge widths, postsInSection, and per-font-size
                    // wrapping constants are now calculated explicitly.)
                    sectionsHeight = Math.ceil(sectionsHeight * 1.05);

                    // postsInParent height: posts rendered directly under the department card
                    const postsInParentHeight = calcPostsInParentHeight(node.data);

                    // Pinned VFPs render inline inside the card. The dept-level VFP
                    // (.vfp-section footer) plus every pinned sub-part VFP (sections,
                    // units, posts) all push the card taller — sum them so the Phase 1
                    // estimate already reserves the space and Phase 2 doesn't have to
                    // bump the heights up after first paint.
                    const deptNodeVfpPx = calcVfpBlockPx(
                        (node.data as any)?.vfp,
                        !!(node.data as any)?.vfpAlwaysVisible,
                        'node'
                    );
                    const deptSubPartVfpPx = calcSubPartVfpHeight(node.data);

                    calculatedHeight =
                        PX_BASE +
                        40 * Math.ceil(characterLength / 25) +
                        fifthLineHeight +
                        sectionsHeight +
                        postsInParentHeight +
                        deptNodeVfpPx +
                        deptSubPartVfpPx;
                } else if (partType === 'executive') {
                    // Executive base: .above-card header (~53px: 2.2rem + padding 0.5rem + border)
                    //   + mat-card border 2px + mat-card-content padding ~32px + general spacing ~16px
                    //   + buffer for browser rounding ~27px = ~130px
                    const PX_EXEC_BASE = 80; // was 130: no empty grey block under an executive band (Nov 2026)

                    // Account for header text wrapping (name + subTitle in .above-card)
                    const execName = (node.data as any)?.name || '';
                    // .above-card uses bold text at default size; ~20 chars per line in 500px-wide card
                    const execNameLines = Math.ceil(execName.length / 30) || 1;
                    const headerExtraHeight = (execNameLines - 1) * 22;

                    const postsInParentHeight = calcPostsInParentHeight(node.data);

                    // Executive cards may have a pinned node-level VFP at the bottom
                    // (.vfp-section). Reserve space for it in Phase 1 so the layout
                    // doesn't reflow after Phase 2 DOM measurement.
                    const execVfpPx = calcVfpBlockPx(
                        (node.data as any)?.vfp,
                        !!(node.data as any)?.vfpAlwaysVisible,
                        'node'
                    );

                    calculatedHeight =
                        PX_EXEC_BASE + fifthLineHeight + headerExtraHeight + postsInParentHeight + execVfpPx;
                    calculatedWidth = 500;
                } else if (partType === 'section') {
                    calculatedHeight = 200;
                    calculatedWidth = 300;
                } else if (partType === 'unit') {
                    calculatedHeight = 200;
                    calculatedWidth = 300;
                } else {
                    // Default calculation for unknown or other types
                    characterLength = firstLine.length + secondLine.length + thirdLine.length + fourthLine.length;
                    calculatedHeight = 100 + 40 * Math.ceil(characterLength / 25) + fifthLineHeight;
                }

                // Final validation for calculated height
                calculatedHeight =
                    typeof calculatedHeight === 'number' && !isNaN(calculatedHeight)
                        ? Math.max(100, calculatedHeight)
                        : 100;
            } else if (node.id === -1) {
                calculatedHeight = 0; // Root node placeholder
                calculatedWidth = 0;
            } else {
                // Fallback for nodes without data (should ideally not happen)
                calculatedHeight = 100;
                calculatedWidth = 300;
            }

            // Store calculation and update max heights per partType.
            // calculatedHeight here is the BODY-ONLY height (everything except the
            // header chip strip). The strip pixels are tracked separately and added
            // to node.h in the second pass using the equalised maxHeaderStrip* per
            // partType, so the body section never gets squeezed by a grown header
            // band on a sibling.
            const headerStripPx = calcHeaderStripPx(node.data);
            const partType = (node.data as any)?.partType;
            const bandPx =
                partType === 'department' || partType === 'executive' || partType === 'division'
                    ? calcBandContentPx(node.data, partType, headerStripPx, node.w || 300)
                    : 0;
            nodeCalculations.push({ height: calculatedHeight, width: calculatedWidth, headerStripPx, bandPx });

            const group = heightGroupKey(node);
            if (group) {
                rowMaxHeight.set(group, Math.max(rowMaxHeight.get(group) || 0, calculatedHeight));
                rowMaxBand.set(group, Math.max(rowMaxBand.get(group) || 0, bandPx));
                // Natural-band rows (division/executive): the card's OWN band + body,
                // so the tallest band and the tallest body are not taken from two
                // different cards and stacked (dead grey under every division).
                const ownTotal = calculatedHeight + Math.max(0, bandPx - (partType === 'division' ? 100 : 53));
                rowMaxTotal.set(group, Math.max(rowMaxTotal.get(group) || 0, ownTotal));
            }
        });

        // --- Second Pass: Apply heights (using max for same-type siblings) and widths ---
        // For each partType, the equalised band height (.above-card / .above-card2
        // min-height) is the largest estimated band content across all sibling
        // nodes of that type \u2014 this includes title + subtitle text wrapping +
        // chip strip. Non-chip cards thus reserve the same band height as the
        // tallest sibling, so cards line up visually even when one has a long
        // wrapping subtitle plus a header chip.
        this.tree.nDatabaseNodes.forEach((node: ECONode<Position>, i) => {
            const partType = (node.data as any)?.partType;
            const calculation = nodeCalculations[i];

            // Pick the equalised max band height for this card's row.
            const group = heightGroupKey(node);
            const maxBand = group ? rowMaxBand.get(group) || 0 : 0;

            // Set Height - use max body height for each type so siblings are uniform,
            // PLUS the equalised band height delta so the body never gets clipped
            // when this card has fewer chips / shorter title than the busiest
            // sibling. (calculatedHeight already includes a baseline .above-card
            // estimate of ~53/100px in PX_BASE/PX_DIV_BASE; we add only the EXTRA
            // band height beyond that baseline.)
            const baselineBand = partType === 'division' ? 100 : 53;
            const bandExtra = Math.max(0, maxBand - baselineBand);
            if (group && partType !== 'department') {
                this.tree.nDatabaseNodes[i].h = rowMaxTotal.get(group) || calculation.height;
            } else if (group) {
                this.tree.nDatabaseNodes[i].h = (rowMaxHeight.get(group) || 0) + bandExtra;
            } else {
                this.tree.nDatabaseNodes[i].h = calculation.height;
            }

            // Equalise the .above-card / .above-card2 band min-height to the
            // tallest sibling band content. The template binds
            // [style.min-height.px] = node.aboveCardMinH so every card header
            // grows to accommodate the busiest sibling band, even when this
            // card has zero chips and a short title.
            // Departments only: their sections must start level across the row.
            // Division and executive cards keep a natural band (the card height is
            // still the row's), so one division with a head AND an assistant no
            // longer paints an empty coloured block on every other division.
            if (partType === 'department') {
                (this.tree.nDatabaseNodes[i] as any).aboveCardMinH = maxBand;
            } else {
                (this.tree.nDatabaseNodes[i] as any).aboveCardMinH = null;
            }

            // Set Width
            if (calculation.width !== undefined) {
                this.tree.nDatabaseNodes[i].w = calculation.width;
            } else if (this.tree.nDatabaseNodes[i].w === undefined || isNaN(this.tree.nDatabaseNodes[i].w)) {
                // Ensure a default width if not specifically set
                this.tree.nDatabaseNodes[i].w = 300;
            }
            // Ensure width is 0 for root placeholder
            if (node.id === -1) {
                this.tree.nDatabaseNodes[i].w = 0;
            }
        });

        // Recalculate ECO tree XPosition/YPosition using the new node.h values.
        // ECOTree.UpdateTree() reads node.h but NEVER modifies it — it only sets
        // XPosition, YPosition, tree.width, and tree.height.
        this.updateTree();

        // Schedule post-render DOM measurement to correct department heights
        // from actual browser-measured content sizes. This runs after Angular
        // renders the nodes so we can measure scrollHeight of mat-card-content.
        if (!this._heightCorrectionInProgress) {
            setTimeout(() => this._correctDepartmentHeightsFromDOM(), 0);
        }
    }

    /**
     * Phase 2 of height calculation: Post-render DOM measurement.
     *
     * Runs AFTER Angular has rendered the nodes (via setTimeout(0) from
     * updateNodeDimensions). Queries actual DOM elements using the
     * [data-part-type] attribute on each mat-card, measures their real
     * scrollHeight, and bumps node.h if the actual content exceeds the
     * PX_*-based pre-calculation from Phase 1.
     *
     * This catches edge cases where CSS margin collapse, text wrapping,
     * or Material component padding differs from the constant estimates.
     *
     * DOM measurement formula per card:
     *   totalNeeded = aboveCardHeight + cardBorderTop + cardBorderBottom
     *                 + cardScrollHeight + 50px buffer
     *
     * Only INCREASES heights (never shrinks). Applies the same "max of
     * all same-type siblings" rule as Phase 1.
     *
     * Guarded by _heightCorrectionInProgress flag to prevent recursive
     * loops (since updateTree() + detectChanges() could re-trigger).
     */
    private _correctDepartmentHeightsFromDOM(): void {
        if (this._heightCorrectionInProgress) return;

        const typesToCorrect = ['department', 'division', 'executive'];
        let needsUpdate = false;
        this._heightCorrectionInProgress = true;

        // Needed height per ROW group (same partType + tree level, see orgBoardHeightGroup),
        // matching Phase 1's equalisation.
        const neededByGroup = new Map<string, number>();
        const nodeByKey = new Map<string, any>();
        this.tree.nDatabaseNodes.forEach((n: any) => {
            const key = n.data?.partKey || n.data?.key;
            if (key) nodeByKey.set(key, n);
        });

        typesToCorrect.forEach((partType) => {
            // Find all rendered cards with this partType via DOM attribute
            const cards = document.querySelectorAll(`[data-part-type="${partType}"]`);

            cards.forEach((card: Element) => {
                // Walk up to the .draggable-item container to measure the full node height
                const draggableItem = card.closest('.draggable-item') as HTMLElement | null;
                if (!draggableItem) return;

                const cardEl = card as HTMLElement;

                // Only correct if content actually overflows the card.
                // scrollHeight > clientHeight means content is being clipped.
                // Without this check, the buffer would cause Phase 2 to fire on
                // every render even when Phase 1 was perfectly accurate.
                if (cardEl.scrollHeight <= cardEl.clientHeight + 2) return; // +2 for sub-pixel rounding

                const node = nodeByKey.get(cardEl.getAttribute('data-card-key') || '');
                const group = orgBoardHeightGroup(node) || `${partType}:?`;

                // Measure the header bar (.above-card for dept/exec, .above-card2 for division)
                const aboveCard = draggableItem.querySelector('.above-card, .above-card2') as HTMLElement | null;
                // offsetHeight, not getBoundingClientRect: the board sits under a scale() transform,
                // so the rect is zoomed while scrollHeight below is not.
                const aboveCardHeight = aboveCard ? aboveCard.offsetHeight : 0;

                const cardStyle = window.getComputedStyle(cardEl);
                const cardBorderTop = parseFloat(cardStyle.borderTopWidth) || 0;
                const cardBorderBottom = parseFloat(cardStyle.borderBottomWidth) || 0;
                const cardScrollHeight = cardEl.scrollHeight;

                // Small buffer (10px) to prevent repeated corrections from sub-pixel rounding
                const totalNeeded = Math.ceil(
                    aboveCardHeight + cardBorderTop + cardBorderBottom + cardScrollHeight + 10
                );

                neededByGroup.set(group, Math.max(neededByGroup.get(group) || 0, totalNeeded));
            });
        });

        neededByGroup.forEach((maxNeededHeight, group) => {
            const inGroup = this.tree.nDatabaseNodes.filter((n: any) => {
                const g = orgBoardHeightGroup(n);
                return g === group || (group.endsWith(':?') && n.data?.partType === group.slice(0, -2));
            });
            // Current max allocated height for this row (from Phase 1)
            const currentMaxH = inGroup.reduce((max: number, n: any) => Math.max(max, n.h || 0), 0);

            // Only correct UPWARD — if actual content exceeds the pre-calculation
            if (maxNeededHeight > currentMaxH) {
                inGroup.forEach((node: any) => (node.h = maxNeededHeight));
                needsUpdate = true;
            }
        });

        if (needsUpdate) {
            this.updateTree();
            this.cdr.detectChanges();
        }

        this._heightCorrectionInProgress = false;
    }

    // ============================================
    // Drag Highlight Tracking
    // ============================================
    // CSS :hover is unreliable during CDK drag because pointer capture
    // prevents hover events from reaching elements under the drag preview.
    // Instead, we track pointermove and use elementFromPoint() to add/remove
    // a 'drag-hover' class on the innermost droppable-part under the cursor.

    private _startDragHighlightTracking(): void {
        if (this._dragPointerMoveHandler) return; // Already tracking
        if (this.hideStaffOnBoard) return; // staff hidden — nothing to highlight a drop target for

        this._dragPointerMoveHandler = (e: PointerEvent) => {
            // Find element under the cursor (CDK preview has pointer-events:none)
            const el = document.elementFromPoint(e.clientX, e.clientY);
            if (!el) {
                this._clearDragHover();
                return;
            }

            // Walk up DOM to find the innermost .droppable-part
            const target: Element | null = el.closest('.droppable-part');

            // If target has a nested .droppable-part child that is also under cursor,
            // find the deepest one. But closest() already finds the nearest ancestor,
            // which is the innermost match. So target IS the innermost.

            if (target === this._dragHoverElement) return; // No change

            // Remove from previous
            this._clearDragHover();

            // Add to new target
            if (target) {
                target.classList.add('drag-hover');
                this._dragHoverElement = target;
            }
        };

        document.addEventListener('pointermove', this._dragPointerMoveHandler);
    }

    private _stopDragHighlightTracking(): void {
        if (this._dragPointerMoveHandler) {
            document.removeEventListener('pointermove', this._dragPointerMoveHandler);
            this._dragPointerMoveHandler = null;
        }
        this._clearDragHover();
    }

    private _clearDragHover(): void {
        if (this._dragHoverElement) {
            this._dragHoverElement.classList.remove('drag-hover');
            this._dragHoverElement = null;
        }
    }

    /**
     * Updates the linking lines between nodes.
     * This is must be called if the colours are changed or if the nodes are moved.
     */
    public updateNodeLinkingLines(tree: ECOTree) {
        // console.log('updateNodeLinkingLines > tree:', tree);
        // Reset the children link paths.
        this.childrenLinkPaths = [];
        // Update the children link paths.
        tree.nDatabaseNodes.forEach((node: ECONode<any>) => {
            if (node.isCollapsed) return;
            const childrenLinks = node._drawChildrenLinks(tree);

            childrenLinks.forEach((path: string, i: number) => {
                const entry: any = {};
                // [FOCUS-FEATURE] Previously: stroke was conditional on isSelected
                // entry.stroke = (node.nodeChildren[i].isSelected ? (node.linkColor || null) : null);
                entry.stroke = node.linkColor || null;
                entry.d = !node._isAncestorCollapsed() ? path : null;
                this.childrenLinkPaths.push(entry);
            });
        });
        // Keep detached-group boxes + their cosmetic connectors in sync with the
        // tree layout (positions can shift on collapse / re-layout).
        this.updateDetachedBoxes();
    }

    // ───────────────────────────────────────────────────────────────────────────
    // Detached post groups (e.g. Board of Directors)
    // ───────────────────────────────────────────────────────────────────────────
    // A detached group lives in its parent container's data
    // (`node.data.detachedGroups`, built by the tree builder) but renders in its
    // OWN absolutely-positioned box elsewhere on the board, joined by hand-drawn
    // SVG connectors that are NOT real tree edges. The posts still belong to the
    // container, so dropping a user on a detached chip resolves via the same
    // `droppable-part` fallback used by header-strip chips — assignment is
    // identical to dropping inside the container. See
    // docs/categorized/features/ORG-BOARD-DETACHED-BOARD-OF-DIRECTORS-SPEC.md.
    public detachedBoxes: any[] = [];
    public detachedLinkPaths: any[] = [];

    /**
     * Rebuilds `detachedBoxes` (the floating box view-models) and
     * `detachedLinkPaths` (the cosmetic connector segments) from the current tree.
     * Cheap and idempotent — safe to call after every layout pass. Zoom needs no
     * recompute (the shared `.wrapper` scale transform covers it).
     *
     * Layout (per spec): the box sits top-left, ABOVE its connect-to target
     * (Division 7) when that target exists, otherwise just left of its owning
     * container. Two segments are drawn: a horizontal peer link from the owning
     * container (the CEO/exec box) to the Board box, then a vertical elbow down
     * from the Board box to the target.
     */
    public updateDetachedBoxes(): void {
        this.detachedBoxes = [];
        const nodes: any[] = this.tree?.nDatabaseNodes || [];
        for (const node of nodes) {
            const groups: any[] = (node?.data as any)?.detachedGroups;
            if (!groups || groups.length === 0) continue;
            if (node.isCollapsed || (node._isAncestorCollapsed && node._isAncestorCollapsed())) continue;

            for (const group of groups) {
                const target = group.connectsTo
                    ? nodes.find((n: any) => (n?.data?.partKey || n?.data?.key) === group.connectsTo)
                    : null;

                const boxW = 440; // room for 28px director names on a wall TV (Nov 2026)
                // Top-left, centred above the target (Division 7) when known.
                const left = target ? target.XPosition + target.w / 2 - boxW / 2 : node.XPosition - boxW - 80;
                const top = node.YPosition;

                this.detachedBoxes.push({
                    key: group.key,
                    title: group.key === 'board' ? 'Board of Directors' : group.key,
                    posts: group.posts || [],
                    left,
                    top,
                    width: boxW,
                    // Parent container's `key` — needed by the drop fallback's attr set.
                    parentDeptKey: (node.data as any)?.key,
                    // The owning ECO node — passed to onApproveUser so an admin can approve
                    // a pending director straight from the box (edit mode).
                    ownerNode: node,
                    // The division the box's line points at — a seat added from the
                    // box's menu inherits it.
                    connectsTo: group.connectsTo || null,
                    // Connector geometry, consumed by rebuildDetachedLinks(). `height` is
                    // an estimate until measureDetachedBoxes() reads the real DOM height,
                    // so the down-line starts exactly at the box's bottom edge.
                    height: 64 + (group.posts?.length || 1) * 92,
                    ownerX: node.XPosition,
                    stroke: node.linkColor || '#8a5a00',
                    target: target ? { x: target.XPosition + target.w / 2, y: target.YPosition } : null,
                });
            }
        }
        this.rebuildDetachedLinks();
        // The box renders on the next CD pass; measure its true height then redraw
        // the down-connector so it meets the box bottom (the estimate over/undershoots).
        if (this.detachedBoxes.length > 0) {
            setTimeout(() => this.measureDetachedBoxes(), 60);
        }
    }

    /**
     * Occupants to render on a detached-group post, matching the rest of the
     * board: view mode shows only active (non-pending) people; edit mode shows
     * everyone (so pending/invited directors are visible with approve/decline).
     */
    public visibleDetachedOccupants(post: any): any[] {
        const all = this.getHeaderPostUsers(post);
        return this.isEditing ? all : all.filter((u: any) => !u?.isPending);
    }

    /**
     * Applies a per-part setting change (from a sidebar/dialog) to the in-memory
     * organization and rebuilds the tree so build-time relationships like the HFA
     * cascade recompute immediately. Persistence is the caller's job (updatePart).
     */
    private applyPartSettingAndRebuild(partKey: string, changes: Partial<OrgPart>): void {
        const org = this._organization as any;
        if (!org?.orgParts || !partKey) return;
        const part = org.orgParts.find((p: any) => p.partKey === partKey || p.key === partKey);
        if (!part) return;
        Object.assign(part, changes);
        this.ensureOrgPartsSorted(org);
        const orgTree = this.treeBuilder.buildOrg(org, this.orbBoardId);
        const ecoTree = new ECOTree();
        this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, { isPositionsOn: this.isPositionsSwitchOn });
        this.loadTreeTheNewWay(ecoTree);
    }

    /** Recomputes the cosmetic connector paths from the current box geometry. */
    private rebuildDetachedLinks(): void {
        this.detachedLinkPaths = [];
        for (const box of this.detachedBoxes) {
            // 1) Horizontal peer link: owner (CEO/exec) box ── Board box, near the top.
            const linkY = box.top + 30;
            this.detachedLinkPaths.push({
                stroke: box.stroke,
                d: `M ${box.ownerX} ${linkY} L ${box.left + box.width} ${linkY}`,
            });
            // 2) Vertical elbow: Board box ──┐ down to the target (Division 7).
            if (box.target) {
                const boxMidX = box.left + box.width / 2;
                const boxBottomY = box.top + box.height;
                const midY = (boxBottomY + box.target.y) / 2;
                this.detachedLinkPaths.push({
                    stroke: box.stroke,
                    d: `M ${boxMidX} ${boxBottomY} L ${boxMidX} ${midY} L ${box.target.x} ${midY} L ${box.target.x} ${box.target.y}`,
                });
            }
        }
    }

    /** Reads the rendered box heights and redraws the connectors to match. */
    private measureDetachedBoxes(): void {
        if (this.detachedBoxes.length === 0) return;
        const els = document.querySelectorAll('.ob-detached-box');
        let changed = false;
        els.forEach((el) => {
            const idx = Number((el as HTMLElement).getAttribute('data-detached-idx'));
            const h = (el as HTMLElement).offsetHeight; // unscaled CSS px (transform-independent)
            if (this.detachedBoxes[idx] && h > 0 && Math.abs(this.detachedBoxes[idx].height - h) > 1) {
                this.detachedBoxes[idx].height = h;
                changed = true;
            }
        });
        if (changed) {
            this.rebuildDetachedLinks();
            this.cdr.detectChanges();
        }
    }

    public updateOverFlowBehaviour(allowOverflow: boolean) {
        if (allowOverflow) {
            this.toggleDragNavOverFlowClass(true);
        } else {
            this.toggleDragNavOverFlowClass(false);
        }
    }

    /**
     * Loads a newly-built ECOTree into the component and kicks off rendering.
     * This is the entry point for the height calculation pipeline:
     *
     *  1. Builds userDropDataMap for CDK drag-drop.
     *  2. Assigns the tree and draws SVG linking lines.
     *  3. Calls updateNodeDimensions() which runs the two-phase height system:
     *     Phase 1: PX_* constant estimation → uniform max heights per partType.
     *     Phase 2: Post-render DOM measurement → upward correction if needed.
     *
     * Called from: onBuildTree (initial load), drag-drop handlers, edit handlers.
     */
    private loadTreeTheNewWay(ecoTree: ECOTree) {
        this.userDropDataMap = {}; // Reset drop list data map.
        for (let i = 0; i < ecoTree.nDatabaseNodes.length; i++) {
            const ecoNode: ECONode<Position> = ecoTree.nDatabaseNodes[i];
            // Set the user drop data map for each node. This must be done this way to ensure that the memory (heap) reference is correct.
            this.userDropDataMap[ecoNode.id] = ecoNode.data?.userDetailsArray || [];
        }

        this.tree = ecoTree;
        // Set initial tree padding based on zoom level
        this.tree.padding = 10 * this.zoomLevel;
        // this.updateHeldFromAboveMapping();

        this.updateNodeLinkingLines(this.tree);
        // Trigger the two-phase height calculation system (see updateNodeDimensions JSDoc).
        this.updateNodeDimensions();
        // Node heights/positions may have shifted in the dimension pass — refresh the
        // detached boxes + connectors against the final layout.
        this.updateDetachedBoxes();
    }

    // Container part types — structural nodes that can NEVER be directly staffed.
    private static readonly CONTAINER_PART_TYPES = new Set(['executive', 'division', 'department', 'section', 'unit']);

    /**
     * Updates HFA mapping for ECO tree nodes and their inline posts after drag-drop events.
     * ECO nodes are always containers (executive/division/department) and don't receive HFA themselves.
     * However, their inline posts (postsInParent, postsInSection, postsInUnit) need HFA calculated
     * by looking at ancestor containers' child posts for users.
     */
    private updateHeldFromAboveMapping() {
        const allInlinePosts: any[] = [];
        for (let i = 0; i < this.tree.nDatabaseNodes.length; i++) {
            const ecoNode: ECONode<Position> = this.tree.nDatabaseNodes[i];

            if (!ecoNode.data || ecoNode.id === -1) continue;

            // Clear any existing HFA data on the ECO node itself (containers don't get HFA)
            if (ecoNode.data && (ecoNode.data as any).heldFromAboveUid) {
                (ecoNode.data as any).heldFromAboveUid = undefined;
                (ecoNode.data as any).heldFromAboveUserName = undefined;
                (ecoNode.data as any).heldFromAboveColor = undefined;
                (ecoNode.data as any).heldFromAbovePartKey = undefined;
            }

            // Collect all inline posts within this ECO node's data
            const inlinePosts: any[] = [];
            const nodeData = ecoNode.data as any;

            // Posts directly under this container (executive/division/department)
            if (nodeData.postsInParent) {
                for (const post of nodeData.postsInParent) {
                    inlinePosts.push(post);
                }
            }

            // Posts inside sections and units within departments
            if (nodeData.sectionsInDepartment) {
                for (const section of nodeData.sectionsInDepartment) {
                    if (section.postsInSection) {
                        for (const post of section.postsInSection) {
                            inlinePosts.push(post);
                        }
                    }
                    if (section.unitsInSection) {
                        for (const unit of section.unitsInSection) {
                            if (unit.postsInUnit) {
                                for (const post of unit.postsInUnit) {
                                    inlinePosts.push(post);
                                }
                            }
                        }
                    }
                }
            }

            allInlinePosts.push(...inlinePosts);

            // For each inline post that has no users, calculate HFA from ancestor containers' posts
            for (const post of inlinePosts) {
                // Clear existing HFA
                post.heldFromAboveUid = undefined;
                post.heldFromAboveUserName = undefined;
                post.heldFromAboveColor = undefined;
                post.heldFromAbovePartKey = undefined;

                if (post.userDetailsArray && post.userDetailsArray.length > 0) {
                    continue; // Post has its own users — no HFA needed
                }

                // Walk up the ECO tree from this node to find ancestor containers with staffed posts
                const parentFamilyTree = this.getParentAndGrandParents(ecoNode);

                let hfaUIDs: string[] = [];
                const hfaNames: string[] = [];
                let hfaColor = '';
                let hfaPartKey = '';
                let depthOfHFA = -1;

                for (let j = 0; j < parentFamilyTree.length; j++) {
                    const p = parentFamilyTree[j];
                    if (!~p.id || !p.data) continue;

                    // Check this ancestor container's inline posts for users
                    const ancestorData = p.data as any;
                    const ancestorPosts: any[] = [];
                    if (ancestorData.postsInParent) {
                        ancestorPosts.push(...ancestorData.postsInParent);
                    }

                    for (const ap of ancestorPosts) {
                        // Posts flagged `excludeFromHfa` (e.g. board members who govern but
                        // don't run the org) are not HFA sources.
                        if (ap.excludeFromHfa) continue;
                        if (ap.userDetailsArray && ap.userDetailsArray.length > 0) {
                            if (~depthOfHFA && p.data.depth !== depthOfHFA) break;

                            hfaUIDs = hfaUIDs.concat(ap.userDetailsArray.map((x: any) => x.key)).flat();
                            hfaNames.push(ap.userDetailsArray.map((x: any) => x.name).join(', '));
                            if (!hfaColor && p.data?.colour?.background) hfaColor = p.data.colour.background;
                            if (!hfaPartKey && (ap.partKey || ap.key)) hfaPartKey = ap.partKey || ap.key;

                            if (depthOfHFA === -1 && ~p.data.depth) depthOfHFA = p.data.depth;
                        }
                    }

                    if (hfaUIDs.length > 0) break; // Found HFA source, stop looking further up
                }

                if (hfaUIDs.length > 0) {
                    post.heldFromAboveUid = hfaUIDs.join(',');
                    post.heldFromAboveUserName = hfaNames.join(', ');
                    post.heldFromAboveColor = hfaColor || undefined;
                    post.heldFromAbovePartKey = hfaPartKey || undefined;
                }
            }
        }
        // Linked posts show their target post's holders (after every post's own HFA is known). Every
        // post on the board is a candidate TARGET — head/deputy posts and the Board of Directors box are
        // not in allInlinePosts, and without them a link resolved on first build vanishes after a drag.
        const linkCandidates = new Set<any>(allInlinePosts);
        for (const n of this.tree.nDatabaseNodes) for (const p of postsOfCard(n?.data)) linkCandidates.add(p);
        applyLinkedPosts([...linkCandidates]);
        this.updateNodeLinkingLines(this.tree);
        this.updateNodeDimensions();
    }

    //#endregion
    //#region Getters and Filters
    // Getter functions and filter methods/functions.

    public getSiblingNodes(node: ECONode<Position>) {
        return [...this.getParentAndGrandParents(node), ...this.getChildren(node)];
    }

    /**
     * Recursively gets all parents of the node, and each of the parent's parents.
     */
    private getParentAndGrandParents(node: ECONode<any>, nodes: ECONode<any>[] = []) {
        if (node.nodeParent) {
            nodes = [...nodes, node.nodeParent];
            nodes = this.getParentAndGrandParents(node.nodeParent, nodes);
        }
        return nodes;
    }

    private getChildren(node: ECONode<any>, nodes: ECONode<any>[] = []) {
        const children = node.nodeChildren;
        if (children && children.length) {
            nodes = [...nodes, ...children];
            children.forEach((x) => {
                nodes = this.getChildren(x, nodes);
            });
        }
        return nodes;
    }

    //#endregion
    //#region Change Detection - building the whole tree for org board
    // Deep config comparisons, change log generation, hasChangesFn, etc.
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['organization']) {
            const newOrg = changes['organization'].currentValue;
            const userKeys = newOrg?.orgUsers?.userKeys || [];
            const firstUser = userKeys[0] ? newOrg.orgUsers[userKeys[0]] : null;
            // console.log('[ngOnChanges] Organization update received:', {
            //   hasOrgUsers: !!newOrg?.orgUsers,
            //   userKeysCount: userKeys.length,
            //   userKeys: userKeys,
            //   firstUserName: firstUser?.name || firstUser?.userName || 'NO NAME FIELD',
            //   firstUserKeys: firstUser ? Object.keys(firstUser) : []
            // });

            if (!this._organization) {
                this._organization = changes['organization'].currentValue;

                // Ensure orgParts are sorted by ID for consistent visual ordering
                this.ensureOrgPartsSorted(this._organization);

                this.dialogService.organization = this._organization;

                if (this._organization?.key !== undefined) {
                    const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);
                    const ecoTree = new ECOTree();

                    // ecoTree is built from orgTree. ecoTree is a new instance of ECOTree and only contains the skeleton structure initialized.
                    // Insert positions into the ecoTree based on the orgTree.
                    // IMPORTANT: Pass isPositionsOn from component state to prevent dialogs from affecting the toggle
                    this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, {
                        isPositionsOn: this.isPositionsSwitchOn,
                    });

                    this.loadTreeTheNewWay(ecoTree);

                    // Select the first node in the tree.
                    if (this.tree?.nDatabaseNodes?.length > 0) {
                        this.selectSiblingNodes(this.tree.nDatabaseNodes[0]);
                    }

                    if (this._organization.key !== this.previousOrgKey) this.scrollToFirstNode();

                    // Pre-load profile colors from orgUsers cache, then fetch any missing from Firestore
                    this.preloadProfileColors(this._organization);

                    this.showLoader = false;
                    this.previousOrgKey = this._organization.key;

                    // Emit all organization updates and changes to the service. This will be used by other components to ensure they have the latest organization as well.
                    this.obService.organization = this._organization;
                    this.obService.organization$.next(this._organization);

                    // Monitor the /securityTasks collection for updates on what the backend is busy doing. This is how we will notify the user of changes and on going events.
                    if (this.securityTasksSub) this.securityTasksSub.unsubscribe();
                    this.securityTasksSub = this.obService
                        .monitorSecurityTasks()
                        .pipe(takeUntil(this.destroy$))
                        .subscribe((tasks) => {
                            this.securityTasks = tasks;

                            // Check if any pending hat assignments have completed
                            this.checkPendingHatAssignments(tasks);

                            // Check if any pending hat removals have completed
                            this.checkPendingHatRemovals(tasks);
                        });

                    this.updateOrganizationOptions();

                    // const userKeys = this._organization.orgUsers.userKeys;
                    // this.userOptions = userKeys.map(key => {
                    //   const user = this._organization.orgUsers[key];
                    //   return { display: user.name, value: user.key };
                    // });

                    // const policyKeys = this._organization.orgPolicies.policiesKeys;
                    // this.policyOptions = policyKeys.map(key => {
                    //   const policy = this._organization.orgPolicies[key];
                    //   return { display: policy.name, value: policy.key };
                    // });

                    // const securityGroupKeys = this._organization.orgSecurityGroups.securityGroupsKeys;
                    // this.securityGroupOptions = securityGroupKeys.map(key => {
                    //   const securityGroup = this._organization.orgSecurityGroups[key];
                    //   return { display: securityGroup.name, value: securityGroup.key };
                    // });

                    // const hatKeys = this._organization.orgHats.hatsKeys;
                    // this.hatOptions = hatKeys.map(key => {
                    //   const hat = this._organization.orgHats[key];
                    //   return { display: hat.name, value: hat.key };
                    // });
                }
            } else {
                // Organization already exists, this is a subsequent update

                // Skip ALL updates if hat assignment is in progress to avoid "Unknown User" flash
                // The backend updates the org doc in stages, causing incomplete user data temporarily
                // We keep the OLD organization data (with correct user info) until task completes
                if (this.isHatAssignmentInProgress) {
                    // DO NOT update _organization, dialogService, or obService
                    // This preserves the correct user display names during backend processing
                    // But store the latest data so we can apply it when the assignment finishes
                    this.deferredOrgUpdate = changes['organization'].currentValue;
                    return;
                }

                this._organization = changes['organization'].currentValue;

                // Ensure orgParts are sorted by ID for consistent visual ordering
                this.ensureOrgPartsSorted(this._organization);

                this.dialogService.organization = this._organization;

                if (this._organization?.key !== undefined) {
                    // Rebuild the tree with the new organization data
                    const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);

                    const ecoTree = new ECOTree();
                    // IMPORTANT: Pass isPositionsOn from component state to prevent dialogs from affecting the toggle
                    this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, {
                        isPositionsOn: this.isPositionsSwitchOn,
                    });
                    this.loadTreeTheNewWay(ecoTree);

                    // Select the first node to restore colors and selection state
                    if (this.tree?.nDatabaseNodes?.length > 0) {
                        this.selectSiblingNodes(this.tree.nDatabaseNodes[0]);
                    }

                    // Update options arrays
                    this.updateOrganizationOptions();

                    // CRITICAL FIX: Emit organization updates to the service so dialogs (like hierarchy dialog) get refreshed
                    // This was missing, causing the hierarchy dialog to show stale data after reorder operations
                    this.obService.organization = this._organization;
                    this.obService.organization$.next(this._organization);
                }
            }
        }
    }

    //#endregion
    //#region Parsing and Validation
    // Transformation/mapping methods
    // Methods returning true/false: isCurrent, areFeedsEqual, shouldProcess, etc.

    /**
     * TrackBy function for ngFor over tree nodes.
     * Includes userDetailsArray length to force re-render when users are added/removed.
     */
    public trackByNodeId(index: number, node: ECONode<Position>): string {
        const userCount = node.data?.userDetailsArray?.length || 0;
        return `${node.id}-${userCount}`;
    }

    public trackByKey(index: number, item: { key: string }): string {
        return item.key;
    }

    /**
     * Find a tree node by matching its userDetailsArray reference with the drop list data.
     * Used to identify which node a user was dropped onto.
     */
    private findNodeByDropListData(dropListData: any[]): ECONode<Position> | undefined {
        if (!dropListData || !this.tree?.nDatabaseNodes) {
            return undefined;
        }

        // First try to find by reference equality (fastest)
        for (const node of this.tree.nDatabaseNodes) {
            if (this.userDropDataMap[node.id] === dropListData) {
                return node;
            }
        }

        // If not found by reference, the dropListData might be a new empty array
        // In this case, we can't reliably determine the target node
        return undefined;
    }

    public determineLayout(users?: any[]) {
        if (!users) {
            return 'NO_USERS';
        }
        if (users.length === 0) {
            return 'NO_USERS';
        } else if (users.length === 1) {
            return 'ONE_USER';
        } else {
            return 'MULTIPLE_USERS';
        }
    }

    /**
     * Returns the correct sidebar template ref based on the node's user layout.
     * Maps: ONE_USER → sideBarOneUserTemplate, MULTIPLE_USERS → sideBarManyUserTemplate,
     * NO_USERS → sideBarEmptyUserTemplate.
     */
    public getSidebarTemplateForNode(node: ECONode<Position>): any {
        const layout = this.determineLayout(node.data?.userDetailsArray);
        switch (layout) {
            case 'ONE_USER':
                return this.sideBarOneUserTemplate;
            case 'MULTIPLE_USERS':
                return this.sideBarManyUserTemplate;
            default:
                return this.sideBarEmptyUserTemplate;
        }
    }

    public areAllSecurityTasksComplete(): boolean {
        return this.securityTasks.every((task) => task.status === 'completed');
    }

    public countOfIncompleteTasks(): number {
        return this.securityTasks.filter((task) => task.status !== 'completed').length;
    }

    public toTaskDescription(task: SecurityTask) {
        return toTaskDescription(task, (uid) => (this.organization as any)?.orgUsers?.[uid]?.name);
    }

    /**
     * Builds the icon + name + action + status presentation for a security task,
     * matching the "Pending Changes" layout used by the org-part edit/bulk dialog.
     */
    public toTaskPresentation(task: SecurityTask): TaskPresentation {
        return toTaskPresentation(task, {
            orgParts: this._organization?.orgParts,
            orgHats: this._organization?.orgHats as Record<string, OrgHat> | undefined,
        });
    }

    //#endregion
    //#region Response Handlers
    // API/NGRX callback handlers.

    //#endregion
    //#region Navigation
    // Confirmation close dialogs, router guards, etc.

    //#endregion
    //#region Post Instantiation
    // Post-init fetch requests, used in such as landing page, after view init, etc.

    //#endregion

    public ngOnDestroy(): void {
        // Leaving the route while presenting must not leave the browser in full screen.
        if (this.isPresenting && typeof document !== 'undefined' && document.fullscreenElement)
            void document.exitFullscreen().catch(() => undefined);
        this.boardPlaceScrollEl?.removeEventListener('scroll', this.onBoardPlaceScroll);
        if (this.boardPlaceFrame) cancelAnimationFrame(this.boardPlaceFrame);
        // Leaving the route mid-edit must not strand the armed entry.
        this.releaseEditBack?.();
        this.releaseEditBack = null;
        this._stopDragHighlightTracking();
        this.destroy$.next();
        this.destroy$.complete();
        this.deleteTaskMonitorSub?.unsubscribe();
        if (this.deleteFinishTimeout) clearTimeout(this.deleteFinishTimeout);
        if (this.deleteAutoCloseTimeout) clearTimeout(this.deleteAutoCloseTimeout);
        this.userOpTaskMonitorSub?.unsubscribe();
        if (this.userOpFinishTimeout) clearTimeout(this.userOpFinishTimeout);
        if (this.userOpAutoCloseTimeout) clearTimeout(this.userOpAutoCloseTimeout);

        const touchEl = this.dragContainerRef?.nativeElement;
        if (touchEl) {
            touchEl.removeEventListener('touchstart', this.boardTouchStart);
            touchEl.removeEventListener('touchmove', this.boardTouchMove);
            touchEl.removeEventListener('touchend', this.boardTouchEnd);
            touchEl.removeEventListener('touchcancel', this.boardTouchEnd);
        }
    }

    public onPositionsSwitchChange(isChecked: boolean) {
        this.treeBuilder.isPositionsOn = isChecked;
        this.isPositionsSwitchOn = isChecked;
        // TODO: This may not be the best way to do this, as it rebuilds the entire tree.
        // Consider a more targeted update if performance becomes an issue.
        if (this.organization) {
            const orgTree = this.treeBuilder.buildOrg(this.organization, this.orbBoardId);
            const ecoTree = new ECOTree();
            // This is called from onPositionsSwitchChange, so isPositionsSwitchOn is already updated
            this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, { isPositionsOn: this.isPositionsSwitchOn });
            this.loadTreeTheNewWay(ecoTree);
            // Select the first node in the tree.
            if (this.tree?.nDatabaseNodes?.length > 0) {
                this.selectSiblingNodes(this.tree.nDatabaseNodes[0]);
            }

            if (this._organization.key !== this.previousOrgKey) this.scrollToFirstNode();
        }
    }

    // Update organization-related options and data
    private updateOrganizationOptions(): void {
        if (!this._organization) return;

        // Update user options
        const userKeys = this._organization.orgUsers?.userKeys || [];
        this.userOptions = userKeys
            .map((key) => {
                const user = this._organization.orgUsers[key];
                return user ? { display: user.name, value: user.key } : null;
            })
            .filter((opt): opt is { display: string; value: string } => opt !== null);

        // Update policy options
        const policyKeys = this._organization.orgPolicies?.policiesKeys || [];
        this.policyOptions = policyKeys
            .map((key) => {
                const policy = this._organization.orgPolicies[key];
                return policy ? { display: policy.name, value: policy.key } : null;
            })
            .filter((opt): opt is { display: string; value: string } => opt !== null);

        // Update security group options
        const securityGroupKeys = this._organization.orgSecurityGroups?.securityGroupsKeys || [];
        this.securityGroupOptions = securityGroupKeys
            .map((key) => {
                const securityGroup = this._organization.orgSecurityGroups[key];
                return securityGroup ? { display: securityGroup.name, value: securityGroup.key } : null;
            })
            .filter((opt): opt is { display: string; value: string } => opt !== null);

        // Update hat options
        const hatKeys = this._organization.orgHats?.hatsKeys || [];
        this.hatOptions = hatKeys
            .map((key) => {
                const hat = this._organization.orgHats[key];
                return hat ? { display: hat.name, value: hat.key } : null;
            })
            .filter((opt): opt is { display: string; value: string } => opt !== null);
    }

    /**
     * Ensures that orgParts in the organization are sorted by ID for consistent processing order.
     * Note: Visual sibling ordering is determined by the path-based index in ECOTree's _firstWalk.
     * @param organization The organization object to sort
     */
    private ensureOrgPartsSorted(organization: Organization | null | undefined): void {
        if (organization?.orgParts) {
            // Normalize: ensure every part has a key field (bulk-builder-created parts
            // may be missing it; the template uses post.key for data-part-key attributes)
            for (const part of organization.orgParts) {
                if (!part.key && part.partKey) {
                    part.key = part.partKey;
                }
            }
            organization.orgParts.sort((a, b) => (a.id || 0) - (b.id || 0));
            // console.log('📋 Sorted orgParts by ID for consistent processing order');
        }
    }
    //#endregion
    //#region Section editing methods
    // Methods for handling section-related events and actions.

    public onEditSection(section: any): void {
        // console.log('onEditSection called with:', section);

        // Find the section/position in the organization parts
        const partKey = section.partKey || section.key;
        // console.log('Looking for partKey:', partKey);
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);
        // console.log('Found position:', position);

        if (!position) {
            // console.log('Position not found, showing snackbar');
            this.snackbar.open('Section not found', 'OK', { duration: 3000 });
            return;
        }

        // console.log('Opening dialog...');
        // Open edit position dialog
        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                orgKey: this.organization?.key,
                organization: this.organization,
                isDeveloper: this.isDev,
            },
        });
        // console.log('Dialog opened, subscribing to afterClosed...');

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.snackbar.open('Section updated successfully', 'OK', { duration: 3000 });
            }
        });
    }

    public onAddSiblingSection(section: any): void {
        // console.log('Add sibling section:', section);
        // console.log('Organization available:', !!this.organization);
        // console.log('Organization orgParts:', this.organization?.orgParts?.length);

        // Find the parent part to get proper path and parentRef
        const parentPart = this.organization?.orgParts?.find((part) => part.partKey === section.parentPartKey);
        // console.log('Parent part found:', parentPart);

        // Create mock node structure similar to hierarchy dialog
        // Use the section's current path - the dialog will handle updating paths correctly
        const mockNode = {
            node: {
                data: {
                    partKey: section.partKey || section.key,
                    parentPartKey: section.parentPartKey,
                    partType: section.partType || 'section',
                    path: section.path, // Use the full current path
                    parentRef: parentPart?.path || parentPart?.parentRef,
                    depth: section.depth,
                },
            },
        };

        // Open create new position dialog for sibling
        try {
            // console.log('Attempting to open dialog...');
            const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
                width: '65%',
                height: '80%',
                minHeight: '300px',
                data: {
                    organization: this.organization,
                    type: 'sibling',
                    node: mockNode,
                    siblings: this.findSectionSiblings(section),
                    children: [],
                    partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                },
            });
            // console.log('Dialog opened successfully');

            dialogRef.afterClosed().subscribe((result) => {
                console.log('[OrgBoard] sibling section dialog closed with:', result);
                if (result && (result.newPart || result.bulkTree)) {
                    this.handleNewPartCreation(result);
                }
            });
        } catch (error) {
            console.error('Error opening dialog:', error);
        }
    }

    public onAddChildUnit(section: any): void {
        // console.log('Add child unit:', section);

        // Create mock node structure similar to hierarchy dialog
        const mockNode = {
            node: {
                data: {
                    partKey: section.partKey || section.key,
                    partType: section.partType || 'section',
                    path: section.path,
                    parentRef: section.parentRef,
                    depth: section.depth,
                },
            },
        };

        // Open create new position dialog for child
        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: this.findSectionChildren(section),
                defaultChildType: 'unit',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] child unit dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public async onDeleteSection(section: any): Promise<void> {
        // console.log('Delete section:', section);

        const partKey = section.partKey || section.key;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Section not found', 'OK', { duration: 3000 });
            return;
        }

        // Find all descendants
        const descendants = this.findAllDescendants(position.partKey);
        const totalToDelete = descendants.length + 1; // +1 for the position itself

        let confirmMessage = `Are you sure you want to delete "${position.name}"?`;
        if (descendants.length > 0) {
            confirmMessage += ` This will also delete ${descendants.length} descendant org part(s). Total org parts to be deleted: ${totalToDelete}`;
        }

        const confirmed = await this.confirmDialogService.asyncConfirm('Delete Section', confirmMessage);

        if (confirmed) {
            await this.deletePartWithPanel(position.partKey, position.name, 'Section');
        }
    }

    // Helper methods for section operations
    private findSectionSiblings(section: any): any[] {
        if (!this.organization?.orgParts) return [];

        const parentPartKey = section.parentPartKey;
        if (!parentPartKey) return [];

        // Get all sections with the same parent (including the current section)
        const siblings = this.organization.orgParts
            .filter((part) => part.parentPartKey === parentPartKey)
            .sort((a, b) => (a.id || 0) - (b.id || 0)); // Sort by ID to maintain order

        // Calculate the base path for siblings
        let basePath = '';
        if (section.path && section.path.endsWith('.data')) {
            const pathParts = section.path.split('.');
            pathParts.pop(); // Remove 'data'
            pathParts.pop(); // Remove the current position number
            basePath = pathParts.join('.');
        }

        // Map siblings to include correct paths with their current positions
        return siblings.map((part, index) => ({
            data: {
                ...part,
                path: basePath ? `${basePath}.${index}.data` : part.path,
            },
            position: index,
        }));
    }

    private findSectionChildren(section: any): any[] {
        if (!this.organization?.orgParts) return [];

        const sectionPartKey = section.partKey || section.key;

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === sectionPartKey)
            .map((part) => ({ data: part }));
    }

    private findAllDescendants(partKey: string): OrgPart[] {
        if (!this.organization?.orgParts) return [];

        const descendants: OrgPart[] = [];
        const directChildren = this.organization.orgParts.filter((p) => p.parentPartKey === partKey);

        for (const child of directChildren) {
            descendants.push(child);
            // Recursively find descendants of each child
            descendants.push(...this.findAllDescendants(child.partKey));
        }

        return descendants;
    }

    private async handleNewPartCreation(result: any): Promise<void> {
        console.log('[OrgBoard] handleNewPartCreation called with:', {
            hasBulkTree: !!result.bulkTree,
            hasNewPart: !!result.newPart,
            result,
        });
        // ── Handle Bulk Builder result ──
        if (result.bulkTree || result.graftPoints?.length || result.siblingOrder?.length) {
            try {
                const anchorPartKey = result.anchorPartKey;
                const insertionType = result.insertionType || 'child';
                const siblingOrder: string[] = result.siblingOrder || [];
                const graftPoints = result.graftPoints || [];

                // Count total new positions across root tree + all graft points
                let totalPositions = (result.bulkTree || []).length;
                for (const graft of graftPoints) {
                    totalPositions += graft.children.length;
                }

                // Send ONE consolidated bulkAddParts command with all graft points embedded.
                // This prevents the duplicate orgParts entries that occurred when sending
                // multiple sequential commands (one per graft point).
                console.log('[OrgBoard] Sending consolidated bulkAddParts:', {
                    orgKey: this.organization?.key,
                    anchorPartKey,
                    insertionType,
                    rootTree: (result.bulkTree || []).length,
                    graftPoints: graftPoints.length,
                    siblingOrder: siblingOrder.length,
                    totalPositions,
                });

                await this.obService.bulkAddParts(
                    this.organization!.key,
                    anchorPartKey,
                    insertionType,
                    result.bulkTree || [],
                    siblingOrder,
                    graftPoints
                );

                const msg =
                    totalPositions > 0
                        ? `Bulk creation submitted (${totalPositions} org parts). Processing...`
                        : 'Reorder submitted. Processing...';
                this.snackbar.open(msg, 'OK', { duration: 4000 });
            } catch (error) {
                console.error('Bulk add failed:', error);
                this.snackbar.open('Bulk creation failed. See console for details.', 'OK', { duration: 5000 });
            }
            return;
        }

        // ── Handle Single Position result ──

        try {
            // For siblings, we need to reorder IDs to maintain proper visual order
            if (result.orderOfAppearance && result.orderOfAppearance.length > 1) {
                // console.log('🔢 Reordering sibling IDs for proper visual order');

                // Get the parent part key to find all siblings
                const parentPartKey = result.newPart.parentPartKey;
                const allSiblings =
                    this.organization?.orgParts?.filter((part) => part.parentPartKey === parentPartKey) || [];

                // console.log('📊 Current siblings:', allSiblings.map(s => ({ id: s.id, name: s.name })));
                // console.log('📋 New order from dialog:', result.orderOfAppearance.map((p: any) => ({
                //   id: p.data.id,
                //   name: p.data.name,
                //   partKey: p.data.partKey
                // })));

                // Find the highest ID in the entire organization to avoid conflicts
                const maxIdInOrg = Math.max(...(this.organization?.orgParts?.map((part) => part.id) || [0]));

                // Get the current sibling IDs in their current order
                const siblingIds = allSiblings.map((s) => s.id).sort((a, b) => a - b);

                // Use a temporary high ID range to avoid conflicts during reassignment
                const tempBaseId = maxIdInOrg + 1000;

                // First pass: assign temporary IDs to avoid conflicts
                result.orderOfAppearance.forEach((part: any, index: number) => {
                    if (part.data.partKey !== 'phantom') {
                        // console.log(`🔄 Temporarily assigning ID ${tempBaseId + index} to ${part.data.name}`);
                        part.data.id = tempBaseId + index;
                    }
                });

                // Second pass: assign final consecutive IDs using the original base
                const baseId = siblingIds.length > 0 ? siblingIds[0] : maxIdInOrg + 1;
                // console.log(`🎯 Using base ID ${baseId} for final assignment`);

                result.orderOfAppearance.forEach((part: any, index: number) => {
                    if (part.data.partKey === 'phantom') {
                        // Set ID for the new part
                        result.newPart.id = baseId + index;
                        // console.log(`✨ New part "${result.newPart.name}" assigned ID ${result.newPart.id}`);
                    } else {
                        // Update existing sibling's ID to final value
                        part.data.id = baseId + index;
                        // console.log(`🔢 Updated "${part.data.name}" to ID ${part.data.id}`);
                    }
                });
            } else {
                // For non-siblings or single items, use the next highest ID
                result.newPart.id =
                    (Math.max(...(this.organization?.orgParts?.map((part) => part.id) || [0])) || 0) + 1;
                // console.log(`🆕 Single part "${result.newPart.name}" assigned ID ${result.newPart.id}`);
            }

            // Save the new part
            await this.obService.saveNewPart(result.newPart);

            // Check if other parts need to be updated (including ID changes)
            if (result.orderOfAppearance) {
                const partsToUpdate = result.orderOfAppearance
                    .filter((part: any) => part.data.partKey !== result.newPart.partKey)
                    .map((part: any) => part.data);

                if (partsToUpdate.length > 0) {
                    // console.log(`💾 Updating ${partsToUpdate.length} existing parts with new IDs`);
                    await this.obService.updateParts(partsToUpdate);

                    // Re-sort the organization after ID updates to maintain consistency
                    this.ensureOrgPartsSorted(this.organization);
                }
            }
        } catch (error) {
            console.error('Error creating new part:', error);
            this.snackbar.open('Error creating new section', 'OK', { duration: 3000 });
        }
    }

    // ═══════════════════════════════════════════════════════════════
    // Delete with Processing Panel
    // ═══════════════════════════════════════════════════════════════

    /**
     * Unified delete handler that shows a side panel with task monitoring.
     * Replaces the old fire-and-forget delete pattern for all part types.
     */
    public async deletePartWithPanel(partKey: string, partName: string, partType: string): Promise<void> {
        if (!this.organization) return;

        // Collect descendant keys for greying out
        const descendants = this.findAllDescendants(partKey);
        const descendantKeys = descendants.map((d) => d.partKey);

        // Open the panel
        this.deletePanel = {
            visible: true,
            phase: 'deleting',
            partName,
            partType,
            partKey,
            descendantKeys,
            taskStatuses: [],
            tasksCompleted: 0,
            totalTasks: 0,
            startTime: Date.now(),
        };

        // Build the Set for child node components to check per-part grey-out
        this.deletingPartKeysSet = new Set([partKey, ...descendantKeys]);

        // Force change detection to update the DOM with grey-out classes and disabled attributes.
        // Then wait for the browser to actually PAINT those changes — requestAnimationFrame fires
        // right before paint, and the nested setTimeout fires right after, guaranteeing the frame
        // has been composited and rendered to screen.
        this.cdr.detectChanges();
        await new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 50)));

        try {
            await this.obService.deleteParts([partKey], this.organization);
            this.deletePanel.phase = 'processing';
            this.monitorDeleteTasks();
        } catch (error) {
            console.error('Error deleting part:', error);
            this.deletePanel.phase = 'error';
            this.deletingPartKeysSet = new Set();
        }
    }

    /** Monitor backend security tasks for the delete operation */
    private monitorDeleteTasks(): void {
        const orgParts = this.organization?.orgParts || [];
        this.deleteTaskMonitorSub?.unsubscribe();

        const timeoutHandle = setTimeout(() => {
            console.warn('[DeletePanel] Task monitor timed out after 60s');
            this.finishDelete();
        }, 60000);

        this.deleteTaskMonitorSub = this.obService.monitorSecurityTasks().subscribe((tasks) => {
            // Filter to tasks created after delete started (with 2s grace)
            const recentTasks = tasks.filter((t) => {
                const created = t.createdTimeStamp?.toMillis?.() || (t.createdTimeStamp as any)?.seconds * 1000 || 0;
                return created > this.deletePanel.startTime - 2000;
            });

            let completed = 0;
            let total = 0;
            const statuses: typeof this.deletePanel.taskStatuses = [];

            for (const task of recentTasks) {
                const isTerminal =
                    task.status === 'completed' ||
                    task.status === 'fulfilled' ||
                    task.status === 'error' ||
                    task.status === 'Error' ||
                    task.status === 'failed';
                const isSuccess = task.status === 'completed' || task.status === 'fulfilled';

                let statusIcon = 'hourglass_empty';
                if (task.status === 'processing') statusIcon = 'sync';
                else if (isSuccess) statusIcon = 'check_circle';
                else if (isTerminal) statusIcon = 'error';

                let icon = 'settings';
                let actionLabel: string = task.typeOfTask || 'processing';
                if (task.typeOfTask === 'updatePart') {
                    icon = 'edit';
                    actionLabel = 'update';
                } else if (task.typeOfTask === 'addPart') {
                    icon = 'add_circle';
                    actionLabel = 'add';
                } else if (task.typeOfTask === 'removePart') {
                    icon = 'remove_circle';
                    actionLabel = 'delete';
                } else if (task.typeOfTask === 'reorderChildren') {
                    icon = 'swap_vert';
                    actionLabel = 'reorder';
                } else if (task.typeOfTask === 'bulkDeleteParts') {
                    icon = 'remove_circle';
                    actionLabel = 'bulk delete';
                } else if (task.typeOfTask === 'addHat') {
                    icon = 'school';
                    actionLabel = 'add hat';
                } else if (task.typeOfTask === 'updateHat') {
                    icon = 'school';
                    actionLabel = 'update hat';
                } else if (task.typeOfTask === 'removeHat') {
                    icon = 'school';
                    actionLabel = 'remove hat';
                }

                let name = 'Unknown';
                if (task.partKey) {
                    const part = orgParts.find((p) => p.partKey === task.partKey);
                    name = part?.name || task.snapshotNew?.name || task.diffNew?.name || task.partKey;
                }
                if (name === 'Unknown' && task.hatId) {
                    const orgHats = (this.organization as any)?.orgHats;
                    if (orgHats) {
                        const hat = orgHats[task.hatId];
                        name = hat?.name || hat?.positionName || task.snapshotNew?.name || task.hatId;
                    } else {
                        name = task.snapshotNew?.name || task.hatId;
                    }
                }

                const isHatTask =
                    task.typeOfTask === 'addHat' || task.typeOfTask === 'updateHat' || task.typeOfTask === 'removeHat';
                const hatPartKey = task.snapshotNew?.positionArray?.[0] || task.snapshotOld?.positionArray?.[0];
                const dedupId = isHatTask
                    ? task.partKey || hatPartKey || task.hatId || task.key
                    : task.hatId || task.partKey || task.key;
                const dedupKey = `${task.typeOfTask}:${dedupId}`;
                if (statuses.some((s) => s.dedupKey === dedupKey)) continue;

                statuses.push({
                    key: task.key,
                    name,
                    action: actionLabel,
                    icon,
                    status:
                        task.status === 'processing'
                            ? 'processing'
                            : isSuccess
                              ? 'completed'
                              : isTerminal
                                ? 'error'
                                : 'queued',
                    statusIcon,
                    dedupKey,
                });

                total++;
                if (isTerminal) completed++;
            }

            this.deletePanel.taskStatuses = statuses;
            this.deletePanel.totalTasks = total;
            this.deletePanel.tasksCompleted = completed;

            if (total > 0 && completed === total) {
                if (!this.deleteFinishTimeout) {
                    clearTimeout(timeoutHandle);
                    this.deleteFinishTimeout = setTimeout(() => this.finishDelete(), 1500);
                }
            } else if (this.deleteFinishTimeout) {
                clearTimeout(this.deleteFinishTimeout);
                this.deleteFinishTimeout = null;
            }
        });
    }

    private finishDelete(): void {
        this.deleteTaskMonitorSub?.unsubscribe();
        this.deleteFinishTimeout = null;
        this.deletePanel.phase = 'done';

        // Clear grey-out immediately — parts are deleted, board can be interactive
        this.deletingPartKeysSet = new Set();

        // Auto-close the panel after 5 seconds
        this.deleteAutoCloseTimeout = setTimeout(() => this.closeDeletePanel(), 5000);
    }

    public closeDeletePanel(): void {
        this.deletePanel.visible = false;
        this.deletePanel.phase = 'idle';
        this.deletePanel.partKey = '';
        this.deletePanel.descendantKeys = [];
        this.deletePanel.taskStatuses = [];
        // NOTE: Do NOT clear deletingPartKeysSet here. The panel is informational
        // only — grey-out is managed by finishDelete() when tasks actually complete.
        // Monitoring continues in the background if panel is closed early.
        if (this.deleteAutoCloseTimeout) {
            clearTimeout(this.deleteAutoCloseTimeout);
            this.deleteAutoCloseTimeout = null;
        }
    }

    /** Check if a part should be greyed out because it's being deleted */
    public isPartBeingDeleted(partKey: string): boolean {
        return this.deletingPartKeysSet.has(partKey);
    }

    /** Set of partKeys being deleted — passed to child node components for per-part grey-out */
    public deletingPartKeysSet: Set<string> = new Set();

    // ═══════════════════════════════════════════════════════════════
    // User Operation Processing Panel Methods
    // ═══════════════════════════════════════════════════════════════

    /**
     * Staff-pool drop or "Assign Staff to Post" on a LINKED post (it represents another post — e.g.
     * Department 19's "Office of the CFO"): refuse with a message naming where to assign instead.
     * Returns true when refused (the caller writes nothing).
     */
    private refuseLinkedPostAssignment(partKey: string | undefined): boolean {
        const reason = linkedDropRefusal(this.organization?.orgParts as any[], partKey);
        if (!reason) return false;
        this.ngZone.run(() => this.snackbar.open(reason, 'OK', { duration: 5000 }));
        return true;
    }

    /** Open the user operation processing panel */
    private openUserOpPanel(
        userId: string,
        userName: string,
        partKey: string,
        action: 'remove' | 'approve' | 'reject' | 'assign'
    ): void {
        this.userOpPanel = {
            visible: true,
            phase: 'processing',
            userName,
            userId,
            partKey,
            action,
            taskStatuses: [],
            tasksCompleted: 0,
            totalTasks: 0,
            startTime: Date.now(),
        };
        this.processingUserKeysSet = new Set([`${userId}:${partKey}`]);
        this.cdr.detectChanges();
    }

    /** Monitor backend security tasks for the user operation */
    private monitorUserOpTasks(): void {
        this.userOpTaskMonitorSub?.unsubscribe();

        const timeoutHandle = setTimeout(() => {
            console.warn('[UserOpPanel] Task monitor timed out after 60s');
            this.finishUserOp();
        }, 60000);

        this.userOpTaskMonitorSub = this.obService.monitorSecurityTasks().subscribe((tasks) => {
            const recentTasks = tasks.filter((t) => {
                const created = t.createdTimeStamp?.toMillis?.() || (t.createdTimeStamp as any)?.seconds * 1000 || 0;
                return created > this.userOpPanel.startTime - 2000;
            });

            let completed = 0;
            let total = 0;
            const orgParts = this.organization?.orgParts || [];
            const statuses: typeof this.userOpPanel.taskStatuses = [];

            for (const task of recentTasks) {
                const isTerminal =
                    task.status === 'completed' ||
                    task.status === 'fulfilled' ||
                    task.status === 'error' ||
                    task.status === 'Error' ||
                    task.status === 'failed';
                const isSuccess = task.status === 'completed' || task.status === 'fulfilled';

                let statusIcon = 'hourglass_empty';
                if (task.status === 'processing') statusIcon = 'sync';
                else if (isSuccess) statusIcon = 'check_circle';
                else if (isTerminal) statusIcon = 'error';

                let icon = 'settings';
                let actionLabel: string = task.typeOfTask || 'processing';
                if (task.typeOfTask === 'updateUser') {
                    icon = 'person';
                    actionLabel = 'update user';
                } else if (task.typeOfTask === 'updateHat') {
                    icon = 'school';
                    actionLabel = 'update hat';
                } else if (task.typeOfTask === 'removeHat') {
                    icon = 'school';
                    actionLabel = 'remove hat';
                } else if (task.typeOfTask === 'addHat') {
                    icon = 'school';
                    actionLabel = 'add hat';
                } else if (task.typeOfTask === 'updatePart') {
                    icon = 'edit';
                    actionLabel = 'update';
                }

                let name = 'Unknown';
                if (task.partKey) {
                    const part = orgParts.find((p) => p.partKey === task.partKey);
                    name = part?.name || task.snapshotNew?.name || task.diffNew?.name || task.partKey;
                }
                if (name === 'Unknown' && task.hatId) {
                    const orgHats = (this.organization as any)?.orgHats;
                    if (orgHats) {
                        const hat = orgHats[task.hatId];
                        name = hat?.name || hat?.positionName || task.snapshotNew?.name || task.hatId;
                    } else {
                        name = task.snapshotNew?.name || task.hatId;
                    }
                }

                const isHatTask =
                    task.typeOfTask === 'addHat' || task.typeOfTask === 'updateHat' || task.typeOfTask === 'removeHat';
                const hatPartKey = task.snapshotNew?.positionArray?.[0] || task.snapshotOld?.positionArray?.[0];
                const dedupId = isHatTask
                    ? task.partKey || hatPartKey || task.hatId || task.key
                    : task.hatId || task.partKey || task.key;
                const dedupKey = `${task.typeOfTask}:${dedupId}`;
                if (statuses.some((s) => s.dedupKey === dedupKey)) continue;

                statuses.push({
                    key: task.key,
                    name,
                    action: actionLabel,
                    icon,
                    status:
                        task.status === 'processing'
                            ? 'processing'
                            : isSuccess
                              ? 'completed'
                              : isTerminal
                                ? 'error'
                                : 'queued',
                    statusIcon,
                    dedupKey,
                });

                total++;
                if (isTerminal) completed++;
            }

            this.userOpPanel.taskStatuses = statuses;
            this.userOpPanel.totalTasks = total;
            this.userOpPanel.tasksCompleted = completed;

            if (total > 0 && completed === total) {
                if (!this.userOpFinishTimeout) {
                    clearTimeout(timeoutHandle);
                    this.userOpFinishTimeout = setTimeout(() => this.finishUserOp(), 1500);
                }
            } else if (this.userOpFinishTimeout) {
                clearTimeout(this.userOpFinishTimeout);
                this.userOpFinishTimeout = null;
            }
        });
    }

    private finishUserOp(): void {
        this.userOpTaskMonitorSub?.unsubscribe();
        this.userOpFinishTimeout = null;
        this.userOpPanel.phase = 'done';

        // Clear grey-out and disabled state immediately — board is interactive again
        // while the panel stays visible for the user to see the results.
        this.processingUserKeysSet = new Set();
        this.isHatAssignmentInProgress = false;
        this.pendingHatAssignments.clear();

        // Rebuild tree with latest org data so removed/approved users are reflected immediately
        const orgData = this.deferredOrgUpdate || this._organization;
        if (orgData?.key !== undefined) {
            this._organization = orgData;
            this.deferredOrgUpdate = null;
            this.ensureOrgPartsSorted(this._organization);
            this.dialogService.organization = this._organization;
            this.obService.organization = this._organization;
            this.obService.organization$.next(this._organization);
            const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);
            const ecoTree = new ECOTree();
            this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, { isPositionsOn: this.isPositionsSwitchOn });
            this.loadTreeTheNewWay(ecoTree);
            this.cdr.detectChanges();
        }

        this.userOpAutoCloseTimeout = setTimeout(() => this.closeUserOpPanel(), 5000);
    }

    public closeUserOpPanel(): void {
        this.userOpPanel.visible = false;
        this.userOpPanel.phase = 'idle';
        this.userOpPanel.userId = '';
        this.userOpPanel.partKey = '';
        this.userOpPanel.taskStatuses = [];
        // NOTE: Do NOT clear processingUserKeysSet, isHatAssignmentInProgress,
        // or pendingHatAssignments here. The panel is informational only —
        // grey-out and processing state are managed by finishUserOp() when
        // tasks actually complete. Monitoring continues in the background.
        if (this.userOpAutoCloseTimeout) {
            clearTimeout(this.userOpAutoCloseTimeout);
            this.userOpAutoCloseTimeout = null;
        }
    }

    private async performDeleteSection(partKey: string, _descendants: OrgPart[]): Promise<void> {
        try {
            // ENHANCED: Use the improved cascading deletion - just delete the parent,
            // and let the service handle finding and deleting all descendants atomically
            // console.log(`🗑️  Deleting section ${partKey} with cascading deletion...`);

            if (!this.organization) {
                throw new Error('No organization data available for deletion');
            }

            await this.obService.deleteParts([partKey], this.organization);

            this.snackbar.open('Section deleted successfully', 'OK', { duration: 3000 });

            // Refresh the organization board to reflect the changes
            await this.refreshOrganizationBoard();
        } catch (error) {
            console.error('Error deleting section:', error);
            this.snackbar.open('Error deleting section', 'OK', { duration: 3000 });
        }
    }

    /**
     * Refreshes the organization board by rebuilding the tree with current organization data
     */
    private async refreshOrganizationBoard(): Promise<void> {
        if (!this._organization) {
            console.warn('refreshOrganizationBoard: No organization data available');
            return;
        }

        try {
            // console.log('🔄 Refreshing organization board after deletion...');

            // Ensure orgParts are sorted by ID for consistent visual ordering
            this.ensureOrgPartsSorted(this._organization);

            // Rebuild the tree with the current organization data
            const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);
            // console.log('🌳 New orgTree built after deletion:', orgTree);

            const ecoTree = new ECOTree();
            // IMPORTANT: Pass isPositionsOn from component state to prevent dialogs from affecting the toggle
            this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, { isPositionsOn: this.isPositionsSwitchOn });
            // console.log('🏗️ Loading refreshed tree...');
            this.loadTreeTheNewWay(ecoTree);

            // Select the first node to restore colors and selection state
            if (this.tree?.nDatabaseNodes?.length > 0) {
                // console.log('🎯 Selecting first node to restore colors...');
                this.selectSiblingNodes(this.tree.nDatabaseNodes[0]);
            }

            // Update options arrays
            this.updateOrganizationOptions();

            // console.log('✅ Organization board refresh complete!');
        } catch (error) {
            console.error('Error refreshing organization board:', error);
        }
    }

    // Unit action handlers
    public onEditUnit(unit: unknown): void {
        // console.log('Edit unit:', unit);

        const unitData = unit as any;

        // Find the unit/position in the organization parts
        const partKey = unitData.partKey || unitData.key;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Unit not found', 'OK', { duration: 3000 });
            return;
        }

        // Open edit position dialog
        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                orgKey: this.organization?.key,
                organization: this.organization,
                isDeveloper: this.isDev,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                // Refresh the organization board after successful edit
                this.snackbar.open('Unit updated successfully', 'OK', { duration: 3000 });
            }
        });
    }

    public onAddSiblingUnit(unit: unknown): void {
        // console.log('Add sibling unit:', unit);
        // console.log('Organization available:', !!this.organization);
        // console.log('Organization orgParts:', this.organization?.orgParts?.length);

        const unitData = unit as any;

        // Find the parent part to get proper path and parentRef
        const parentPart = this.organization?.orgParts?.find((part) => part.partKey === unitData.parentPartKey);

        // Create mock node structure similar to hierarchy dialog
        const mockNode = {
            node: {
                data: {
                    partKey: unitData.partKey || unitData.key,
                    parentPartKey: unitData.parentPartKey,
                    partType: unitData.partType || 'unit',
                    path: unitData.path,
                    parentRef: parentPart?.path || parentPart?.parentRef,
                    depth: unitData.depth,
                },
            },
        };

        // Open create new position dialog for sibling
        try {
            // console.log('Attempting to open unit sibling dialog...');
            const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
                width: '65%',
                height: '80%',
                minHeight: '300px',
                data: {
                    organization: this.organization,
                    type: 'sibling',
                    node: mockNode,
                    siblings: this.findUnitSiblings(unitData),
                    children: [],
                    partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                },
            });
            // console.log('Unit sibling dialog opened successfully');

            dialogRef.afterClosed().subscribe((result) => {
                console.log('[OrgBoard] sibling unit dialog closed with:', result);
                if (result && (result.newPart || result.bulkTree)) {
                    this.handleNewPartCreation(result);
                }
            });
        } catch (error) {
            console.error('Error opening unit sibling dialog:', error);
        }
    }

    public onAddChildPost(unit: unknown): void {
        // console.log('Add child post:', unit);

        const unitData = unit as any;

        // Create mock node structure similar to hierarchy dialog
        const mockNode = {
            node: {
                data: {
                    partKey: unitData.partKey || unitData.key,
                    partType: unitData.partType || 'unit',
                    path: unitData.path,
                    parentRef: unitData.parentRef,
                    depth: unitData.depth,
                },
            },
        };

        // Open create new position dialog for child post
        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: this.findUnitChildren(unitData),
                defaultChildType: 'post',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] child post dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public async onDeleteUnit(unit: unknown): Promise<void> {
        // console.log('Delete unit:', unit);

        const unitData = unit as any;
        const partKey = unitData.partKey || unitData.key;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Unit not found', 'OK', { duration: 3000 });
            return;
        }

        // Find all descendants
        const descendants = this.findAllDescendants(position.partKey);
        const totalToDelete = descendants.length + 1; // +1 for the position itself

        let confirmMessage = `Are you sure you want to delete "${position.name}"?`;
        if (descendants.length > 0) {
            confirmMessage += ` This will also delete ${descendants.length} descendant org part(s). Total org parts to be deleted: ${totalToDelete}`;
        }

        const confirmed = await this.confirmDialogService.asyncConfirm('Delete Unit', confirmMessage);

        if (confirmed) {
            await this.deletePartWithPanel(position.partKey, position.name, 'Unit');
        }
    }

    // Helper methods for unit operations
    private findUnitSiblings(unit: unknown): unknown[] {
        const unitData = unit as any;

        if (!this.organization?.orgParts) return [];

        const parentPartKey = unitData.parentPartKey;
        if (!parentPartKey) return [];

        // Get all units with the same parent (including the current unit)
        const siblings = this.organization.orgParts
            .filter((part) => part.parentPartKey === parentPartKey && part.partType === 'unit')
            .map((part) => ({ data: part }));

        return siblings;
    }

    private findUnitChildren(unit: unknown): unknown[] {
        const unitData = unit as any;

        if (!this.organization?.orgParts) return [];

        const unitPartKey = unitData.partKey || unitData.key;

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === unitPartKey)
            .map((part) => ({ data: part }));
    }

    private async performDeleteUnit(partKey: string, _descendants: OrgPart[]): Promise<void> {
        try {
            // ENHANCED: Use the improved cascading deletion - just delete the parent,
            // and let the service handle finding and deleting all descendants atomically
            // console.log(`🗑️  Deleting unit ${partKey} with cascading deletion...`);

            if (!this.organization) {
                throw new Error('No organization data available for deletion');
            }

            await this.obService.deleteParts([partKey], this.organization);

            this.snackbar.open('Unit deleted successfully', 'OK', { duration: 3000 });

            // Refresh the organization board to reflect the changes
            await this.refreshOrganizationBoard();
        } catch (error) {
            console.error('Error deleting unit:', error);
            this.snackbar.open('Error deleting unit', 'OK', { duration: 3000 });
        }
    }

    //#endregion

    //#region Post action handlers
    // Methods for handling post-related events and actions from the postsInParent 3-dot menu.

    public onAddSiblingPost(post: unknown): void {
        const postData = post as any;

        // Create mock node structure for the sibling dialog
        const mockNode = {
            node: {
                data: {
                    partKey: postData.partKey || postData.key,
                    partType: postData.partType || 'post',
                    path: postData.path,
                    parentRef: postData.parentRef,
                    depth: postData.depth,
                    parentPartKey: postData.parentPartKey,
                },
            },
        };

        // Open create new position dialog for sibling post
        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'sibling',
                node: mockNode,
                siblings: this.findPostSiblings(postData),
                children: [],
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] sibling post dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public onEditPost(post: unknown): void {
        const postData = post as any;

        // Find the post/position in the organization parts
        const partKey = postData.partKey || postData.key;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Post not found', 'OK', { duration: 3000 });
            return;
        }

        // Open edit position dialog (same as Edit Section / Edit Unit)
        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                orgKey: this.organization?.key,
                organization: this.organization,
                isDeveloper: this.isDev,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.snackbar.open('Post updated successfully', 'OK', { duration: 3000 });
            }
        });
    }

    public async onDeletePost(post: unknown): Promise<void> {
        const postData = post as any;
        const partKey = postData.partKey || postData.key;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Post not found', 'OK', { duration: 3000 });
            return;
        }

        const confirmMessage = `Are you sure you want to delete "${position.name}"?`;

        const confirmed = await this.confirmDialogService.asyncConfirm('Delete Post', confirmMessage);

        if (confirmed) {
            await this.deletePartWithPanel(position.partKey, position.name, 'Post');
        }
    }

    private findPostSiblings(post: unknown): unknown[] {
        const postData = post as any;

        if (!this.organization?.orgParts) return [];

        const parentPartKey = postData.parentPartKey;
        if (!parentPartKey) return [];

        // Get all posts with the same parent (including the current post)
        const siblings = this.organization.orgParts
            .filter((part) => part.parentPartKey === parentPartKey && part.partType === 'post')
            .map((part) => ({ data: part }));

        return siblings;
    }

    private async performDeletePost(partKey: string): Promise<void> {
        try {
            if (!this.organization) {
                throw new Error('No organization data available for deletion');
            }

            await this.obService.deleteParts([partKey], this.organization);

            this.snackbar.open('Post deleted successfully', 'OK', { duration: 3000 });

            // Refresh the organization board to reflect the changes
            await this.refreshOrganizationBoard();
        } catch (error) {
            console.error('Error deleting post:', error);
            this.snackbar.open('Error deleting post', 'OK', { duration: 3000 });
        }
    }

    //#endregion

    //#region Division action handlers
    // Methods for handling division-related events and actions from the org board header menu.

    /**
     * Extract the sibling index from a path string.
     * Path format: children.X.data or children.X.children.Y.data
     * E.g., "children.0.children.2.data" -> 2
     */
    private extractIndexFromPath(path: string): number {
        if (!path) return 0;

        const pathParts = path.split('.');

        // Work backwards from the end to find the last children.X pattern
        for (let i = pathParts.length - 1; i >= 0; i--) {
            if (pathParts[i] === 'data' && i > 0 && pathParts[i - 1].match(/^\d+$/)) {
                return parseInt(pathParts[i - 1], 10);
            }
        }

        return 0;
    }

    /**
     * Checks if a node has children (used for showing/hiding Reorder Children menu item)
     */
    public hasChildren(node: ECONode<Position>): boolean {
        if (!this.organization?.orgParts || !node.data) return false;
        const partKey = node.data.partKey;
        return this.organization.orgParts.some((part) => part.parentPartKey === partKey);
    }

    public onEditDivision(node: ECONode<Position>): void {
        if (!node.data) return;
        const partKey = node.data.partKey;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Division not found', 'OK', { duration: 3000 });
            return;
        }

        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                orgKey: this.organization?.key,
                organization: this.organization,
                isDeveloper: this.isDev,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.snackbar.open('Division updated successfully', 'OK', { duration: 3000 });
            }
        });
    }

    public onAddSiblingDivision(node: ECONode<Position>): void {
        if (!node.data) return;
        const parentPart = this.organization?.orgParts?.find((part) => part.partKey === node.data!.parentPartKey);
        const siblings = this.findDivisionSiblings(node);

        // Get default colours from first sibling for colour inheritance
        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (siblings.length > 0) {
            const firstSibling = siblings[0];
            if (firstSibling?.data?.colour) {
                defaultColours = {
                    color: firstSibling.data.colour.color || '#000000',
                    background: firstSibling.data.colour.background || '#ffffff',
                    linkColor: firstSibling.data.colour.linkColor || '#000000',
                };
            }
        }

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    parentPartKey: node.data.parentPartKey,
                    partType: 'division',
                    path: node.data.path,
                    parentRef: parentPart?.path || parentPart?.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'sibling',
                node: mockNode,
                siblings: siblings,
                children: [],
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] sibling division dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public onAddChildDepartment(node: ECONode<Position>): void {
        if (!node.data) return;
        const children = this.findDivisionChildren(node);

        // Get default colours from first child for colour inheritance
        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (children.length > 0) {
            const firstChild = children[0];
            if (firstChild?.data?.colour) {
                defaultColours = {
                    color: firstChild.data.colour.color || '#000000',
                    background: firstChild.data.colour.background || '#ffffff',
                    linkColor: firstChild.data.colour.linkColor || '#000000',
                };
            }
        }

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    partType: 'division',
                    path: node.data.path,
                    parentRef: node.data.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: children,
                defaultChildType: 'department',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] child department dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public async onDeleteDivision(node: ECONode<Position>): Promise<void> {
        if (!node.data) return;
        const partKey = node.data.partKey;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Division not found', 'OK', { duration: 3000 });
            return;
        }

        const descendants = this.findAllDescendants(position.partKey);
        const totalToDelete = descendants.length + 1;

        let confirmMessage = `Are you sure you want to delete "${position.name}"?`;
        if (descendants.length > 0) {
            confirmMessage += ` This will also delete ${descendants.length} descendant org part(s). Total org parts to be deleted: ${totalToDelete}`;
        }

        const confirmed = await this.confirmDialogService.asyncConfirm('Delete Division', confirmMessage);

        if (confirmed) {
            await this.deletePartWithPanel(partKey, position.name, 'Division');
        }
    }

    public onReorderDivisionChildren(node: ECONode<Position>): void {
        this.openReorderChildrenDialog(node, 'division');
    }

    private findDivisionSiblings(node: ECONode<Position>): any[] {
        if (!this.organization?.orgParts || !node.data) return [];

        const parentPartKey = node.data.parentPartKey;
        if (!parentPartKey) return [];

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === parentPartKey && part.partType === 'division')
            .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
            .map((part) => ({ data: part }));
    }

    private findDivisionChildren(node: ECONode<Position>): any[] {
        if (!this.organization?.orgParts || !node.data) return [];

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === node.data!.partKey)
            .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
            .map((part) => ({ data: part }));
    }

    //#endregion

    //#region Executive action handlers
    // Methods for handling executive-related events and actions from the org board header menu.

    /** Board of Directors box ⋮ → Edit: the seats live in the CEO's container, so edit that. */
    public onEditDetachedBox(box: any): void {
        if (box?.ownerNode) this.onEditExecutive(box.ownerNode);
    }

    /** Board of Directors box ⋮ → Add board seat: same editor, one amber seat pre-added. */
    public onAddBoardSeat(box: any): void {
        if (box?.ownerNode)
            this.onEditExecutive(box.ownerNode, { seedBoardSeat: { connectsTo: box.connectsTo || null } });
    }

    public onEditExecutive(
        node: ECONode<Position>,
        opts: { seedBoardSeat?: { connectsTo?: string | null } } = {}
    ): void {
        if (!node.data) return;
        const partKey = node.data.partKey;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Executive not found', 'OK', { duration: 3000 });
            return;
        }

        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                orgKey: this.organization?.key,
                organization: this.organization,
                isDeveloper: this.isDev,
                seedBoardSeat: opts.seedBoardSeat,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.snackbar.open('Executive updated successfully', 'OK', { duration: 3000 });
            }
        });
    }

    public onAddSiblingExecutive(node: ECONode<Position>): void {
        if (!node.data) return;
        const parentPart = this.organization?.orgParts?.find((part) => part.partKey === node.data!.parentPartKey);
        const siblings = this.findExecutiveSiblings(node);

        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (siblings.length > 0) {
            const firstSibling = siblings[0];
            if (firstSibling?.data?.colour) {
                defaultColours = {
                    color: firstSibling.data.colour.color || '#000000',
                    background: firstSibling.data.colour.background || '#ffffff',
                    linkColor: firstSibling.data.colour.linkColor || '#000000',
                };
            }
        }

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    parentPartKey: node.data.parentPartKey,
                    partType: 'executive',
                    path: node.data.path,
                    parentRef: parentPart?.path || parentPart?.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'sibling',
                node: mockNode,
                siblings: siblings,
                children: [],
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] sibling executive dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public onAddChildDivision(node: ECONode<Position>): void {
        if (!node.data) return;
        const children = this.findExecutiveChildren(node);

        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (children.length > 0) {
            const firstChild = children[0];
            if (firstChild?.data?.colour) {
                defaultColours = {
                    color: firstChild.data.colour.color || '#000000',
                    background: firstChild.data.colour.background || '#ffffff',
                    linkColor: firstChild.data.colour.linkColor || '#000000',
                };
            }
        }

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    partType: 'executive',
                    path: node.data.path,
                    parentRef: node.data.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: children,
                defaultChildType: 'division',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] child division dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public async onDeleteExecutive(node: ECONode<Position>): Promise<void> {
        if (!node.data) return;
        const partKey = node.data.partKey;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Executive not found', 'OK', { duration: 3000 });
            return;
        }

        const descendants = this.findAllDescendants(position.partKey);
        const totalToDelete = descendants.length + 1;

        let confirmMessage = `Are you sure you want to delete "${position.name}"?`;
        if (descendants.length > 0) {
            confirmMessage += ` This will also delete ${descendants.length} descendant org part(s). Total org parts to be deleted: ${totalToDelete}`;
        }

        const confirmed = await this.confirmDialogService.asyncConfirm('Delete Executive', confirmMessage);

        if (confirmed) {
            await this.deletePartWithPanel(partKey, position.name, 'Executive');
        }
    }

    public onReorderExecutiveChildren(node: ECONode<Position>): void {
        this.openReorderChildrenDialog(node, 'executive');
    }

    private findExecutiveSiblings(node: ECONode<Position>): any[] {
        if (!this.organization?.orgParts || !node.data) return [];

        const parentPartKey = node.data.parentPartKey;

        // For root-level executives (no parent), find other root-level executives
        if (!parentPartKey) {
            const allPartKeys = new Set(this.organization.orgParts.map((p) => p.partKey));
            return this.organization.orgParts
                .filter(
                    (part) =>
                        part.partType === 'executive' && (!part.parentPartKey || !allPartKeys.has(part.parentPartKey))
                )
                .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
                .map((part) => ({ data: part }));
        }

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === parentPartKey && part.partType === 'executive')
            .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
            .map((part) => ({ data: part }));
    }

    private findExecutiveChildren(node: ECONode<Position>): any[] {
        if (!this.organization?.orgParts || !node.data) return [];

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === node.data!.partKey)
            .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
            .map((part) => ({ data: part }));
    }

    //#endregion

    //#region Department action handlers
    // Methods for handling department-related events and actions from the org board header menu.

    public onEditDepartment(node: ECONode<Position>): void {
        if (!node.data) return;
        const partKey = node.data.partKey;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Department not found', 'OK', { duration: 3000 });
            return;
        }

        const dialogRef = this.dialog.open(OrgEditPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                part: position,
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                orgKey: this.organization?.key,
                organization: this.organization,
                isDeveloper: this.isDev,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.snackbar.open('Department updated successfully', 'OK', { duration: 3000 });
            }
        });
    }

    public onAddSiblingDepartment(node: ECONode<Position>): void {
        if (!node.data) return;
        const parentPart = this.organization?.orgParts?.find((part) => part.partKey === node.data!.parentPartKey);
        const siblings = this.findDepartmentSiblings(node);

        // Get default colours from first sibling for colour inheritance
        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (siblings.length > 0) {
            const firstSibling = siblings[0];
            if (firstSibling?.data?.colour) {
                defaultColours = {
                    color: firstSibling.data.colour.color || '#000000',
                    background: firstSibling.data.colour.background || '#ffffff',
                    linkColor: firstSibling.data.colour.linkColor || '#000000',
                };
            }
        }

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    parentPartKey: node.data.parentPartKey,
                    partType: 'department',
                    path: node.data.path,
                    parentRef: parentPart?.path || parentPart?.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'sibling',
                node: mockNode,
                siblings: siblings,
                children: [],
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] sibling department dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public onAddChildSection(node: ECONode<Position>): void {
        if (!node.data) return;
        const children = this.findDepartmentChildren(node);

        // Get default colours from first child for colour inheritance
        let defaultColours: { color: string; background: string; linkColor: string } | undefined;
        if (children.length > 0) {
            const firstChild = children[0];
            if (firstChild?.data?.colour) {
                defaultColours = {
                    color: firstChild.data.colour.color || '#000000',
                    background: firstChild.data.colour.background || '#ffffff',
                    linkColor: firstChild.data.colour.linkColor || '#000000',
                };
            }
        }

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    partType: 'department',
                    path: node.data.path,
                    parentRef: node.data.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: children,
                defaultChildType: 'section',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                defaultColours: defaultColours,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            console.log('[OrgBoard] child section dialog closed with:', result);
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    public async onDeleteDepartment(node: ECONode<Position>): Promise<void> {
        if (!node.data) return;
        const partKey = node.data.partKey;
        const position = this.organization?.orgParts.find((p) => p.partKey === partKey);

        if (!position) {
            this.snackbar.open('Department not found', 'OK', { duration: 3000 });
            return;
        }

        const descendants = this.findAllDescendants(position.partKey);
        const totalToDelete = descendants.length + 1;

        let confirmMessage = `Are you sure you want to delete "${position.name}"?`;
        if (descendants.length > 0) {
            confirmMessage += ` This will also delete ${descendants.length} descendant org part(s). Total org parts to be deleted: ${totalToDelete}`;
        }

        const confirmed = await this.confirmDialogService.asyncConfirm('Delete Department', confirmMessage);

        if (confirmed) {
            await this.deletePartWithPanel(partKey, position.name, 'Department');
        }
    }

    public onReorderDepartmentChildren(node: ECONode<Position>): void {
        this.openReorderChildrenDialog(node, 'department');
    }

    public onBulkAssignStaff(node: ECONode<Position>): void {
        if (!node.data || !this.organization) return;
        // A linked post is staffed at the post it represents (the menu item on the post itself).
        if ((node.data as any).partType === 'post' && this.refuseLinkedPostAssignment((node.data as any).partKey))
            return;

        const dialogRef = this.dialog.open(OrgBulkAssignStaffDialogComponent, {
            width: '65%',
            height: '80%',
            minHeight: '400px',
            panelClass: 'bulk-assign-staff-dialog',
            data: {
                node,
                organization: this.organization,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.applied) {
                console.log(`Bulk assign completed: ${result.successCount} succeeded, ${result.errorCount} errors`);

                // Rebuild tree with latest org data so changes (removals/assignments) are reflected
                const orgData = this.deferredOrgUpdate || this._organization;
                if (orgData?.key !== undefined) {
                    this._organization = orgData;
                    this.deferredOrgUpdate = null;
                    this.isHatAssignmentInProgress = false;
                    this.pendingHatAssignments.clear();
                    this.ensureOrgPartsSorted(this._organization);
                    this.dialogService.organization = this._organization;
                    this.obService.organization = this._organization;
                    this.obService.organization$.next(this._organization);
                    const orgTree = this.treeBuilder.buildOrg(this._organization, this.orbBoardId);
                    const ecoTree = new ECOTree();
                    this.treeBuilder.insertPositionsToEcoTree(orgTree, ecoTree, {
                        isPositionsOn: this.isPositionsSwitchOn,
                    });
                    this.loadTreeTheNewWay(ecoTree);
                    this.cdr.detectChanges();
                }
            }
        });
    }

    /**
     * Handles bulk assign from sub-part menus (section/unit) in the node component.
     * Wraps the raw part data into a node-like structure expected by the dialog.
     */
    public onBulkAssignStaffFromPart(part: any): void {
        if (!part || !this.organization) return;

        // Preserve name + partType on the wrapped node so the dialog can render
        // a context-aware title ("Assign Staff to Post" for a single post vs
        // "Assign Staff to Posts" for a section/unit container).
        const wrappedNode = {
            data: {
                partKey: part.partKey || part.key,
                name: part.name,
                partType: part.partType,
            },
        } as unknown as ECONode<Position>;
        this.onBulkAssignStaff(wrappedNode);
    }

    private findDepartmentSiblings(node: ECONode<Position>): any[] {
        if (!this.organization?.orgParts || !node.data) return [];

        const parentPartKey = node.data.parentPartKey;
        if (!parentPartKey) return [];

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === parentPartKey && part.partType === 'department')
            .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
            .map((part) => ({ data: part }));
    }

    private findDepartmentChildren(node: ECONode<Position>): any[] {
        if (!this.organization?.orgParts || !node.data) return [];

        return this.organization.orgParts
            .filter((part) => part.parentPartKey === node.data!.partKey)
            .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path))
            .map((part) => ({ data: part }));
    }

    /**
     * Add a child unit directly under a department node.
     * Uses the same create dialog pattern as onAddChildSection but for unit type.
     */
    public onAddChildUnitFromDept(node: ECONode<Position>): void {
        if (!node.data) return;
        const children = this.findDepartmentChildren(node);

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    partType: 'department',
                    path: node.data.path,
                    parentRef: node.data.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: children,
                defaultChildType: 'unit',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    /**
     * Add a child post directly under any node (division, department, etc.).
     * Generic handler for header-level menus that operate on ECONode<Position>.
     */
    public onAddChildPostFromNode(node: ECONode<Position>): void {
        if (!node.data) return;

        const nodeData = node.data as any;
        const children =
            this.organization?.orgParts
                ?.filter((part) => part.parentPartKey === node.data!.partKey)
                .map((part) => ({ data: part })) || [];

        const mockNode = {
            node: {
                data: {
                    partKey: node.data.partKey,
                    partType: nodeData.partType || 'department',
                    path: node.data.path,
                    parentRef: node.data.parentRef,
                    depth: node.data.depth,
                },
            },
        };

        const dialogRef = this.dialog.open(OrgCreateNewPositionDialogComponent, {
            width: '80%',
            height: '85%',
            minHeight: '300px',
            maxWidth: '98vw',
            panelClass: 'ng-resizable-dialog',
            data: {
                organization: this.organization,
                type: 'child',
                node: mockNode,
                siblings: [],
                children: children,
                defaultChildType: 'post',
                partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && (result.newPart || result.bulkTree)) {
                this.handleNewPartCreation(result);
            }
        });
    }

    //#endregion

    //#region Section/Unit reorder handlers
    // Methods for handling reorder children from section/unit menus (child components)

    public onReorderSectionChildren(section: any): void {
        // Section data has partKey property
        if (!section?.partKey) {
            this.snackbar.open('Invalid section data', 'OK', { duration: 3000 });
            return;
        }
        this.openReorderChildrenDialogByPartKey(section.partKey, 'section');
    }

    public onReorderUnitChildren(unit: any): void {
        // Unit data has partKey property
        if (!unit?.partKey) {
            this.snackbar.open('Invalid unit data', 'OK', { duration: 3000 });
            return;
        }
        this.openReorderChildrenDialogByPartKey(unit.partKey, 'unit');
    }

    private openReorderChildrenDialogByPartKey(partKey: string, partType: string): void {
        // Find all direct children of this part
        const children =
            this.organization?.orgParts
                ?.filter((part) => part.parentPartKey === partKey)
                .sort((a, b) => this.extractIndexFromPath(a.path) - this.extractIndexFromPath(b.path)) || [];

        if (children.length < 2) {
            this.snackbar.open('Need at least 2 children to reorder', 'OK', { duration: 3000 });
            return;
        }

        // Import and open the reorder dialog
        import('./dialogs/org-reorder-children-dialog/org-reorder-children-dialog.component').then((module) => {
            const dialogRef = this.dialog.open(module.OrgReorderChildrenDialogComponent, {
                width: '500px',
                data: {
                    parentPartKey: partKey,
                    organization: this.organization,
                },
            });

            dialogRef.afterClosed().subscribe(async (result) => {
                if (result && result.newOrder) {
                    try {
                        // Call reorder service
                        await this.obService.reorderChildren(
                            this.organization?.key || '',
                            partKey,
                            result.newOrder.map((p: OrgPart) => p.partKey)
                        );
                        this.snackbar.open(
                            `${partType.charAt(0).toUpperCase() + partType.slice(1)} children reordered successfully`,
                            'OK',
                            { duration: 3000 }
                        );
                        await this.refreshOrganizationBoard();
                    } catch (error) {
                        console.error(`Error reordering ${partType} children:`, error);
                        this.snackbar.open(`Error reordering ${partType} children`, 'OK', { duration: 3000 });
                    }
                }
            });
        });
    }

    //#endregion

    //#region Reorder children dialog
    // Shared method for opening the reorder children dialog for any part type.

    private openReorderChildrenDialog(node: ECONode<Position>, partType: string): void {
        if (!node.data) return;
        // Find all direct children of this node
        const children =
            this.organization?.orgParts
                ?.filter((part) => part.parentPartKey === node.data!.partKey)
                .sort((a, b) => (a.id || 0) - (b.id || 0)) || [];

        if (children.length < 2) {
            this.snackbar.open('Need at least 2 children to reorder', 'OK', { duration: 3000 });
            return;
        }

        // Import and open the reorder dialog
        import('./dialogs/org-reorder-children-dialog/org-reorder-children-dialog.component').then((module) => {
            const dialogRef = this.dialog.open(module.OrgReorderChildrenDialogComponent, {
                width: '500px',
                data: {
                    parentPartKey: node.data!.partKey,
                    organization: this.organization,
                },
            });

            dialogRef.afterClosed().subscribe(async (result) => {
                if (result && result.newOrder) {
                    try {
                        // Call reorder service
                        await this.obService.reorderChildren(
                            this.organization?.key || '',
                            node.data!.partKey,
                            result.newOrder.map((p: OrgPart) => p.partKey)
                        );
                        this.snackbar.open(
                            `${partType.charAt(0).toUpperCase() + partType.slice(1)} children reordered successfully`,
                            'OK',
                            { duration: 3000 }
                        );
                        await this.refreshOrganizationBoard();
                    } catch (error) {
                        console.error(`Error reordering ${partType} children:`, error);
                        this.snackbar.open(`Error reordering ${partType} children`, 'OK', { duration: 3000 });
                    }
                }
            });
        });
    }

    //#endregion
}

function fromStringToHex(color: string): string {
    // Possible example of the objects that will call this function include:
    //     const partData = {
    //         linkColor: "blue",
    //         background: "blue",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "orange",
    //         background: "orange",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "purple",
    //         background: "purple",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "pink",
    //         background: "pink",
    //         color: "black",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "lime",
    //         background: "lime",
    //         color: "black",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "grey",
    //         background: "grey",
    //         color: "white",
    //         selected: true,
    //     };
    //     const partData = {
    //         linkColor: "yellow",
    //         background: "yellow",
    //         color: "black",
    //         selected: true,
    //     };
    // Taking this into consideration, we must return the 7 code HEX value of the color.
    // For example, if the color is "blue", we must return "#0000FF".

    switch (color) {
        case 'blue':
            return '#0000FF';
        case 'orange':
            return '#FFA500';
        case 'purple':
            return '#800080';
        case 'pink':
            return '#FFC0CB';
        case 'lime':
            return '#00FF00';
        case 'grey':
            return '#808080';
        case 'yellow':
            return '#FFFF00';
        case 'red':
            return '#FF0000';
        case 'green':
            return '#008000';
        case 'white':
            return '#FFFFFF';
        case 'black':
            return '#000000';
        default:
            return color;
    }
}

export interface Section {
    name: string;
    updated: Date;
}

enum ToolbarOptionsEnum {
    none = 'none',
    hats = 'hats',
    my_policies = 'my_policies',
    policies = 'policies',
    users = 'users',
    security_groups = 'security_groups',
    positions = 'positions',
    new_user = 'new_user',
    new_policy = 'new_policy',
    new_hat = 'new_hat',
    new_part = 'new_part',
    new_sg = 'new_sg',
}
